import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  AlertTriangle,
  Archive,
  ArrowRight,
  Ban,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronRight,
  Clipboard,
  ClipboardList,
  Clock,
  FileText,
  ListChecks,
  Pencil,
  Trash2,
  User,
  Users as UsersLucide,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import AssessmentCalendarStatusBadge from "./AssessmentCalendarStatusBadge";
import ConsolidatedReportModal from "./ConsolidatedReportModal";
import { shouldMergeDisconnectReviewRow } from "./disconnectReviewEnrichment";
import DisconnectTriggerModal from "./DisconnectTriggerModal";
import { ProvisionalLevelTooltip } from "./ProvisionalLevelTag";
import {
  buildPublishedOverrideEligibleKeys,
  isPublishedOverrideEligibleRow,
} from "./publishedOverrideEligibility";
import ResolutionReviewModal, {
  readResolutionAgreedLevel,
} from "./ResolutionReviewModal";
import ReviewAssessmentModal, {
  AssessmentForReview,
  AssessmentResponseMap,
  TemplateData,
} from "./ReviewAssessmentModal";
import SelfAssessmentsHubTab, {
  type TeamAssessmentHubItem,
} from "./SelfAssessmentsHubTab";
import Button from "../../Components/Button";
import CadenceTag from "../../Components/CadenceTag";
import DropdownSwitcher from "../../Components/DropdownSwitcher";
import HoverHint from "../../Components/HoverHint";
import CommonInput from "../../Components/Input";
import Modal from "../../Components/Modal";
import SearchInput from "../../Components/Search/SearchInput";
import Select from "../../Components/Select";
import SuccessNotification from "../../Components/SuccessNotification";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import { formatAssessmentSubmitSavedOnDate } from "../../Pages/ReviewAssessments/assessmentSubmitSuccessCopy";
import AssessmentSubmitSuccessModal, {
  type AssessmentSubmitSuccessInfo,
} from "../../Pages/ReviewAssessments/AssessmentSubmitSuccessModal";
import PortfolioAccountsStatusPanel from "../../Pages/ReviewAssessments/PortfolioAccountsStatusPanel";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  DisconnectReviewAccount,
  DisconnectResolution,
  DisconnectSponsorAssessment,
  fetchAssessments,
  fetchVendorSelfAssessments,
  fetchDisconnectReview,
  fetchDisconnectResolutions,
  triggerDisconnectWorkflow,
  publishPostResolution,
  publishAssessmentAccounts,
  TriggerDisconnectAccountPayload,
  updateAssessment,
  updateAssessmentStatus,
  deleteAssessment,
  fetchAssessmentQuestionsAndResponses,
  overrideAccountLevel,
  Assessment,
  clearCreateAssessmentSuccess,
} from "../../Store/Reducers/AssessmentSlice";
import { fetchPortfolioAccounts } from "../../Store/Reducers/CustomerWorkspaceSlice";
import {
  getDisconnectActionLabel,
  getDisconnectActionPillBadgeClass,
  getDisconnectStatusLabel,
  getHubDisconnectWorkflowAccountTag,
  HUB_COMBINED_DISCONNECT_LABEL,
  HUB_COMBINED_PUBLISH_LABEL,
  normalizeDisconnectStatus,
  DISCONNECT_RESOLVED_ASSESSMENT_LABEL,
} from "../../Utils/Constants/disconnectStatusLabels";
import { isRole, ROLES } from "../../Utils/Constants/RoleConstants";
import {
  addUtcDaysToToday,
  formatAssessmentDateTimeUtc,
  isUtcDateOverdue,
  toUtcDateInputValue,
  utcTodayInputValue,
} from "../../Utils/Helper/assessmentDateUtils";
import {
  type AssessmentKindTab,
  HUB_LEVEL_MIX_SCOPES,
  isPublishedSelfOrigin,
  isSelfAssessmentType,
  matchesAssessmentKindTab,
  matchesDisconnectKindTab,
  normalizeTeamAssessmentDisplayName,
  parseHubLevelMixScope,
  TEAM_ASSESSMENT_DESCRIPTION,
  type LevelMixScope,
} from "../../Utils/Helper/assessmentOriginHelpers";
import {
  classifyCadenceExecutive,
  type CadencePlacement,
} from "../../Utils/Helper/cadenceExecutiveDisplay";
import {
  getLatestLevelUpdateAt,
  getLevelUpdateLog,
} from "../../Utils/Helper/levelUpdateLogUtils";
import {
  getAccountLevelVerboseLabel,
  toStoredLevelCode,
  toVerboseLevelLabel,
} from "../../Utils/Helper/levelUtils";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import { toast } from "../../Utils/Helper/toast";
import LevelMixContainer from "../LevelMixContainer";
import AssessmentCreationWizard from "../TemplateContainer/components/AssessmentCreationWizard";
import XFactorLevelGuide from "../TemplateContainer/components/XFactorLevelGuide";

import "./style.scss";

/** Stored at close / Formal publish for a member whose input was left out. */
type ExcludedInputNote = {
  reason?: "stale" | "not_assessed" | string;
  level?: string | null;
  submitted_on?: string | null;
  days_since?: number | null;
  stale_for_days?: number | null;
  recorded_on?: string | null;
};

type AssignmentNotes = {
  origin?: string;
  pending_merge?: boolean;
  skipped?: boolean;
  skip_reason?: string;
  excluded?: ExcludedInputNote | null;
  /** Input given during an open disconnect; counts after it. */
  held?: { at?: string | null; resolution_id?: string | null } | null;
  /** Partner round id when published together with another round. */
  combined_with?: string | null;
  /** Formal: counted from an earlier input at publish, keeping its date. */
  carried_in_at_publish?: boolean;
};

const isHeldExecutive = (exec: { notes?: AssignmentNotes | null }): boolean =>
  Boolean(exec.notes?.held);

interface ExecutiveAssignment {
  id?: string;
  user_id?: string;
  assignment_id?: string;
  name: string;
  email?: string;
  role?: string;
  status?: string;
  is_responded?: boolean;
  /** Formal: not yet responded; shows the input a publish would carry in. */
  carried?: boolean;
  submitted_at?: string | null;
  responses?: {
    rationale?: string;
    maturity_level?: string;
  };
  notes?: AssignmentNotes | null;
}

/** Primary-sponsor recorded disconnect resolution shown in Full Report. */
interface AccountDisconnectResolutionSummary {
  id?: string;
  status?: string | null;
  agreedLevel?: number | null;
  agreedLevelLabel?: string | null;
  rationale?: string | null;
  resolvedBy?: string | null;
  resolvedById?: string | null;
  resolvedAt?: string | null;
}

/** ESL Click-to-Override rationale stored on assessment.notes.override_rationales. */
type OverrideRationaleEntry = {
  rationale?: string | null;
  overridden_at?: string | null;
  overridden_by?: string | null;
  override_level?: string | null;
};

interface AssessmentAccount {
  id?: string;
  account_id?: string;
  account_name?: string;
  account_code?: string;
  name?: string;
  totalAssigned?: number;
  requests_sent?: number;
  responses?: number;
  responses_received?: number;
  minLevel?: string | null;
  min_level?: string | null;
  medianLevel?: string | null;
  median_level?: string | null;
  maxLevel?: string | null;
  max_level?: string | null;
  finalLevel?: string | null;
  final_level?: string | null;
  maturity_level?: string | null;
  overrideLevel?: boolean;
  override?: {
    override_level?: string | null;
    overridden_by?: string | null;
    overridden_at?: string | null;
  };
  executives?: ExecutiveAssignment[];
  users?: Array<{
    user_id: string;
    assignment_id?: string;
    name: string;
    email: string;
    role: string;
    is_responded: boolean;
    submitted_at?: string | null;
    responses?: {
      rationale?: string;
      maturity_level?: string;
    };
    notes?: AssignmentNotes | null;
  }>;
  disconnectDetected?: boolean;
  disconnectDelta?: number | null;
  disconnectTier?: string | null;
  disconnectStatus?: string | null;
  resolutionId?: string | null;
  /** Primary-sponsor recorded disconnect resolution (resolved/published). */
  disconnectResolution?: AccountDisconnectResolutionSummary | null;
  hubPublished?: boolean;
  published_at?: string | null;
  published_level?: string | null;
  eslFinalLevel?: string | null;
  finalLevelPending?: boolean;
  noResponseBanner?: string | null;
  noResponseMode?: string | null;
  disconnectReviewRow?: DisconnectReviewAccount;
  /** Formal and Team published together (same due date). */
  combined?: boolean;
  /** Formal disconnect pending after disconnect due. */
  disconnectOngoing?: boolean;
  /** Stamped Formal publish mode: manual_publish | cron_auto_publish. */
  publishMode?: string | null;
}

// API Response structure
interface ApiAssessmentUser {
  user_id: string;
  assignment_id?: string;
  name: string;
  email: string;
  role: string;
  is_responded: boolean;
  carried?: boolean;
  submitted_at?: string | null;
  notes?: AssignmentNotes | null;
  responses?: {
    rationale?: string;
    maturity_level?: string;
  };
}

interface ApiAssessmentAccount {
  account_id: string;
  account_name: string;
  account_code?: string;
  users: ApiAssessmentUser[];
  requests_sent: number;
  responses_received: number;
  maturity_level?: string | null;
  min_level?: string | null;
  median_level?: string | null;
  max_level?: string | null;
  final_level?: string | null;
  override?: {
    override_level?: string | null;
    overridden_by?: string | null;
    overridden_at?: string | null;
  };
  disconnectDetected?: boolean;
  disconnectDelta?: number | null;
  disconnectTier?: string | null;
  disconnectStatus?: string | null;
  resolutionId?: string | null;
  disconnectResolution?: AccountDisconnectResolutionSummary | null;
  hubPublished?: boolean;
  published_at?: string | null;
  published_level?: string | null;
  eslFinalLevel?: string | null;
  finalLevelPending?: boolean;
  noResponseBanner?: string | null;
  noResponseMode?: string | null;
  disconnectOngoing?: boolean;
  publishMode?: string | null;
}

interface DueDateInfo {
  count: number;
  reason?: string;
  updated_by?: string;
  last_due_date?: string;
  last_updated_at?: string;
  current_due_date?: string;
}

interface AbortInfo {
  reason?: string;
  abort_at?: string;
  abort_by?: string;
}

interface ApiAssessmentResponse {
  assessment_id?: string;
  id?: string;
  name: string;
  title: string;
  description?: string | null;
  questions: number;
  due_date?: string;
  due_on?: string;
  start_date?: string;
  status: string; // "ACTIVE", "COMPLETED", "DRAFT", "UPCOMING"
  assessment_type?: string | null;
  category?: string | null;
  categories?: string[];
  target_accounts?: ApiAssessmentAccount[];
  accounts?: ApiAssessmentAccount[];
  hub_publish_scope?: "partial" | "full" | null;
  has_self_fold?: boolean;
  target_questionnaires?: string; // "small" | "large"
  created_by: string;
  created_by_user?: {
    id: string;
    name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
  published_at?: string | null;
  participants?: number;
  total_responses?: number;
  progress?: number;
  due_date_info?: DueDateInfo;
  notes?: {
    source?: string | null;
    converted_from?: string | null;
    self_assessment_id?: string | null;
    override_rationales?: Record<string, OverrideRationaleEntry> | null;
    level_update_log?:
      | import("../../Utils/Helper/levelUpdateLogUtils").LevelUpdateLogEntry[]
      | null;
  } | null;
}

const FORMAL_MAX_DUE_DAYS = 60;

/** Latest due date (YYYY-MM-DD) a Formal can be extended to: launch + 60 days. */
const formalLatestDueInputValue = (assessment: {
  assessment_type?: string | null;
  start_date?: string | null;
  created_at?: string | null;
}): string | undefined => {
  if (isSelfAssessmentType(assessment.assessment_type)) return undefined;
  const launch = toUtcDateInputValue(
    assessment.start_date || assessment.created_at
  );
  if (!launch) return undefined;
  const [y, m, d] = launch.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + FORMAL_MAX_DUE_DAYS))
    .toISOString()
    .slice(0, 10);
};

type AssessmentWithAccounts = Assessment & {
  accounts?: AssessmentAccount[];
  assessment_type?: string | null;
  notes?: {
    source?: string | null;
    converted_from?: string | null;
    self_assessment_id?: string | null;
    override_rationales?: Record<string, OverrideRationaleEntry> | null;
    level_update_log?:
      | import("../../Utils/Helper/levelUpdateLogUtils").LevelUpdateLogEntry[]
      | null;
  } | null;
  category?: string | null;
  createdBy?: string | null;
  created_by_user?: {
    id: string;
    name: string;
    email: string;
  };
  created_at?: string | null;
  start_date?: string | null;
  description?: string | null;
  dueDate?: string | null;
  due_on?: string | null;
  published_at?: string | null;
  cronPublishFailed?: boolean;
  has_self_fold?: boolean;
  template_name?: string | null;
  target_accounts?: Array<{
    account_id?: string;
    account_code?: string;
    account_name?: string;
  }>;
  total_questions?: number;
  target_questionnaires?: string;
  due_date_info?: DueDateInfo;
  abort_info?: AbortInfo;
  completed_at?: string;
  completed_on?: string;
  questions_data?: {
    metadata?: {
      categories?: string[];
      template_id?: string;
    };
    questions?: Array<{
      id?: string;
      question_text?: string;
      question_type?: string;
      required?: boolean;
      is_custom?: boolean;
      order_index?: number;
      options?: any;
    }>;
  };
};
interface FullReportData {
  assessment: AssessmentWithAccounts;
  account: AssessmentAccount;
  responses: Array<{
    id: string;
    executiveName: string;
    executiveEmail: string;
    submittedAt?: string;
    calculatedLevel?: string;
    responses?: any;
    /** Disconnect-skipped or hub display-Ignored (no real response). */
    ignored?: boolean;
  }>;
}

interface OverrideModalState {
  isOpen: boolean;
  assessmentId: string | null;
  accountId: string | null;
  currentValue: string | null;
  /** Ongoing / Disconnect Ongoing: save also hub-publishes this account. */
  publishOnSave: boolean;
}

/** API response item shape for assessment Q&A (account_id, executive_id = user id). */
interface ApiResponseItem {
  id?: string;
  executive_id?: string;
  account_id?: string;
  responses?: Record<string, string>;
  submitted_at?: string;
  [key: string]: any;
}

/**
 * Find the API response for the Individual Response modal.
 * Matches by account_id and executive_id (executive_id is the user id from the table row).
 * API shape: responses[].account_id, responses[].executive_id.
 */
function getMatchedApiResponse(
  apiData: { responses?: ApiResponseItem[] } | null,
  executiveId: string | null,
  accountId: string | null
): ApiResponseItem | null {
  if (!apiData?.responses || !Array.isArray(apiData.responses)) return null;
  if (!executiveId) return null;

  const list = apiData.responses;

  // When we have both account and executive (full report context), match strictly by both
  if (accountId) {
    const match = list.find(
      r =>
        String(r.account_id ?? "") === String(accountId) &&
        String(r.executive_id ?? "") === String(executiveId)
    );
    if (match) return match;
  }

  // Fallback: same account not in context, match by executive_id only
  const byExecutive = list.find(
    r => String(r.executive_id ?? "") === String(executiveId)
  );
  return byExecutive ?? null;
}

/** Resolve display text for a question answer (e.g. single_choice label from options). */
function getDisplayAnswerForQuestion(
  question: {
    question_type?: string;
    options?: { choices?: Array<{ value?: string; label?: string }> };
  },
  answer: string
): string {
  if (!answer) return "No response provided";
  const isSingleChoice =
    question.question_type === "Single Choice" ||
    question.question_type === "single_choice";
  const choices = question.options?.choices;
  if (!isSingleChoice || !choices?.length) return answer;
  const choice = choices.find(c => c.value === answer || c.label === answer);
  return choice?.label ?? answer;
}

const EMPTY_INDIVIDUAL_RESPONSE_MODAL = {
  isOpen: false,
  response: null,
  assessmentId: null,
  accountId: null,
  questionsAndResponses: null,
  loading: false,
} as const;

// Helper function to normalize status from API (uppercase) to lowercase
const normalizeStatus = (
  status: string
): "active" | "completed" | "draft" | "upcoming" | "aborted" | "published" => {
  const normalized = status?.toLowerCase() || "draft";
  if (normalized === "active") return "active";
  if (normalized === "completed" || normalized === "closed") return "completed";
  if (normalized === "published") return "published";
  if (normalized === "draft") return "draft";
  if (normalized === "upcoming") return "upcoming";
  if (normalized === "aborted") return "aborted";
  if (normalized === "archived") return "published";
  return "draft";
};

const getAssessmentAccountId = (account: AssessmentAccount): string =>
  String(account.account_id || account.id || "");

const disconnectBlocksRegularPublish = (
  account: AssessmentAccount
): boolean => {
  if (account.hubPublished) return false;
  const status = (account.disconnectStatus ?? "none").toLowerCase();
  if (status === "pending_resolution" || status === "resolved") {
    return true;
  }
  if (status === "published") {
    return false;
  }
  return Boolean(account.disconnectDetected);
};

const isAlignedPublishableAccount = (account: AssessmentAccount): boolean =>
  !disconnectBlocksRegularPublish(account);

const isUnpublishedDisconnectAccount = (account: AssessmentAccount): boolean =>
  !account.hubPublished && disconnectBlocksRegularPublish(account);

const mergeAssessmentAccountFromDisconnectReview = (
  account: AssessmentAccount,
  reviewRow: DisconnectReviewAccount
): AssessmentAccount => {
  const fromReview = disconnectResolutionFromReviewRow(reviewRow);
  const accountStatus = (account.disconnectStatus ?? "none").toLowerCase();
  const disconnectStatus =
    accountStatus !== "none"
      ? account.disconnectStatus
      : reviewRow.disconnectStatus;

  return {
    ...account,
    disconnectReviewRow: reviewRow,
    resolutionId: account.resolutionId ?? reviewRow.resolutionId ?? null,
    disconnectResolution: account.disconnectResolution || fromReview,
    disconnectDetected: Boolean(account.disconnectDetected),
    disconnectDelta: account.disconnectDelta ?? reviewRow.delta ?? null,
    disconnectTier: account.disconnectTier ?? reviewRow.deltaTier ?? null,
    disconnectStatus,
    override:
      account.override ||
      (reviewRow.isEslOverridden
        ? {
            override_level: levelFromNumber(reviewRow.eslFinalLevel),
            overridden_by: reviewRow.publishedByName ?? null,
            overridden_at: reviewRow.resolution?.published_at ?? null,
          }
        : undefined),
    published_level:
      account.published_level ||
      (fromReview?.agreedLevel != null ? `L${fromReview.agreedLevel}` : null),
    combined: account.combined || Boolean(reviewRow.combined),
  };
};

const assessmentHasDisconnectAccounts = (
  assessment: AssessmentWithAccounts
): boolean => (assessment.accounts || []).some(isUnpublishedDisconnectAccount);

/**
 * Published-tab / Full Report Disconnect tags: workflow actually ran.
 * A raw response gap (`disconnectDetected`) alone is not history — Formal ESL
 * override can publish while status is still `none` and must not show Disconnect.
 */
const accountHasDisconnectHistory = (account: AssessmentAccount): boolean => {
  if (account.resolutionId) return true;
  if (account.disconnectResolution) return true;
  const status = (account.disconnectStatus ?? "none").toLowerCase();
  return Boolean(status) && status !== "none";
};

const PUBLISHED_DISCONNECT_TAG_CLASS =
  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200";

const PUBLISHED_DISCONNECT_UNRESOLVED_TAG_CLASS =
  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200";

const PUBLISHED_DISCONNECT_RESOLVED_TAG_CLASS =
  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200";

const getPublishedFormalDisconnectNameTag = (
  account: AssessmentAccount
): { label: string; title: string; className: string } | null => {
  if (!accountHasDisconnectHistory(account)) return null;
  const workflowStatus = (account.disconnectStatus ?? "none").toLowerCase();
  const status =
    workflowStatus === "unresolved"
      ? account.disconnectStatus
      : (account.disconnectResolution?.status ??
        account.disconnectStatus ??
        null);
  const tag = getHubDisconnectWorkflowAccountTag(
    status,
    Boolean(account.hubPublished)
  );
  if (!tag) return null;
  const className =
    tag.statusKey === "unresolved"
      ? PUBLISHED_DISCONNECT_UNRESOLVED_TAG_CLASS
      : tag.statusKey === "resolved"
        ? PUBLISHED_DISCONNECT_RESOLVED_TAG_CLASS
        : PUBLISHED_DISCONNECT_TAG_CLASS;
  return {
    label: tag.label,
    title: tag.description ?? "",
    className,
  };
};

const PUBLISH_MODE_TAG_CLASS =
  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200";

const COMBINED_DISCONNECT_TAG_CLASS =
  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-900 border border-amber-200";

const OVERRIDE_ACCOUNT_TAG_CLASS =
  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-600 border border-purple-600";

const ROW_ACTION_BTN_BASE =
  "inline-flex h-7 w-full shrink-0 cursor-pointer items-center justify-center gap-0.5 whitespace-nowrap rounded-md border px-1.5 text-[10.5px] font-bold leading-none transition disabled:cursor-not-allowed disabled:opacity-50";

const VIEW_REPORT_ACTION_BTN = `${ROW_ACTION_BTN_BASE} border-purple-200 bg-purple-50 text-purple-600 hover:border-purple-600 hover:bg-purple-600 hover:text-white`;

const OVERRIDE_ACTION_BTN = `${ROW_ACTION_BTN_BASE} border-purple-200 bg-white text-[#974ae4] hover:border-[#974ae4] disabled:hover:border-purple-200`;

const FORCE_PUBLISH_ACTION_BTN = `${ROW_ACTION_BTN_BASE} border-green-200 bg-green-50 text-green-600 hover:border-green-500 hover:bg-green-500 hover:text-white`;

const formalPublishModeLabel = (
  mode?: string | null
): "Auto Publish" | "Manual Publish" | null => {
  const normalized = (mode || "").toLowerCase();
  if (normalized === "cron_auto_publish" || normalized === "auto_publish") {
    return "Auto Publish";
  }
  // ESL Force Publish (hub) and legacy ESL publish with actor.
  if (
    normalized === "manual_force_publish" ||
    normalized === "manual_publish"
  ) {
    return "Manual Publish";
  }
  return null;
};

const isCombinedAccount = (account: AssessmentAccount): boolean =>
  Boolean(account.combined) ||
  Boolean(account.disconnectReviewRow?.combined) ||
  (account.executives || []).some(exec => Boolean(exec.notes?.combined_with)) ||
  (account.users || []).some(user => Boolean(user.notes?.combined_with));

const getCombinedAccountNameTag = (
  account: AssessmentAccount
): { label: string; title: string; className: string } | null => {
  if (!isCombinedAccount(account)) return null;
  if (accountHasDisconnectHistory(account)) {
    return {
      label: HUB_COMBINED_DISCONNECT_LABEL,
      title:
        "Formal and Team assessments published together (same due date); this account had a disconnect workflow.",
      className: COMBINED_DISCONNECT_TAG_CLASS,
    };
  }
  return {
    label: HUB_COMBINED_PUBLISH_LABEL,
    title:
      "Formal and Team assessments published together (same due date); one shared level and no disconnect on this account.",
    className: PUBLISH_MODE_TAG_CLASS,
  };
};

const getCompletedTabAccounts = (
  assessment: AssessmentWithAccounts,
  view: "aligned" | "disconnects"
): AssessmentAccount[] => {
  const accounts = assessment.accounts || [];
  if (view === "disconnects") {
    return accounts.filter(isUnpublishedDisconnectAccount);
  }
  return accounts.filter(
    account => !account.hubPublished && !isUnpublishedDisconnectAccount(account)
  );
};

type AssessmentDisconnectBadge = {
  tier: string;
  delta: number;
  count: number;
};

const getAssessmentDisconnectBadge = (
  assessment: AssessmentWithAccounts
): AssessmentDisconnectBadge | null => {
  const active = getCompletedTabAccounts(assessment, "disconnects");
  if (!active.length) return null;

  const strongest = active.reduce((best, account) =>
    (account.disconnectDelta ?? 0) > (best.disconnectDelta ?? 0)
      ? account
      : best
  );

  return {
    tier: strongest.disconnectTier ?? "soft",
    delta: strongest.disconnectDelta ?? 0,
    count: active.length,
  };
};

/** Most actionable disconnect workflow status on an assessment card. */
const getAssessmentDisconnectWorkflowStatus = (
  assessment: AssessmentWithAccounts
): string | null => {
  const statuses = (assessment.accounts || []).map(account =>
    (account.disconnectStatus ?? "none").toLowerCase()
  );
  if (statuses.includes("resolved")) return "resolved";
  if (statuses.includes("pending_resolution")) return "pending_resolution";
  if (statuses.includes("published")) return "published";
  return null;
};

const countReadyToPublishDisconnectAccounts = (
  assessment: AssessmentWithAccounts
): number =>
  (assessment.accounts || []).filter(
    account =>
      (account.disconnectStatus ?? "none").toLowerCase() === "published" &&
      !account.hubPublished
  ).length;

const DisconnectWorkflowStatusBadge: React.FC<{ status: string }> = ({
  status,
}) => (
  <span className={getDisconnectActionPillBadgeClass(status)}>
    {getDisconnectActionLabel(status)}
  </span>
);

const getDefaultCompletedAccountView = (
  assessment: AssessmentWithAccounts
): "aligned" | "disconnects" => {
  if (getCompletedTabAccounts(assessment, "aligned").length > 0) {
    return "aligned";
  }
  if (assessmentHasDisconnectAccounts(assessment)) {
    return "disconnects";
  }
  return "aligned";
};

const hasPublishableAlignedAccounts = (
  assessment: AssessmentWithAccounts
): boolean => (assessment.accounts || []).some(isAlignedPublishableAccount);

const normalizeQuestionnaireType = (
  type: string | null | undefined
): "short" | "long" => {
  if (!type) return "long";
  const normalized = type.toLowerCase();
  if (normalized === "small" || normalized === "short") return "short";
  return "long";
};

const isPersistedSkippedExecutive = (exec: ExecutiveAssignment): boolean =>
  Boolean(exec.notes?.skipped);

const isSkippedExecutive = (exec: ExecutiveAssignment): boolean =>
  Boolean(exec.notes?.skipped) || exec.status?.toLowerCase() === "ignored";

// Map API user to ExecutiveAssignment
/** Ignored only when publish/resolve persisted notes.skipped — not display-only. */
const isDisconnectHubDisplayIgnored = (
  _isResponded: boolean | undefined,
  notes: { skipped?: boolean } | null | undefined,
  _disconnectStatus?: string | null
): boolean => Boolean(notes?.skipped);

const mapApiUserToExecutive = (
  user: ApiAssessmentUser,
  disconnectStatus?: string | null
): ExecutiveAssignment => {
  const displayIgnored = isDisconnectHubDisplayIgnored(
    user.is_responded,
    user.notes,
    disconnectStatus
  );
  return {
    id: user.assignment_id || user.user_id,
    user_id: user.user_id,
    assignment_id: user.assignment_id,
    name: user.name,
    email: user.email,
    role: user.role,
    status: displayIgnored
      ? "ignored"
      : user.is_responded
        ? "completed"
        : "pending",
    is_responded: user.is_responded,
    carried: user.carried === true,
    submitted_at: user.submitted_at,
    responses: user.responses,
    notes: user.notes,
  };
};

// Map API account to AssessmentAccount
const mapApiDisconnectResolution = (
  raw?: AccountDisconnectResolutionSummary | null
): AccountDisconnectResolutionSummary | null => {
  if (!raw) return null;
  const status = (raw.status || "").toLowerCase();
  if (status && status !== "resolved" && status !== "published") {
    return null;
  }
  const agreedLevel =
    typeof raw.agreedLevel === "number" ? raw.agreedLevel : null;
  return {
    id: raw.id,
    status: raw.status ?? null,
    agreedLevel,
    agreedLevelLabel:
      raw.agreedLevelLabel ||
      (agreedLevel != null ? getAccountLevelVerboseLabel(agreedLevel) : null),
    rationale: raw.rationale ?? null,
    resolvedBy: raw.resolvedBy ?? null,
    resolvedById: raw.resolvedById ?? null,
    resolvedAt: raw.resolvedAt ?? null,
  };
};

const disconnectResolutionFromReviewRow = (
  row?: DisconnectReviewAccount | null
): AccountDisconnectResolutionSummary | null => {
  if (!row?.resolution) return null;
  const status = (
    row.resolution.status ||
    row.disconnectStatus ||
    ""
  ).toLowerCase();
  if (status !== "resolved" && status !== "published") return null;
  const agreedLevel =
    row.resolution.agreed_level ??
    (row.resolution as { agreedLevel?: number }).agreedLevel ??
    null;
  return {
    id: row.resolution.id || row.resolutionId || undefined,
    status: row.resolution.status ?? row.disconnectStatus ?? null,
    agreedLevel: typeof agreedLevel === "number" ? agreedLevel : null,
    agreedLevelLabel:
      typeof agreedLevel === "number"
        ? getAccountLevelVerboseLabel(agreedLevel)
        : null,
    rationale: row.resolution.resolution_justification ?? null,
    resolvedBy: row.resolution.resolvedByName ?? row.resolvedByName ?? null,
    resolvedById: null,
    resolvedAt: row.resolution.resolved_at ?? null,
  };
};

const parseLevelNumber = (
  value: string | number | null | undefined
): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value == null) return 0;
  const match = String(value).match(/-?\d+/);
  return match ? Number(match[0]) : 0;
};

const resolveAccountDisconnectResolution = (
  account: AssessmentAccount
): AccountDisconnectResolutionSummary | null => {
  const existing =
    account.disconnectResolution ||
    disconnectResolutionFromReviewRow(account.disconnectReviewRow);
  if (existing) return existing;

  // Self→AL disconnect-accept may leave override without resolution payload
  // after the workflow ran. Do not invent a resolution when disconnect was
  // never triggered (Formal ESL override from Ongoing while status is none).
  if (!account.disconnectDetected || !account.override?.override_level) {
    return null;
  }
  const workflowStatus = (account.disconnectStatus ?? "none").toLowerCase();
  if (workflowStatus === "none" && !account.resolutionId) {
    return null;
  }
  const agreed = parseLevelNumber(account.override.override_level);
  if (agreed == null || !Number.isFinite(agreed)) return null;
  const status =
    account.disconnectStatus && account.disconnectStatus !== "none"
      ? account.disconnectStatus
      : "published";
  return {
    id: account.resolutionId ?? undefined,
    status,
    agreedLevel: agreed,
    agreedLevelLabel: getAccountLevelVerboseLabel(agreed),
    rationale: null,
    resolvedBy: account.override.overridden_by ?? null,
    resolvedById: null,
    resolvedAt: account.override.overridden_at ?? null,
  };
};

/** Written when ESL Click-to-Override publishes and closes an open disconnect. */
const ESL_OVERRIDE_CLOSED_DISCONNECT_RATIONALE =
  "ESL override published this account and closed the disconnect.";

const getAccountOverrideRationaleEntry = (
  assessment:
    | {
        notes?: {
          override_rationales?: Record<string, OverrideRationaleEntry> | null;
        } | null;
      }
    | null
    | undefined,
  accountId: string
): OverrideRationaleEntry | null => {
  if (!accountId) return null;
  const entry = assessment?.notes?.override_rationales?.[accountId];
  return entry ?? null;
};

const isEslClosedByOverrideResolution = (
  primaryResolution: AccountDisconnectResolutionSummary | null
): boolean =>
  (primaryResolution?.rationale?.trim() || "") ===
  ESL_OVERRIDE_CLOSED_DISCONNECT_RATIONALE;

/**
 * True only when ESL Click-to-Override closed the disconnect with no prior
 * PS resolve / ESL Accept — in that case Full Report shows Overridden alone.
 * PS resolve + Accept + later Override must still show both blocks.
 */
const isEslCloseOnlyWithoutPsOrAccept = (
  primaryResolution: AccountDisconnectResolutionSummary | null,
  opts?: { hasAcceptLog?: boolean }
): boolean => {
  if (!isEslClosedByOverrideResolution(primaryResolution)) return false;
  if (opts?.hasAcceptLog) return false;
  if (primaryResolution?.resolvedBy || primaryResolution?.resolvedAt) {
    return false;
  }
  return true;
};

/**
 * ESL Accept writes AssessmentOverride with the agreed level. That is not a
 * Completed-tab Override — only show the purple ESL block for a true override.
 * Click-to-Override (with stored rationale) that closed a disconnect still counts.
 */
const isManualEslOverride = (
  account: AssessmentAccount,
  primaryResolution: AccountDisconnectResolutionSummary | null,
  storedOverrideRationale?: string | null
): boolean => {
  const overrideLevel = account.override?.override_level;
  if (!overrideLevel) return false;

  // Click-to-Override always stores rationale on assessment.notes.
  if (storedOverrideRationale?.trim()) return true;

  // ESL override that auto-closed disconnect (not PS Accept).
  if (isEslClosedByOverrideResolution(primaryResolution)) return true;

  // Disconnect gap + override is accept/resolution, not Completed-tab ESL override.
  if (account.disconnectDetected) return false;
  if (!primaryResolution) return true;

  const agreed = primaryResolution.agreedLevel;
  const overridden = parseLevelNumber(overrideLevel);
  if (agreed != null && overridden != null && agreed === overridden) {
    return false;
  }
  // Disconnect resolution present: Accept still owns the override unless levels diverge.
  if (agreed == null) return false;
  return true;
};

/** Published / accepted / overridden level for Full Report Final display. */
const resolveFullReportFinalLevel = (
  account: AssessmentAccount
): string | null => {
  const primary = resolveAccountDisconnectResolution(account);
  const overrideLevel = account.override?.override_level;

  // Latest ESL Click-to-Override wins when it diverges from PS/Accept agreed.
  // (Accept also writes AssessmentOverride at the agreed level — same number is fine.)
  if (overrideLevel) {
    const overridden = parseLevelNumber(overrideLevel);
    const agreed = primary?.agreedLevel;
    if (
      agreed == null ||
      (overridden != null &&
        Number.isFinite(overridden) &&
        overridden !== agreed)
    ) {
      return overrideLevel;
    }
  }

  if (primary?.agreedLevel != null) return `L${primary.agreedLevel}`;
  if (overrideLevel) return overrideLevel;
  if (account.eslFinalLevel) return account.eslFinalLevel;
  if (account.published_level) return account.published_level;

  return (
    account.finalLevel ||
    account.final_level ||
    account.medianLevel ||
    account.median_level ||
    null
  );
};

/** Fold-in inputs count automatically; no executive waits on an ESL merge. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isPendingSelfFoldExecutive = (_exec: {
  notes?: {
    origin?: string;
    pending_merge?: boolean;
  } | null;
}): boolean => false;

const FORMAL_INCLUDED_ORIGIN = "included_member";

/** Included bucket: not selected on the Formal, current input counted. */
const isCadenceCurrentExecutive = (exec: {
  notes?: {
    origin?: string;
    carry_forward?: boolean;
  } | null;
}): boolean =>
  exec.notes?.origin === FORMAL_INCLUDED_ORIGIN ||
  (exec.notes?.origin === "cadence_current" &&
    exec.notes?.carry_forward === true);

/** Unassigned bucket: late take or demoted Current (or legacy fold-in). */
const isUnassignedParticipantExecutive = (exec: {
  notes?: {
    origin?: string;
    pending_merge?: boolean;
    carry_forward?: boolean;
  } | null;
}): boolean => {
  if (isCadenceCurrentExecutive(exec)) return false;
  if (exec.notes?.origin === "unassigned_self") return true;
  if (
    exec.notes?.origin === "cadence_current" &&
    exec.notes?.carry_forward === false
  ) {
    return true;
  }
  return false;
};

/** Assigned bucket: selected at Formal create (incl. Assigned Current carry). */
const isOriginallyAssignedExecutive = (exec: {
  notes?: {
    origin?: string;
    pending_merge?: boolean;
    carry_forward?: boolean;
  } | null;
}): boolean =>
  !isCadenceCurrentExecutive(exec) && !isUnassignedParticipantExecutive(exec);

const mapApiAccountToAssessmentAccount = (
  apiAccount: ApiAssessmentAccount
): AssessmentAccount => {
  const disconnectStatus = apiAccount.disconnectStatus ?? null;
  const executives =
    apiAccount.users?.map(user =>
      mapApiUserToExecutive(user, disconnectStatus)
    ) || [];
  // Official hub fractions: assigned only (exclude pending fold-in + skipped).
  const displayCountable = executives.filter(
    exec => !isSkippedExecutive(exec) && !isPendingSelfFoldExecutive(exec)
  );
  const displayResponses = displayCountable.filter(
    exec => exec.is_responded
  ).length;
  const useDisplayCounts = executives.length > 0;

  return {
    id: apiAccount.account_id,
    account_id: apiAccount.account_id,
    account_name: apiAccount.account_name,
    account_code: apiAccount.account_code,
    name: apiAccount.account_name,
    totalAssigned: useDisplayCounts
      ? displayCountable.length
      : apiAccount.requests_sent,
    requests_sent: useDisplayCounts
      ? displayCountable.length
      : apiAccount.requests_sent,
    responses: useDisplayCounts
      ? displayResponses
      : apiAccount.responses_received,
    responses_received: useDisplayCounts
      ? displayResponses
      : apiAccount.responses_received,
    minLevel: apiAccount.min_level || null,
    min_level: apiAccount.min_level || null,
    medianLevel: apiAccount.median_level || null,
    median_level: apiAccount.median_level || null,
    maxLevel: apiAccount.max_level || null,
    max_level: apiAccount.max_level || null,
    finalLevel: apiAccount.final_level || null,
    final_level: apiAccount.final_level || null,
    maturity_level: apiAccount.maturity_level || null,
    override: apiAccount.override,
    executives,
    users: apiAccount.users,
    disconnectDetected: apiAccount.disconnectDetected,
    disconnectDelta: apiAccount.disconnectDelta ?? null,
    disconnectTier: apiAccount.disconnectTier ?? null,
    disconnectStatus,
    resolutionId: apiAccount.resolutionId ?? null,
    disconnectResolution: mapApiDisconnectResolution(
      apiAccount.disconnectResolution
    ),
    hubPublished: apiAccount.hubPublished ?? false,
    published_at: apiAccount.published_at ?? null,
    published_level: apiAccount.published_level ?? null,
    eslFinalLevel: apiAccount.eslFinalLevel ?? null,
    finalLevelPending: Boolean(apiAccount.finalLevelPending),
    noResponseBanner: apiAccount.noResponseBanner ?? null,
    noResponseMode: apiAccount.noResponseMode ?? null,
    disconnectOngoing: Boolean(apiAccount.disconnectOngoing),
    publishMode: apiAccount.publishMode ?? null,
  };
};

const getAccountExecutivesForCompletion = (
  account: AssessmentAccount
): ExecutiveAssignment[] => {
  if (account.users?.length) {
    return account.users.map(user =>
      mapApiUserToExecutive(user, account.disconnectStatus)
    );
  }

  if (account.executives?.length) {
    return account.executives;
  }

  return [];
};

const getAccountCompletionCounts = (account: AssessmentAccount) => {
  const accountExecutives = getAccountExecutivesForCompletion(account);
  const totalAssigned =
    account.totalAssigned ||
    account.requests_sent ||
    accountExecutives.length ||
    0;
  const responsesCount = account.responses || account.responses_received || 0;

  return { totalAssigned, responsesCount, accountExecutives };
};

const executiveHasResponded = (exec: ExecutiveAssignment): boolean => {
  // Persisted skip counts as "done" for progress; Pending non-responders do not.
  if (exec.notes?.skipped) return true;
  const executiveStatus = exec.status?.toLowerCase();
  if (executiveStatus === "ignored") return false;
  return executiveStatus === "completed" || exec.is_responded === true;
};

const accountHasAllExecutivesResponded = (
  account: AssessmentAccount
): boolean => {
  const { accountExecutives } = getAccountCompletionCounts(account);

  // Only persisted notes.skipped unlocks Complete — open non-responders stay Pending.
  // Pending fold-in does not count toward assigned completion until ESL merges it.
  const countableExecutives = accountExecutives.filter(
    exec =>
      !isPersistedSkippedExecutive(exec) && !isPendingSelfFoldExecutive(exec)
  );

  if (countableExecutives.length === 0) {
    return accountExecutives.length > 0
      ? accountExecutives.every(isPersistedSkippedExecutive)
      : false;
  }

  return countableExecutives.every(
    exec =>
      exec.is_responded === true || exec.status?.toLowerCase() === "completed"
  );
};

interface ProgressParticipantRow {
  id: string;
  name: string;
  accountNames: string[];
  hasResponded: boolean;
}

const buildProgressParticipants = (
  accounts: AssessmentAccount[]
): ProgressParticipantRow[] => {
  const participantMap = new Map<string, ProgressParticipantRow>();

  accounts.forEach(account => {
    const accountName =
      account.name || account.account_name || "Unknown Account";
    const executives = getAccountExecutivesForCompletion(account);

    executives.forEach(exec => {
      const id =
        exec.id || exec.user_id || exec.email || `${exec.name}-${accountName}`;
      const responded = executiveHasResponded(exec);
      const existing = participantMap.get(id);

      if (existing) {
        if (!existing.accountNames.includes(accountName)) {
          existing.accountNames.push(accountName);
        }
        existing.hasResponded = existing.hasResponded || responded;
      } else {
        participantMap.set(id, {
          id,
          name: exec.name,
          accountNames: [accountName],
          hasResponded: responded,
        });
      }
    });
  });

  return Array.from(participantMap.values());
};

const levelFromNumber = (value: number | null | undefined): string | null =>
  value == null ? null : getAccountLevelVerboseLabel(value);

const formatSponsorRole = (
  role?: string | null,
  title?: string | null
): string => {
  if (title) return title;
  if (!role) return "Executive Sponsor";
  return role.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
};

const mapDisconnectReviewAccountToAssessmentAccount = (
  row: DisconnectReviewAccount
): AssessmentAccount => {
  const executives: ExecutiveAssignment[] = row.sponsorAssessments.map(
    sponsor => {
      const hasLevel =
        typeof sponsor.level === "number" && Number.isFinite(sponsor.level);
      return {
        id: sponsor.executiveId,
        user_id: sponsor.executiveId,
        assignment_id: sponsor.assignmentId ?? undefined,
        name: sponsor.name,
        role: formatSponsorRole(sponsor.role, sponsor.title),
        status: hasLevel || sponsor.submittedAt ? "completed" : "pending",
        is_responded: Boolean(hasLevel || sponsor.submittedAt),
        submitted_at: sponsor.submittedAt ?? null,
        responses: {
          maturity_level: hasLevel
            ? sponsor.levelLabel ||
              (sponsor.level != null ? `L${sponsor.level}` : undefined)
            : undefined,
          rationale: sponsor.rationale || "",
        },
      };
    }
  );
  const sponsorCount = executives.length;
  const disconnectResolution = disconnectResolutionFromReviewRow(row);
  const publishedLevel =
    disconnectResolution?.agreedLevel != null
      ? `L${disconnectResolution.agreedLevel}`
      : row.eslFinalLevel != null
        ? `L${row.eslFinalLevel}`
        : null;
  const finalLevelValue =
    disconnectResolution?.agreedLevel != null
      ? levelFromNumber(disconnectResolution.agreedLevel)
      : levelFromNumber(row.eslFinalLevel);

  return {
    id: row.accountId,
    account_id: row.accountId,
    account_name: row.accountName,
    name: row.accountName,
    totalAssigned: sponsorCount,
    requests_sent: sponsorCount,
    responses: sponsorCount,
    responses_received: sponsorCount,
    minLevel: levelFromNumber(row.minLevel),
    min_level: levelFromNumber(row.minLevel),
    medianLevel: levelFromNumber(row.medianLevel),
    median_level: levelFromNumber(row.medianLevel),
    maxLevel: levelFromNumber(row.maxLevel),
    max_level: levelFromNumber(row.maxLevel),
    // Disconnect accept stores agreed level as AssessmentOverride — do not
    // label Final as "(Overridden)" or show Completed-tab Override chrome.
    finalLevel: finalLevelValue,
    final_level: finalLevelValue,
    overrideLevel: false,
    override: row.isEslOverridden
      ? {
          override_level: levelFromNumber(row.eslFinalLevel),
          overridden_by: row.publishedByName ?? null,
          overridden_at: row.resolution?.published_at ?? null,
        }
      : undefined,
    executives,
    disconnectDetected: true,
    disconnectDelta: row.delta,
    disconnectTier: row.deltaTier,
    disconnectStatus: row.disconnectStatus,
    resolutionId: row.resolutionId ?? null,
    disconnectResolution,
    published_level: publishedLevel,
    published_at:
      row.resolution?.published_at ?? row.assessmentPublishedAt ?? null,
    eslFinalLevel: levelFromNumber(row.eslFinalLevel),
    hubPublished: Boolean(row.hubPublished),
    combined: Boolean(row.combined),
    publishMode: row.publishMode ?? null,
    disconnectReviewRow: row,
  };
};

const getDisconnectSelectionKey = (
  assessmentId: string,
  accountId: string
): string => `${assessmentId}::${accountId}`;

const canSelectCompletedDisconnectAccount = (
  account: AssessmentAccount
): boolean =>
  Boolean(account.disconnectDetected) &&
  (account.disconnectStatus ?? "none") === "none" &&
  !account.hubPublished;

const isDisconnectTriggerSelectableAccount = (
  account: AssessmentAccount,
  allowCompletedWithoutReviewRow: boolean
): boolean => {
  if ((account.disconnectStatus ?? "none") !== "none") return false;
  if (
    account.disconnectReviewRow &&
    Boolean(account.disconnectDetected) &&
    !account.hubPublished
  ) {
    return true;
  }
  return (
    allowCompletedWithoutReviewRow &&
    canSelectCompletedDisconnectAccount(account)
  );
};

const hasSelectableCompletedDisconnectAccounts = (
  assessment: AssessmentWithAccounts
): boolean =>
  getCompletedTabAccounts(assessment, "disconnects").some(account =>
    isDisconnectTriggerSelectableAccount(account, true)
  );

const buildSponsorAssessmentsFromExecutives = (
  executives: ExecutiveAssignment[]
): DisconnectSponsorAssessment[] =>
  executives
    .filter(exec => executiveHasResponded(exec))
    .map(exec => {
      const maturityLevel = exec.responses?.maturity_level;
      const level = parseLevelNumber(maturityLevel);
      const levelLabel = maturityLevel
        ? String(maturityLevel).trim()
        : getAccountLevelVerboseLabel(level);

      return {
        executiveId: String(exec.user_id || exec.id || ""),
        name: exec.name || "Unknown",
        title: null,
        role: exec.role || null,
        level,
        levelLabel,
        rationale: exec.responses?.rationale ?? null,
        submittedAt: exec.submitted_at ?? null,
        assignmentId: exec.assignment_id ?? null,
      };
    });

const buildDisconnectReviewFromAssessmentAccount = (
  assessment: AssessmentWithAccounts,
  account: AssessmentAccount
): DisconnectReviewAccount => {
  const accountId = getAssessmentAccountId(account);
  const minLevel = parseLevelNumber(account.min_level || account.minLevel);
  const medianLevel = parseLevelNumber(
    account.median_level || account.medianLevel
  );
  const maxLevel = parseLevelNumber(account.max_level || account.maxLevel);
  const eslFinalLevel =
    (typeof account.eslFinalLevel === "number"
      ? account.eslFinalLevel
      : null) ?? parseLevelNumber(account.final_level || account.finalLevel);

  const sponsorAssessments = buildSponsorAssessmentsFromExecutives(
    getAccountExecutivesForCompletion(account)
  );

  return {
    accountId,
    accountName: account.account_name || account.name || "Unknown",
    assessmentId: assessment.id,
    assessmentTitle: assessment.name || "",
    assessmentStatus: assessment.status || "completed",
    delta: account.disconnectDelta ?? 0,
    deltaTier: account.disconnectTier || "soft",
    minLevel,
    maxLevel,
    medianLevel,
    eslFinalLevel,
    eslJustification: null,
    isEslOverridden: Boolean(account.override),
    sponsorAssessments,
    disconnectStatus: account.disconnectStatus ?? "none",
    resolutionId: account.resolutionId ?? null,
  };
};

const readReviewRowAgreedLevel = (
  reviewRow: DisconnectReviewAccount | null | undefined
): number | null => {
  const resolution = reviewRow?.resolution as
    | Record<string, unknown>
    | null
    | undefined;
  if (!resolution) return null;
  const raw = resolution.agreed_level ?? resolution.agreedLevel;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const parsed = parseLevelNumber(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const mapResolutionSponsorAssessments = (
  sponsors:
    | Array<DisconnectSponsorAssessment | Record<string, unknown>>
    | undefined
): DisconnectSponsorAssessment[] =>
  (sponsors ?? [])
    .filter(entry => entry && typeof entry === "object")
    .map(entry => {
      const raw = entry as Record<string, unknown>;
      return {
        executiveId: String(raw.executiveId ?? raw.executive_id ?? ""),
        name: String(raw.name ?? "Unknown"),
        title: (raw.title as string | null | undefined) ?? null,
        role: (raw.role as string | null | undefined) ?? null,
        level:
          typeof raw.level === "number"
            ? raw.level
            : parseLevelNumber(String(raw.levelLabel ?? raw.level ?? "")),
        levelLabel: String(raw.levelLabel ?? raw.level_label ?? ""),
        rationale: (raw.rationale as string | null | undefined) ?? null,
        submittedAt:
          (raw.submittedAt as string | null | undefined) ??
          (raw.submitted_at as string | null | undefined) ??
          null,
        assignmentId:
          (raw.assignmentId as string | null | undefined) ??
          (raw.assignment_id as string | null | undefined) ??
          null,
      };
    })
    .filter(entry => entry.executiveId);

const mergeResolutionDetailIntoReviewRow = (
  reviewRow: DisconnectReviewAccount,
  detail: DisconnectResolution
): DisconnectReviewAccount => {
  const agreedLevel =
    detail.agreed_level ??
    (detail as DisconnectResolution & { agreedLevel?: number }).agreedLevel ??
    readReviewRowAgreedLevel(reviewRow);
  const detailSponsors = mapResolutionSponsorAssessments(
    detail.sponsor_assessments
  );

  return {
    ...reviewRow,
    resolvedByName:
      detail.resolvedByName ??
      reviewRow.resolvedByName ??
      reviewRow.resolution?.resolvedByName ??
      null,
    resolutionId: reviewRow.resolutionId ?? detail.id,
    disconnectStatus: reviewRow.disconnectStatus ?? detail.status,
    sponsorAssessments:
      reviewRow.sponsorAssessments?.length > 0
        ? reviewRow.sponsorAssessments
        : detailSponsors,
    resolution: {
      ...(reviewRow.resolution ?? { id: detail.id, status: detail.status }),
      id: detail.id,
      status: detail.status,
      agreed_level: agreedLevel,
      resolution_justification:
        detail.resolution_justification ??
        reviewRow.resolution?.resolution_justification ??
        null,
      resolved_at:
        detail.resolved_at ?? reviewRow.resolution?.resolved_at ?? null,
      resolvedByName:
        detail.resolvedByName ??
        reviewRow.resolution?.resolvedByName ??
        reviewRow.resolvedByName ??
        null,
      context_note:
        detail.context_note ?? reviewRow.resolution?.context_note ?? null,
    },
  };
};

const buildReviewRowFromResolutionDetail = (
  assessment: AssessmentWithAccounts,
  account: AssessmentAccount,
  detail: DisconnectResolution
): DisconnectReviewAccount => {
  const base = buildDisconnectReviewFromAssessmentAccount(assessment, account);
  return mergeResolutionDetailIntoReviewRow(base, detail);
};

const buildTriggerDisconnectAccountPayload = (
  row: DisconnectReviewAccount
): TriggerDisconnectAccountPayload => ({
  accountId: row.accountId,
  assessmentId: row.assessmentId,
  accountName: row.accountName,
  deltaTier: row.deltaTier,
  delta: row.delta,
  minLevel: row.minLevel,
  maxLevel: row.maxLevel,
  medianLevel: row.medianLevel,
  eslFinalLevel: row.eslFinalLevel,
  eslJustification: row.eslJustification ?? null,
  sponsorAssessments: row.sponsorAssessments,
});

const buildDefaultDisconnectDueDate = (defaultDueDateDays = 7): string =>
  addUtcDaysToToday(defaultDueDateDays);

const buildDisconnectContextNote = (accountNames: string[]): string => {
  if (accountNames.length === 1) {
    return `Sponsor level gap on ${accountNames[0]} — please resolve.`;
  }
  return "Sponsor level gap on selected accounts — please resolve.";
};

type DisconnectSubTab = "ongoing" | "logs";

const isFinalDisconnectStatus = (status?: string | null): boolean =>
  status === "published" || status === "unresolved";

const isDisconnectHubPublishedLogRow = (
  row: DisconnectReviewAccount
): boolean =>
  isFinalDisconnectStatus(row.disconnectStatus) && Boolean(row.hubPublished);

const resolveDisconnectPublishedAt = (
  row: DisconnectReviewAccount
): string | null => {
  if (row.hubPublished) {
    return row.assessmentPublishedAt ?? row.resolution?.published_at ?? null;
  }
  return null;
};

const resolveDisconnectCardHubPublishedStatus = (
  accountRows: DisconnectReviewAccount[]
): "published" | "completed" => {
  if (accountRows.some(isDisconnectHubPublishedLogRow)) {
    return "published";
  }
  return "completed";
};

const matchesDisconnectSubTab = (
  row: DisconnectReviewAccount,
  subTab: DisconnectSubTab
): boolean => {
  const status = row.disconnectStatus;
  const hubPublished = Boolean(row.hubPublished);
  if (subTab === "ongoing") {
    return (
      status === "pending_resolution" ||
      status === "resolved" ||
      (status === "published" && !hubPublished)
    );
  }
  return isFinalDisconnectStatus(status) && hubPublished;
};

const DisconnectKpiCard: React.FC<{
  value: number;
  label: string;
  valueClassName: string;
  loading?: boolean;
}> = ({ value, label, valueClassName, loading = false }) => (
  <div className="flex min-h-[72px] flex-col items-start justify-center rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
    <span
      className={`text-2xl font-bold tabular-nums leading-none ${valueClassName} ${loading ? "opacity-40" : ""}`}
    >
      {value}
    </span>
    <span className="mt-1.5 text-xs font-medium text-gray-600">{label}</span>
  </div>
);

type PublishedKindTab = AssessmentKindTab;

// Map API assessment response to AssessmentWithAccounts
const mapApiAssessmentToAssessmentWithAccounts = (
  apiAssessment: ApiAssessmentResponse
): AssessmentWithAccounts => {
  const accountSource = apiAssessment.accounts?.length
    ? apiAssessment.accounts
    : apiAssessment.target_accounts;
  const accounts = accountSource?.map(mapApiAccountToAssessmentAccount) || [];
  const normalizedStatus = normalizeStatus(apiAssessment.status);
  const category = normalizeQuestionnaireType(
    apiAssessment.target_questionnaires
  );

  // Extract template_id from questions_data.metadata.template_id or data.metadata.template_id
  const templateId =
    (apiAssessment as any).questions_data?.metadata?.template_id ||
    (apiAssessment as any).data?.metadata?.template_id ||
    (apiAssessment as any).template_id ||
    "";

  return {
    id: apiAssessment.assessment_id || apiAssessment.id || "",
    name: normalizeTeamAssessmentDisplayName(
      apiAssessment.name || apiAssessment.title,
      "Untitled Assessment",
      apiAssessment.target_accounts?.[0]?.account_name ||
        apiAssessment.accounts?.[0]?.account_name
    ),
    description: (() => {
      const raw = apiAssessment.description;
      const normalized = raw
        ? normalizeTeamAssessmentDisplayName(raw, raw)
        : undefined;
      if (
        isPublishedSelfOrigin(apiAssessment) ||
        isSelfAssessmentType(apiAssessment.assessment_type)
      ) {
        return normalized || TEAM_ASSESSMENT_DESCRIPTION;
      }
      return normalized;
    })(),
    template_id: templateId,
    template_name: "",
    status: normalizedStatus as any,
    progress: apiAssessment.progress || 0,
    participants: apiAssessment.participants || 0,
    total_responses: apiAssessment.total_responses || 0,
    total_questions: apiAssessment.questions || 0,
    due_on:
      apiAssessment.due_on ||
      apiAssessment.due_date ||
      apiAssessment.start_date ||
      "",
    created_at: apiAssessment.created_at,
    start_date: apiAssessment.start_date ?? null,
    updated_at: apiAssessment.updated_at,
    published_at: apiAssessment.published_at || null,
    assessment_type: apiAssessment.assessment_type ?? null,
    notes: apiAssessment.notes ?? null,
    hub_publish_scope: apiAssessment.hub_publish_scope ?? null,
    has_self_fold: apiAssessment.has_self_fold ?? false,
    created_by: {
      id: apiAssessment.created_by_user?.id || apiAssessment.created_by,
      name: apiAssessment.created_by_user?.name || "System",
      email: apiAssessment.created_by_user?.email || "",
    },
    target_accounts: apiAssessment.target_accounts?.map(acc => ({
      account_id: acc.account_id,
      account_name: acc.account_name,
      account_code: acc.account_code,
    })),
    categories: apiAssessment.categories || [],
    accounts,
    category,
    createdBy: apiAssessment.created_by_user?.name || "System",
    created_by_user: apiAssessment.created_by_user,
    dueDate:
      apiAssessment.due_on ||
      apiAssessment.due_date ||
      apiAssessment.start_date ||
      null,
    target_questionnaires: apiAssessment.target_questionnaires,
    due_date_info: apiAssessment.due_date_info,
    abort_info: (apiAssessment as any).abort_info,
    completed_at: (apiAssessment as any).completed_at,
    completed_on: (apiAssessment as any).completed_on,
    questions_data:
      (apiAssessment as any).questions_data || (apiAssessment as any).data,
  };
};

const QUESTIONNAIRE_TYPE_OPTIONS = [
  { label: "All Types", value: "all" },
  { label: "Short Assessment", value: "short" },
  { label: "Long Assessment", value: "long" },
];

const DISCONNECT_TYPE_OPTIONS = [
  { label: "All Types", value: "all" },
  { label: "Strong disconnect", value: "strong" },
  { label: "Soft disconnect", value: "soft" },
];

interface AssessmentToolbarProps {
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  typeFilterValue: string;
  onTypeFilterChange: (value: string) => void;
  typeFilterOptions?: { label: string; value: string }[];
  /** Optional controls shown to the right of search/filter (e.g. Ongoing/Logs). */
  trailingContent?: React.ReactNode;
  className?: string;
}

const AssessmentToolbar: React.FC<AssessmentToolbarProps> = ({
  searchPlaceholder = "Search assessments...",
  searchValue,
  onSearchChange,
  typeFilterValue,
  onTypeFilterChange,
  typeFilterOptions = QUESTIONNAIRE_TYPE_OPTIONS,
  trailingContent,
  className = "mb-5",
}) => (
  <div
    className={`flex items-center justify-end gap-3 flex-wrap shrink-0 ${className}`}
  >
    <div className="min-w-[10rem] w-52 sm:w-72">
      <SearchInput
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={e => onSearchChange(e.target.value)}
      />
    </div>
    <DropdownSwitcher
      className="shrink-0"
      value={typeFilterValue}
      prefix="Type: "
      options={typeFilterOptions}
      onChange={onTypeFilterChange}
      listboxLabel="Type"
    />
    {trailingContent ?? null}
  </div>
);

// Reusable Assessment Card Component Props
interface AssessmentCardComponentProps {
  assessment: AssessmentWithAccounts;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onViewStatistics?: (assessment: AssessmentWithAccounts) => void;
  onViewReport?: (assessment: AssessmentWithAccounts) => void;
  onEdit?: (assessment: AssessmentWithAccounts) => void;
  onDelete?: (assessment: AssessmentWithAccounts) => void;
  onLaunch?: (assessment: AssessmentWithAccounts) => void;
  isLaunching?: boolean;
  onPublish?: (assessment: AssessmentWithAccounts) => void;
  isPublishing?: boolean;
  onViewFullReport?: (
    assessment: AssessmentWithAccounts,
    account: AssessmentAccount
  ) => void;
  onOpenReviewAssessment?: (
    assessment: AssessmentWithAccounts,
    account: AssessmentAccount
  ) => void;
  onOpenOverrideModal?: (
    assessment: AssessmentWithAccounts,
    account: AssessmentAccount
  ) => void;
  onExtendDueDate?: (assessment: AssessmentWithAccounts) => void;
  onOpenExtendedReason?: (assessment: AssessmentWithAccounts) => void;
  onAbortAssessment?: (assessment: AssessmentWithAccounts) => void;
  statusTab:
    | "pending"
    | "ongoing"
    | "completed"
    | "published"
    | "aborted"
    | "disconnects";
  userRole: string;
  getLevelDisplayName: (level: string | null | undefined) => string;
  getStatusDisplayName: (status: string) => string;
  isShortCategory: (category: string | null | undefined) => boolean;
  shouldShowLevelMetrics: (category: string | null | undefined) => boolean;
  formatDueDate: (date?: string | null) => string;
  getAccountLevel: (
    account: AssessmentAccount,
    levelType: "min" | "median" | "max" | "final"
  ) => string | null;
  getAccountResponseCounts: (account: AssessmentAccount) => {
    totalAssigned: number;
    responsesCount: number;
    responseRate: number;
  };
  isOverdue: (dueDate?: string | null) => boolean;
  /** Team Assessed: everyone is included, so Current · Unassigned is omitted. */
  hideCurrentUnassigned?: boolean;
  disconnectSelectionEnabled?: boolean;
  completedDisconnectSelectionEnabled?: boolean;
  selectedDisconnectAccountKeys?: Set<string>;
  onToggleDisconnectAccount?: (assessmentId: string, accountId: string) => void;
  onToggleAllDisconnectAccounts?: (
    assessmentId: string,
    accountIds: string[],
    selectAll: boolean
  ) => void;
  disconnectAcceptEnabled?: boolean;
  onOpenResolutionReview?: (
    assessment: AssessmentWithAccounts,
    account: AssessmentAccount
  ) => void;
  hubPublishSelectionEnabled?: boolean;
  selectedHubPublishAccountIds?: Set<string>;
  onToggleHubPublishAccount?: (assessmentId: string, accountId: string) => void;
  onToggleAllHubPublishAccounts?: (
    assessmentId: string,
    accountIds: string[],
    selectAll: boolean
  ) => void;
  onPublishAssessment?: (assessment: AssessmentWithAccounts) => void;
  /** Team hub: button stays visible and is disabled until the publish cron fails. */
  forcePublishDisabled?: boolean;
  forcePublishTitle?: string;
  completedAccountView?: "aligned" | "disconnects";
  onCompletedAccountViewChange?: (view: "aligned" | "disconnects") => void;
  /** Disconnects tab Team/Formal filter — drives accept/publish copy. */
  disconnectAssessmentKind?: AssessmentKindTab;
  /** Published tab: latest hub publish per account (Team/Formal scope). */
  publishedOverrideEligibleKeys?: Set<string>;
  /** Disconnects hub: show Manual/Auto publish tags on Logs rows only. */
  disconnectSubTab?: DisconnectSubTab;
}

// Reusable Assessment Card Component
const AssessmentCardComponent: React.FC<AssessmentCardComponentProps> = ({
  assessment,
  isExpanded,
  onToggleExpand,
  onViewStatistics,
  onViewReport,
  onEdit,
  onDelete,
  onLaunch,
  isLaunching,
  onPublish,
  isPublishing,
  onViewFullReport,
  onOpenReviewAssessment,
  onOpenOverrideModal,
  onExtendDueDate,
  onOpenExtendedReason,
  onAbortAssessment,
  statusTab,
  userRole,
  getLevelDisplayName,
  getStatusDisplayName,
  isShortCategory,
  shouldShowLevelMetrics,
  formatDueDate,
  getAccountLevel,
  getAccountResponseCounts,
  isOverdue,
  hideCurrentUnassigned = false,
  disconnectSelectionEnabled = false,
  completedDisconnectSelectionEnabled = false,
  selectedDisconnectAccountKeys,
  onToggleDisconnectAccount,
  onToggleAllDisconnectAccounts,
  disconnectAcceptEnabled = false,
  onOpenResolutionReview,
  hubPublishSelectionEnabled = false,
  selectedHubPublishAccountIds,
  onToggleHubPublishAccount,
  onToggleAllHubPublishAccounts,
  onPublishAssessment,
  forcePublishDisabled = false,
  forcePublishTitle,
  completedAccountView = "aligned",
  onCompletedAccountViewChange,
  disconnectAssessmentKind,
  publishedOverrideEligibleKeys,
  disconnectSubTab,
}) => {
  const hubAssessmentId =
    assessment.id || (assessment as { assessment_id?: string }).assessment_id;
  const createdBy =
    assessment.created_by_user?.name || assessment.createdBy || "System";
  const showCompletedAccountSubTabs =
    statusTab === "completed" || statusTab === "ongoing";
  const alignedAccountCount = getCompletedTabAccounts(
    assessment,
    "aligned"
  ).length;
  const disconnectAccountCount = getCompletedTabAccounts(
    assessment,
    "disconnects"
  ).length;
  const requestedAccountView = showCompletedAccountSubTabs
    ? completedAccountView
    : getDefaultCompletedAccountView(assessment);
  const effectiveCompletedAccountView: "aligned" | "disconnects" =
    requestedAccountView === "disconnects" &&
    disconnectAccountCount === 0 &&
    alignedAccountCount > 0
      ? "aligned"
      : requestedAccountView === "aligned" &&
          alignedAccountCount === 0 &&
          disconnectAccountCount > 0
        ? "disconnects"
        : requestedAccountView;
  const showCompletedDisconnectColumns =
    (statusTab === "completed" || statusTab === "ongoing") &&
    effectiveCompletedAccountView === "disconnects";
  const disconnectCheckboxEnabled =
    disconnectSelectionEnabled || completedDisconnectSelectionEnabled;

  // Helper function to format date safely
  const formatDate = (
    date: string | null | undefined,
    fallback?: string
  ): string => {
    if (!date) return fallback || "N/A";
    try {
      return formatAssessmentDateTimeUtc(date, true) || fallback || "N/A";
    } catch {
      return fallback || "N/A";
    }
  };

  const getCreatedDate = (): string =>
    formatDate(
      assessment.start_date ||
        assessment.created_at ||
        (assessment as any).createdDate
    );

  // Helper function to render metadata item
  const renderMetadataItem = (
    key: string,
    icon: React.ReactNode,
    label: string,
    value: string
  ) => (
    <div key={key} className="flex items-center gap-1">
      {icon}
      <span>
        {label}: {value}
      </span>
    </div>
  );

  // Render metadata based on status tab
  const renderMetadata = () => {
    const metadataItems: React.ReactNode[] = [];

    switch (statusTab) {
      case "pending":
      case "ongoing":
        metadataItems.push(
          renderMetadataItem(
            "created",
            <Calendar size={16} />,
            "Created",
            getCreatedDate()
          ),
          renderMetadataItem(
            "assigned-by",
            <UsersLucide size={16} />,
            "Assigned By",
            createdBy
          ),
          renderMetadataItem(
            "due",
            <Clock size={16} />,
            "Due",
            formatDueDate(assessment.dueDate || assessment.due_on)
          )
        );
        break;

      case "aborted": {
        const abortInfo = assessment.abort_info;
        metadataItems.push(
          renderMetadataItem(
            "aborted-on",
            <Calendar size={16} />,
            "Aborted on",
            formatDate(abortInfo?.abort_at, assessment.updated_at || undefined)
          ),
          renderMetadataItem(
            "aborted-by",
            <UsersLucide size={16} />,
            "Aborted by",
            abortInfo?.abort_by ||
              assessment.created_by_user?.name ||
              createdBy ||
              "N/A"
          ),
          renderMetadataItem(
            "abort-reason",
            <span />,
            "Abort Reason",
            abortInfo?.reason || "N/A"
          )
        );
        break;
      }

      case "completed":
        metadataItems.push(
          renderMetadataItem(
            "created",
            <Calendar size={16} />,
            "Created",
            getCreatedDate()
          ),
          renderMetadataItem(
            "assigned-by",
            <UsersLucide size={16} />,
            "Assigned By",
            createdBy
          ),
          renderMetadataItem(
            "due",
            <Clock size={16} />,
            "Due",
            formatDueDate(assessment.dueDate || assessment.due_on)
          )
        );
        break;

      case "published":
        metadataItems.push(
          renderMetadataItem(
            "created",
            <Calendar size={16} />,
            "Created",
            getCreatedDate()
          ),
          renderMetadataItem(
            "assigned-by",
            <UsersLucide size={16} />,
            "Assigned By",
            createdBy
          ),
          renderMetadataItem(
            "due",
            <Clock size={16} />,
            "Due",
            formatDueDate(assessment.dueDate || assessment.due_on)
          )
        );
        if (assessment.published_at) {
          metadataItems.push(
            renderMetadataItem(
              "published",
              <Clipboard size={16} />,
              "Published",
              formatDate(assessment.published_at)
            )
          );
        }
        {
          const levelUpdatedAt = getLatestLevelUpdateAt(
            assessment.notes as any
          );
          if (levelUpdatedAt) {
            metadataItems.push(
              renderMetadataItem(
                "updated",
                <Clock size={16} />,
                "Updated",
                formatDate(levelUpdatedAt)
              )
            );
          }
        }
        break;

      case "disconnects": {
        const disconnectDueDate = (
          assessment as AssessmentWithAccounts & { disconnectDueDate?: string }
        ).disconnectDueDate;
        metadataItems.push(
          renderMetadataItem(
            "created",
            <Calendar size={16} />,
            "Created",
            getCreatedDate()
          ),
          renderMetadataItem(
            "assigned-by",
            <UsersLucide size={16} />,
            "Assigned By",
            createdBy
          ),
          renderMetadataItem(
            "disconnect-due",
            <Clock size={16} />,
            "Disconnect due",
            formatDueDate(disconnectDueDate)
          )
        );
        if (
          (
            assessment as AssessmentWithAccounts & {
              hubPublishedStatus?: string;
            }
          ).hubPublishedStatus === "published" &&
          assessment.published_at
        ) {
          metadataItems.push(
            renderMetadataItem(
              "published",
              <Clipboard size={16} />,
              "Published",
              formatDate(assessment.published_at)
            )
          );
        }
        break;
      }
    }

    return metadataItems;
  };

  const renderActionButtons = () => {
    switch (statusTab) {
      case "pending":
        return (
          <>
            {onEdit && (
              <button
                onClick={() => onEdit(assessment)}
                className="box-border bg-white border border-gray-200 p-1.5 min-w-[2.25rem] w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-400"
                type="button"
                title="Edit Assessment"
              >
                <Pencil
                  size={18}
                  className="stroke-gray-900"
                  strokeWidth={2}
                  aria-hidden
                />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(assessment)}
                className="box-border bg-white border border-gray-200 p-1.5 min-w-[2.25rem] w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg transition-all duration-200 hover:bg-red-50 hover:border-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-400"
                type="button"
                title="Delete Assessment"
              >
                <Trash2
                  size={18}
                  className="stroke-gray-900"
                  strokeWidth={2}
                  aria-hidden
                />
              </button>
            )}
          </>
        );
      case "completed": {
        if (onPublishAssessment) {
          return (
            <Button
              variant="success"
              onClick={() => onPublishAssessment(assessment)}
              loading={isPublishing}
            >
              Publish Assessment
            </Button>
          );
        }

        return null;
      }
      case "disconnects":
        return null;
      case "ongoing":
        return (
          <>
            {onEdit && (
              <button
                onClick={() => onEdit(assessment)}
                className="box-border bg-white border border-gray-200 p-1.5 min-w-[2.25rem] w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gray-400"
                type="button"
                title="Edit Assessment"
              >
                <Pencil
                  size={18}
                  className="stroke-gray-900"
                  strokeWidth={2}
                  aria-hidden
                />
              </button>
            )}
            {onPublishAssessment && (
              <span
                title={forcePublishTitle}
                className={forcePublishDisabled ? "inline-flex" : undefined}
              >
                <Button
                  variant="success"
                  onClick={() => onPublishAssessment(assessment)}
                  loading={isPublishing}
                  disabled={forcePublishDisabled}
                  className={
                    forcePublishDisabled ? "pointer-events-none" : undefined
                  }
                  title={forcePublishTitle}
                >
                  {(() => {
                    const alignedCount =
                      selectedHubPublishAccountIds?.size ?? 0;
                    const disconnectCount = Array.from(
                      selectedDisconnectAccountKeys || []
                    ).filter(key =>
                      key.startsWith(`${assessment.id}::`)
                    ).length;
                    const selectedCount = alignedCount + disconnectCount;
                    return selectedCount > 0
                      ? `Force Publish (${selectedCount})`
                      : "Force Publish";
                  })()}
                </Button>
              </span>
            )}
          </>
        );
      case "aborted":
        return null;
      case "published":
        return null;
      default:
        return null;
    }
  };

  // Helper function to get badge Tailwind classes
  const getBadgeClasses = (type: string): string => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    const typeClasses: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      ongoing: "bg-blue-100 text-blue-800",
      published: "bg-green-100 text-green-800",
      draft: "bg-gray-100 text-gray-600",
      warning: "bg-yellow-100 text-yellow-800",
      danger: "bg-red-100 text-red-800",
      success: "bg-green-100 text-green-800",
      "completed-green": "bg-green-100 text-green-800",
      primary: "bg-purple-100 text-purple-800",
      info: "bg-blue-100 text-blue-800",
      "short-assessment": "bg-blue-100 text-blue-800",
      "long-assessment-purple": "bg-purple-100 text-purple-600",
    };
    return `${baseClasses} ${typeClasses[type] || typeClasses.info}`;
  };

  const renderTableData = () => {
    const accountsForTable =
      statusTab === "completed" || statusTab === "ongoing"
        ? getCompletedTabAccounts(assessment, effectiveCompletedAccountView)
        : assessment.accounts || [];

    if (!accountsForTable.length) return [];

    const renderLevelCell = (level: string | null | undefined) =>
      level ? (
        <span className="inline-flex items-center whitespace-nowrap px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
          {getLevelDisplayName(level)}
        </span>
      ) : (
        "-"
      );

    return accountsForTable.flatMap((account: AssessmentAccount) => {
      const accountExecutives: ExecutiveAssignment[] =
        account.executives ||
        account.users?.map(u =>
          mapApiUserToExecutive(u, account.disconnectStatus)
        ) ||
        [];

      // Assigned / Current / Unassigned (auto fold-in — no ESL merge UI).
      const originallyAssignedExecutives = accountExecutives.filter(
        isOriginallyAssignedExecutive
      );
      const currentCadenceExecutives = accountExecutives.filter(
        isCadenceCurrentExecutive
      );
      const unassignedExecutives = accountExecutives.filter(
        isUnassignedParticipantExecutive
      );
      // Counts / levels: all shown buckets (pending_merge legacy still excluded elsewhere).
      const assignedExecutives = [
        ...originallyAssignedExecutives,
        ...currentCadenceExecutives,
        ...unassignedExecutives.filter(e => !isPendingSelfFoldExecutive(e)),
      ];
      const assignedCountable = assignedExecutives.filter(
        exec => !isSkippedExecutive(exec)
      );
      const assignedResponded = assignedCountable.filter(
        exec => exec.is_responded
      ).length;
      const assignedTotal = assignedCountable.length;
      const assignedResponseRate =
        assignedTotal > 0
          ? Math.round((assignedResponded / assignedTotal) * 100)
          : 0;

      const cadenceGroups: Array<{
        key: string;
        title: string;
        match: (placement: CadencePlacement) => boolean;
      }> = [
        {
          key: "current-assigned",
          title: "Current · Assigned",
          match: placement =>
            placement.section === "current" &&
            placement.assignment === "assigned",
        },
        {
          key: "current-unassigned",
          title: "Current · Unassigned",
          match: placement =>
            !hideCurrentUnassigned &&
            placement.section === "current" &&
            placement.assignment === "unassigned",
        },
        {
          key: "stale-assigned",
          title: "Stale · Assigned",
          match: placement =>
            placement.section === "stale" &&
            placement.assignment === "assigned",
        },
        {
          key: "stale-unassigned",
          title: "Stale · Unassigned",
          match: placement =>
            placement.section === "stale" &&
            placement.assignment === "unassigned",
        },
      ];

      const renderExecRow = (exec: ExecutiveAssignment, index: number) => {
        const placement = classifyCadenceExecutive(exec);
        return (
          <div
            key={exec.id || exec.user_id || index}
            className="flex flex-col gap-0.5"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-900">{exec.name}</span>
              {exec.notes?.skipped ||
              exec.status?.toLowerCase() === "ignored" ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                  Ignored
                </span>
              ) : placement.tag ? (
                <CadenceTag tag={placement.tag} />
              ) : null}
            </div>
            <span className="capitalize text-gray-500 text-sm">
              ({exec.role})
            </span>
          </div>
        );
      };

      const placedExecutives = accountExecutives.map(exec => ({
        exec,
        placement: classifyCadenceExecutive(exec),
      }));

      const visibleCadenceGroups = cadenceGroups
        .filter(
          group => group.key !== "current-unassigned" || !hideCurrentUnassigned
        )
        .map(group => ({
          ...group,
          people: placedExecutives.filter(row => group.match(row.placement)),
        }));

      const assignedExecutivesDisplay = (
        <div className="flex flex-col gap-3">
          {visibleCadenceGroups.map(group => (
            <div key={group.key}>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {group.title}
              </span>
              <div className="mt-1 flex flex-col gap-2">
                {group.people.length > 0 ? (
                  group.people.map((row, index) =>
                    renderExecRow(row.exec, index)
                  )
                ) : (
                  <span className="text-sm text-gray-500">No executives</span>
                )}
              </div>
            </div>
          ))}
        </div>
      );

      const responsesDisplay =
        assignedResponded === 0 || assignedTotal === 0 ? (
          "-"
        ) : (
          <>
            {assignedResponded}/{assignedTotal}
            <div className="text-xs text-gray-500">
              ({assignedResponseRate}%)
            </div>
          </>
        );

      const hasAssignedCompletedExecutive =
        assignedResponded > 0 ||
        assignedExecutives.some(exec => {
          const executiveStatus = exec.status?.toLowerCase();
          return executiveStatus === "completed" || exec.is_responded === true;
        });

      const actionsCell =
        hasAssignedCompletedExecutive && onViewFullReport ? (
          <div className="flex w-[130px] flex-col items-stretch">
            <button
              type="button"
              onClick={() => onViewFullReport(assessment, account)}
              className={VIEW_REPORT_ACTION_BTN}
            >
              View full report
              <ArrowRight className="h-3 w-3 shrink-0" />
            </button>
          </div>
        ) : (
          <span className="text-gray-500">No response</span>
        );

      const disconnectStatusKey = (
        account.disconnectStatus || ""
      ).toLowerCase();
      const disconnectStillOpen =
        disconnectStatusKey === "pending_resolution" ||
        disconnectStatusKey === "resolved";
      const overrideBlockedOnPublished =
        disconnectStillOpen ||
        (Boolean(account.finalLevelPending) &&
          disconnectStatusKey !== "published" &&
          disconnectStatusKey !== "unresolved");
      const hasStoredOverride = Boolean(account.override?.override_level);
      const showOverrideAccountNameTag =
        statusTab === "published"
          ? !overrideBlockedOnPublished && hasStoredOverride
          : statusTab === "disconnects"
            ? hasStoredOverride || Boolean(account.overrideLevel)
            : false;

      const hubPublishModeLabel = formalPublishModeLabel(account.publishMode);
      const showHubPublishModeTag =
        Boolean(hubPublishModeLabel) &&
        ((statusTab === "published" && !isPublishedSelfOrigin(assessment)) ||
          (statusTab === "disconnects" && disconnectSubTab === "logs"));

      const publishedDisconnectNameTag =
        statusTab === "published"
          ? getPublishedFormalDisconnectNameTag(account)
          : null;
      const combinedAccountNameTag =
        statusTab === "published" ||
        statusTab === "disconnects" ||
        showCompletedDisconnectColumns
          ? getCombinedAccountNameTag(account)
          : null;
      const hasAccountNameTags =
        showHubPublishModeTag ||
        Boolean(combinedAccountNameTag) ||
        Boolean(publishedDisconnectNameTag) ||
        showOverrideAccountNameTag;

      const accountNameCell = (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="font-medium leading-snug">
            {account.name || account.account_name}
          </span>
          {hasAccountNameTags ? (
            <div className="flex flex-col items-start gap-1">
              {showHubPublishModeTag && (
                <span
                  className={PUBLISH_MODE_TAG_CLASS}
                  title="How this account was published to the hub"
                >
                  {hubPublishModeLabel}
                </span>
              )}
              {combinedAccountNameTag && (
                <span
                  className={combinedAccountNameTag.className}
                  title={combinedAccountNameTag.title}
                >
                  {combinedAccountNameTag.label}
                </span>
              )}
              {publishedDisconnectNameTag && (
                <span
                  className={publishedDisconnectNameTag.className}
                  title={publishedDisconnectNameTag.title}
                >
                  {publishedDisconnectNameTag.label}
                </span>
              )}
              {showOverrideAccountNameTag && (
                <span
                  className={OVERRIDE_ACCOUNT_TAG_CLASS}
                  title={
                    account.override?.overridden_by?.trim() ||
                    "Acceptance level was overridden"
                  }
                >
                  Override
                </span>
              )}
            </div>
          ) : null}
        </div>
      );

      const rowData: any = {
        name: accountNameCell,
        executives: assignedExecutivesDisplay,
        responses: responsesDisplay,
        actions: actionsCell,
      };

      const accountId = String(account.account_id || account.id || "");
      if (hubPublishSelectionEnabled && accountId) {
        const canSelectHubPublish = isAlignedPublishableAccount(account);
        const isSelected =
          canSelectHubPublish &&
          (selectedHubPublishAccountIds?.has(accountId) ?? false);

        rowData.hubSelect = (
          <input
            type="checkbox"
            disabled={!canSelectHubPublish}
            checked={isSelected}
            onChange={() =>
              onToggleHubPublishAccount?.(assessment.id, accountId)
            }
            className="h-4 w-4 cursor-pointer rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-40"
            aria-label={`Select ${account.name || account.account_name} for publish`}
          />
        );
      }

      if (disconnectCheckboxEnabled && accountId) {
        const selectionKey = getDisconnectSelectionKey(
          assessment.id,
          accountId
        );
        const isSelectable = isDisconnectTriggerSelectableAccount(
          account,
          completedDisconnectSelectionEnabled
        );

        rowData.select = isSelectable ? (
          <input
            type="checkbox"
            checked={selectedDisconnectAccountKeys?.has(selectionKey) ?? false}
            onChange={() =>
              onToggleDisconnectAccount?.(assessment.id, accountId)
            }
            className="h-4 w-4 cursor-pointer rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            aria-label={`Select ${account.name || account.account_name}`}
          />
        ) : null;
      }

      // Official levels exclude pending fold-in (API / publish math).
      const minLevel = getAccountLevel(account, "min");
      const medianLevel = getAccountLevel(account, "median");
      const maxLevel = getAccountLevel(account, "max");
      const finalLevel = getAccountLevel(account, "final") || medianLevel;
      const acceptanceLevel =
        getAccountLevel(account, "final") ||
        getAccountLevel(account, "median") ||
        getAccountLevel(account, "max") ||
        getAccountLevel(account, "min");

      rowData.minLevel = renderLevelCell(minLevel);
      rowData.medianLevel = renderLevelCell(medianLevel);
      rowData.maxLevel = renderLevelCell(maxLevel);
      if (statusTab === "disconnects" || showCompletedDisconnectColumns) {
        const tier = account.disconnectTier;
        const delta = account.disconnectDelta;
        const actionStatus = account.disconnectStatus ?? "none";

        rowData.disconnect =
          tier && delta != null ? (
            <span
              className={`inline-flex items-center whitespace-nowrap px-2 py-1 rounded text-xs font-medium ${
                tier === "strong"
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : "bg-orange-50 text-orange-700 border border-orange-200"
              }`}
            >
              {tier === "strong" ? `Strong (Δ${delta})` : `Soft (Δ${delta})`}
            </span>
          ) : (
            "-"
          );

        const statusKey = normalizeDisconnectStatus(actionStatus);
        const isResolutionRecorded =
          statusKey === "resolved" ||
          (statusKey === "published" && !account.hubPublished);
        // Force publish is accepted only while the resolution is still "resolved".
        const canForcePublish =
          disconnectAcceptEnabled &&
          statusKey === "resolved" &&
          Boolean(onOpenResolutionReview);

        rowData.actions = (
          <div className="flex w-[130px] flex-col items-stretch gap-2">
            {hasAssignedCompletedExecutive && onViewFullReport ? (
              <button
                type="button"
                className={VIEW_REPORT_ACTION_BTN}
                onClick={() => onViewFullReport(assessment, account)}
              >
                View full report
                <ArrowRight className="h-3 w-3 shrink-0" />
              </button>
            ) : (
              <span className="text-gray-500">No response</span>
            )}
            {canForcePublish && (
              <button
                type="button"
                className={FORCE_PUBLISH_ACTION_BTN}
                onClick={() => onOpenResolutionReview?.(assessment, account)}
                title="Publish the Primary Sponsor's resolution now instead of waiting for the disconnect due date"
              >
                Force Publish
                <ArrowRight className="h-3 w-3 shrink-0" />
              </button>
            )}
          </div>
        );

        const renderStatusTag = (
          label: string,
          className: string,
          description: string | null
        ) => {
          const tag = (
            <span tabIndex={0} className={`${className} cursor-pointer`}>
              {label}
            </span>
          );
          return description ? (
            <HoverHint description={description}>{tag}</HoverHint>
          ) : (
            tag
          );
        };

        if (
          statusKey === "none" ||
          statusKey === "flagged" ||
          statusKey === "unknown"
        ) {
          rowData.disconnectAction = "-";
        } else {
          const workflowTag = getHubDisconnectWorkflowAccountTag(
            isResolutionRecorded ? "resolved" : actionStatus,
            account.hubPublished
          );
          if (!workflowTag) {
            rowData.disconnectAction = "-";
          } else {
            rowData.disconnectAction = renderStatusTag(
              workflowTag.label,
              getDisconnectActionPillBadgeClass(workflowTag.statusKey),
              workflowTag.description
            );
          }
        }
      }

      const showDisconnectOverride =
        statusTab === "disconnects" &&
        (disconnectStatusKey === "unresolved" ||
          (disconnectStatusKey === "published" && !account.hubPublished));
      const showOverrideAndPublishControl =
        Boolean(onOpenOverrideModal) && showDisconnectOverride;
      const publishedOverrideAccountId =
        account.id ?? account.account_id ?? null;
      const showPublishedOverrideButton =
        statusTab === "published" &&
        Boolean(onOpenOverrideModal) &&
        isPublishedOverrideEligibleRow(
          publishedOverrideEligibleKeys,
          hubAssessmentId,
          publishedOverrideAccountId
        );

      if (statusTab === "published" && onOpenOverrideModal) {
        rowData.actions = (
          <div className="flex w-[130px] flex-col items-stretch gap-2">
            {actionsCell}
            {showPublishedOverrideButton ? (
              <button
                type="button"
                disabled={overrideBlockedOnPublished}
                onClick={() => onOpenOverrideModal(assessment, account)}
                className={OVERRIDE_ACTION_BTN}
                title={
                  overrideBlockedOnPublished
                    ? "Override is unavailable while a disconnect resolution is running for this account."
                    : undefined
                }
              >
                Click to Override
                <ArrowRight className="h-3 w-3 shrink-0" />
              </button>
            ) : null}
          </div>
        );
      }
      const overrideAndPublishControl = showOverrideAndPublishControl ? (
        <button
          type="button"
          onClick={() => onOpenOverrideModal?.(assessment, account)}
          className="bg-transparent border-none cursor-pointer p-0 m-0"
          title="Override the final level and publish this account to the hub"
        >
          <span className="inline-flex items-center whitespace-nowrap px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-600 border border-purple-600 hover:bg-purple-100 transition-colors">
            Click to Override
          </span>
        </button>
      ) : null;

      const isProvisionalLevel =
        Boolean(finalLevel) &&
        (statusTab === "ongoing" || statusTab === "completed") &&
        !account.hubPublished &&
        !account.published_at &&
        !account.overrideLevel &&
        !hasStoredOverride;

      rowData.finalLevel = (
        <div className="flex flex-col items-start gap-1.5">
          {finalLevel && isProvisionalLevel ? (
            <ProvisionalLevelTooltip>
              <span
                tabIndex={0}
                className="inline-flex cursor-pointer items-center whitespace-nowrap px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
              >
                {getLevelDisplayName(finalLevel).replace(
                  /^Account Level/,
                  "Provisional Level"
                )}
              </span>
            </ProvisionalLevelTooltip>
          ) : finalLevel ? (
            <span className="inline-flex items-center whitespace-nowrap px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              {getLevelDisplayName(finalLevel)}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
          {statusTab !== "disconnects" &&
            statusTab !== "published" &&
            (account.overrideLevel || hasStoredOverride) && (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-600 border border-purple-600">
                (Override)
              </span>
            )}
          {overrideAndPublishControl}
        </div>
      );

      if (statusTab === "published" || statusTab === "disconnects") {
        const isDisconnectsTab = statusTab === "disconnects";
        const showOverrideTag = isDisconnectsTab
          ? hasStoredOverride || Boolean(account.overrideLevel)
          : !overrideBlockedOnPublished && hasStoredOverride;
        const primaryResolution = resolveAccountDisconnectResolution(account);
        const publishedAcceptanceLevel =
          (primaryResolution?.agreedLevel != null
            ? `L${primaryResolution.agreedLevel}`
            : null) ||
          (account.disconnectDetected
            ? account.override?.override_level || account.eslFinalLevel
            : null) ||
          account.published_level ||
          account.override?.override_level ||
          account.eslFinalLevel ||
          acceptanceLevel;
        rowData.acceptanceLevel = publishedAcceptanceLevel ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center whitespace-nowrap px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              {getLevelDisplayName(
                showOverrideTag
                  ? account.override?.override_level || publishedAcceptanceLevel
                  : publishedAcceptanceLevel
              )}
            </span>
            {isDisconnectsTab ? overrideAndPublishControl : null}
          </div>
        ) : isDisconnectsTab && overrideAndPublishControl ? (
          overrideAndPublishControl
        ) : (
          "-"
        );
      }

      const rows: any[] = [rowData];
      return rows;
    });
  };

  const tableColumns =
    statusTab === "published"
      ? [
          { key: "name", title: "ACCOUNT NAME" },
          { key: "executives", title: "TEAM MEMBERS" },
          { key: "responses", title: "RESPONSES" },
          {
            key: "acceptanceLevel",
            title: "ACCEPTANCE LEVEL",
          },
          { key: "actions", title: "ACTION" },
        ]
      : statusTab === "disconnects"
        ? [
            ...(disconnectSelectionEnabled
              ? [
                  {
                    key: "select",
                    title: (() => {
                      const selectableAccountIds = (assessment.accounts ?? [])
                        .filter(account =>
                          isDisconnectTriggerSelectableAccount(account, false)
                        )
                        .map(account =>
                          String(account.account_id || account.id)
                        )
                        .filter(Boolean);
                      const allSelected =
                        selectableAccountIds.length > 0 &&
                        selectableAccountIds.every(id =>
                          selectedDisconnectAccountKeys?.has(
                            getDisconnectSelectionKey(assessment.id, id)
                          )
                        );

                      return (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() =>
                            onToggleAllDisconnectAccounts?.(
                              assessment.id,
                              selectableAccountIds,
                              !allSelected
                            )
                          }
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          aria-label="Select all accounts"
                        />
                      );
                    })(),
                    width: "48px",
                  },
                ]
              : []),
            { key: "name", title: "ACCOUNT NAME" },
            { key: "executives", title: "TEAM MEMBERS" },
            { key: "responses", title: "RESPONSES" },
            { key: "acceptanceLevel", title: "ACCEPTANCE LEVEL" },
            { key: "disconnect", title: "TYPE" },
            { key: "disconnectAction", title: "STATUS" },
            { key: "actions", title: "ACTION" },
          ]
        : showCompletedDisconnectColumns
          ? [
              ...(completedDisconnectSelectionEnabled
                ? [
                    {
                      key: "select",
                      title: (() => {
                        const selectableAccountIds = (assessment.accounts ?? [])
                          .filter(account =>
                            isDisconnectTriggerSelectableAccount(account, true)
                          )
                          .map(account => getAssessmentAccountId(account))
                          .filter(Boolean);
                        const allSelected =
                          selectableAccountIds.length > 0 &&
                          selectableAccountIds.every(id =>
                            selectedDisconnectAccountKeys?.has(
                              getDisconnectSelectionKey(assessment.id, id)
                            )
                          );

                        return (
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() =>
                              onToggleAllDisconnectAccounts?.(
                                assessment.id,
                                selectableAccountIds,
                                !allSelected
                              )
                            }
                            className="h-4 w-4 cursor-pointer rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            aria-label="Select all disconnect accounts"
                          />
                        );
                      })(),
                      width: "48px",
                    },
                  ]
                : []),
              { key: "name", title: "ACCOUNT NAME" },
              { key: "executives", title: "TEAM MEMBERS" },
              { key: "responses", title: "RESPONSES" },
              { key: "minLevel", title: "MIN LEVEL" },
              { key: "medianLevel", title: "MEDIAN LEVEL" },
              { key: "maxLevel", title: "MAX LEVEL" },
              {
                key: "finalLevel",
                title: "FINAL LEVEL",
                width: "160px",
              },
              { key: "disconnect", title: "TYPE" },
              { key: "disconnectAction", title: "STATUS" },
              { key: "actions", title: "ACTION" },
            ]
          : hubPublishSelectionEnabled
            ? [
                {
                  key: "hubSelect",
                  title: (() => {
                    const selectableAccountIds = (assessment.accounts ?? [])
                      .filter(isAlignedPublishableAccount)
                      .map(account => getAssessmentAccountId(account))
                      .filter(Boolean);
                    const allSelected =
                      selectableAccountIds.length > 0 &&
                      selectableAccountIds.every(id =>
                        selectedHubPublishAccountIds?.has(id)
                      );

                    return (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() =>
                          onToggleAllHubPublishAccounts?.(
                            assessment.id,
                            selectableAccountIds,
                            !allSelected
                          )
                        }
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-green-600 focus:ring-green-500"
                        aria-label="Select all aligned accounts"
                      />
                    );
                  })(),
                  width: "48px",
                },
                { key: "name", title: "ACCOUNT NAME" },
                { key: "executives", title: "TEAM MEMBERS" },
                { key: "responses", title: "RESPONSES" },
                { key: "minLevel", title: "MIN LEVEL" },
                { key: "medianLevel", title: "MEDIAN LEVEL" },
                { key: "maxLevel", title: "MAX LEVEL" },
                {
                  key: "finalLevel",
                  title: "FINAL LEVEL",
                  width: "160px",
                },
                { key: "actions", title: "ACTION" },
              ]
            : [
                { key: "name", title: "ACCOUNT NAME" },
                { key: "executives", title: "TEAM MEMBERS" },
                { key: "responses", title: "RESPONSES" },
                { key: "minLevel", title: "MIN LEVEL" },
                { key: "medianLevel", title: "MEDIAN LEVEL" },
                { key: "maxLevel", title: "MAX LEVEL" },
                {
                  key: "finalLevel",
                  title: "FINAL LEVEL",
                  width: "160px",
                },
                { key: "actions", title: "ACTION" },
              ];

  const participantsCount =
    typeof assessment.participants === "number" && assessment.participants >= 0
      ? assessment.participants
      : (assessment.accounts || []).reduce((sum, account) => {
          const { totalAssigned } = getAccountResponseCounts(account);
          return sum + totalAssigned;
        }, 0);

  return (
    <div
      key={assessment.id}
      className="bg-white border border-gray-200 rounded-lg overflow-hidden"
    >
      <div className="p-6 border-gray-200">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={onToggleExpand}
              className="bg-transparent border-none cursor-pointer text-gray-500 transition-colors duration-200 hover:text-gray-900"
            >
              {isExpanded ? (
                <ChevronDown size={20} />
              ) : (
                <ChevronRight size={20} />
              )}
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  {normalizeTeamAssessmentDisplayName(
                    assessment.name,
                    "Untitled Assessment",
                    assessment.target_accounts?.[0]?.account_name ||
                      assessment.accounts?.[0]?.account_name ||
                      assessment.accounts?.[0]?.name
                  )}
                </h3>
                {/* Overdue badge for ongoing assessments */}
                {statusTab === "ongoing" &&
                  isOverdue(assessment.dueDate || assessment.due_on) && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs  bg-red-100 text-red-700 border border-red-200">
                      Overdue
                    </span>
                  )}
                {/* Extended count badge */}
                {statusTab === "ongoing" &&
                  assessment.due_date_info?.count &&
                  assessment.due_date_info.count > 0 && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs  bg-orange-100 text-orange-700 border border-orange-200">
                      {`Extended: ${assessment.due_date_info.count} time(s)`}
                    </span>
                  )}
                {(statusTab === "ongoing" || statusTab === "completed") &&
                  (() => {
                    const workflowStatus =
                      getAssessmentDisconnectWorkflowStatus(assessment);
                    if (workflowStatus === "resolved") {
                      return (
                        <DisconnectWorkflowStatusBadge status="resolved" />
                      );
                    }
                    if (workflowStatus === "published") {
                      const readyCount =
                        countReadyToPublishDisconnectAccounts(assessment);
                      if (readyCount === 0) return null;
                      return (
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                          title="Disconnect resolution accepted — use Publish Assessment to publish to the hub"
                        >
                          {DISCONNECT_RESOLVED_ASSESSMENT_LABEL}
                          {readyCount > 1 ? ` (${readyCount})` : ""}
                        </span>
                      );
                    }
                    const disconnectBadge =
                      getAssessmentDisconnectBadge(assessment);
                    if (!disconnectBadge) return null;
                    const isStrong = disconnectBadge.tier === "strong";
                    const tierLabel = isStrong ? "Strong" : "Soft";
                    const accountLabel =
                      disconnectBadge.count > 1
                        ? `${disconnectBadge.count} accounts`
                        : "1 account";
                    return (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isStrong
                            ? "bg-red-50 text-red-700 border border-red-200"
                            : "bg-orange-50 text-orange-700 border border-orange-200"
                        }`}
                        title={`${tierLabel} disconnect detected (Δ${disconnectBadge.delta}) on ${accountLabel} — not yet published`}
                      >
                        {getDisconnectStatusLabel("none")}
                        {disconnectBadge.count > 1
                          ? ` (${disconnectBadge.count})`
                          : ""}
                      </span>
                    );
                  })()}
                {statusTab === "ongoing" &&
                  (() => {
                    const workflowStatus =
                      getAssessmentDisconnectWorkflowStatus(assessment);
                    // "published" → Ready to publish is Completed-tab only;
                    // Ongoing already shows Disconnect Resolved above.
                    if (
                      !workflowStatus ||
                      workflowStatus === "resolved" ||
                      workflowStatus === "published"
                    ) {
                      return null;
                    }
                    return (
                      <DisconnectWorkflowStatusBadge status={workflowStatus} />
                    );
                  })()}
              </div>
              {assessment.description && (
                <TruncatedTooltip
                  id={`id-${assessment.id}`}
                  tooltip={normalizeTeamAssessmentDisplayName(
                    assessment.description,
                    assessment.description
                  )}
                  className="text-sm text-gray-500 mb-0"
                >
                  {normalizeTeamAssessmentDisplayName(
                    assessment.description,
                    assessment.description
                  )}
                </TruncatedTooltip>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {renderActionButtons()}
            {statusTab === "published" && (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {(() => {
                  const disconnectCount = (assessment.accounts || []).filter(
                    accountHasDisconnectHistory
                  ).length;
                  if (disconnectCount === 0) return null;
                  return (
                    <span
                      className={PUBLISHED_DISCONNECT_TAG_CLASS}
                      title={`${disconnectCount} account${
                        disconnectCount === 1 ? "" : "s"
                      } went through a disconnect workflow`}
                    >
                      {disconnectCount === 1
                        ? "1 disconnect"
                        : `${disconnectCount} disconnects`}
                    </span>
                  );
                })()}
                <span className={getBadgeClasses("published")}>Published</span>
              </div>
            )}
            {statusTab === "disconnects" &&
              (
                assessment as AssessmentWithAccounts & {
                  hubPublishedStatus?: string;
                }
              ).hubPublishedStatus === "published" && (
                <span className={getBadgeClasses("published")}>Published</span>
              )}
            {statusTab === "disconnects" &&
              disconnectAssessmentKind !== "team" &&
              countReadyToPublishDisconnectAccounts(assessment) > 0 && (
                <DisconnectWorkflowStatusBadge status="published" />
              )}
            {/* Action buttons for ongoing assessments */}
            {statusTab === "ongoing" && (
              <>
                {onExtendDueDate && (
                  <button
                    onClick={() => onExtendDueDate(assessment)}
                    className="bg-orange-500 hover:bg-orange-600 text-white border-none w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg shadow-md transition-all duration-200"
                    type="button"
                    title="Extend Due Date"
                  >
                    <Calendar size={18} className="stroke-white" />
                  </button>
                )}
                {onAbortAssessment && (
                  <button
                    onClick={() => onAbortAssessment(assessment)}
                    className="bg-red-500 hover:bg-red-600 text-white border-none w-10 h-10 flex items-center justify-center cursor-pointer rounded-lg shadow-md transition-all duration-200"
                    type="button"
                    title="Abort Assessment"
                  >
                    <Ban size={18} className="stroke-white" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 mt-4 text-sm text-gray-600 flex-wrap">
          <div className="flex items-center gap-x-6 gap-y-1 flex-wrap">
            {renderMetadata()}
            <div className="flex items-center gap-1">
              <UsersLucide size={16} />
              <span>Participants: {participantsCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <UsersLucide size={16} />
              <span>Target Accounts: {assessment.accounts?.length || 0}</span>
            </div>
            {statusTab === "ongoing" &&
              assessment.due_date_info?.count &&
              assessment.due_date_info.count > 0 &&
              assessment.due_date_info?.reason &&
              onOpenExtendedReason && (
                <div
                  key={`extended-reason-${assessment.id}`}
                  className="flex items-center gap-1"
                >
                  <span>Extended Reason:</span>
                  <button
                    type="button"
                    onClick={() => onOpenExtendedReason(assessment)}
                    className="bg-transparent border-none p-0 text-purple-600 hover:text-purple-700 hover:underline text-sm cursor-pointer"
                  >
                    Reason
                  </button>
                </div>
              )}
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {statusTab !== "pending" && onViewStatistics && (
              <button
                type="button"
                onClick={() => onViewStatistics(assessment)}
                className="bg-transparent border-none p-0 text-purple-600 hover:text-purple-700 hover:underline text-sm cursor-pointer"
              >
                View Statistics
              </button>
            )}
            {(statusTab === "published" || statusTab === "disconnects") &&
              onViewReport &&
              (statusTab === "published" ||
                (assessment as any).hubPublishedStatus === "published") && (
                <button
                  type="button"
                  onClick={() => onViewReport(assessment)}
                  className="text-indigo-600 hover:text-indigo-700 hover:underline text-sm cursor-pointer"
                >
                  View Report
                </button>
              )}
            <span
              className={getBadgeClasses(
                isShortCategory(assessment.category)
                  ? "short-assessment"
                  : "long-assessment-purple"
              )}
            >
              {isShortCategory(assessment.category) ? "Short" : "Long"}
            </span>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 border-t">
          {showCompletedAccountSubTabs && onCompletedAccountViewChange && (
            <div
              className="mb-4 inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1"
              role="tablist"
              aria-label={`Account filters for ${assessment.name}`}
            >
              {(
                [
                  { id: "aligned" as const, label: "Aligned" },
                  { id: "disconnects" as const, label: "Disconnects" },
                ] as const
              ).map(tab => {
                const isActive = effectiveCompletedAccountView === tab.id;
                const isDisabled =
                  tab.id === "aligned"
                    ? alignedAccountCount === 0
                    : disconnectAccountCount === 0;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      onCompletedAccountViewChange(tab.id);
                    }}
                    className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold sm:text-sm ${
                      isDisabled
                        ? "cursor-not-allowed text-gray-300"
                        : isActive
                          ? "bg-white text-violet-700 shadow-sm"
                          : "text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
          {(() => {
            const tableData = renderTableData();
            return tableData.length > 0 ? (
              <Table columns={tableColumns} data={tableData} />
            ) : assessment.accounts && assessment.accounts.length > 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 py-8 px-4">
                <p className="text-sm text-gray-600">
                  {effectiveCompletedAccountView === "disconnects"
                    ? "No disconnect accounts left to publish."
                    : "No aligned accounts left to publish."}
                </p>
                {effectiveCompletedAccountView === "aligned" &&
                  onCompletedAccountViewChange &&
                  assessmentHasDisconnectAccounts(assessment) && (
                    <button
                      type="button"
                      onClick={() =>
                        onCompletedAccountViewChange("disconnects")
                      }
                      className="mt-2 bg-transparent border-0 p-0 text-sm font-medium text-violet-600 hover:underline cursor-pointer"
                    >
                      View disconnect accounts
                    </button>
                  )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-5 px-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                  <UsersLucide className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-base font-medium text-gray-600 mb-1">
                  No accounts found in this assessment
                </p>
                <p className="text-sm text-gray-500 mb-2">
                  You can add accounts by editing this assessment.
                </p>
                {onEdit && (
                  <Button
                    variant="primary"
                    onClick={() => onEdit(assessment)}
                    icon={<Pencil size={16} />}
                  >
                    Edit Assessment to Add Accounts
                  </Button>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

const toTeamAssessmentCard = (
  item: TeamAssessmentHubItem
): AssessmentWithAccounts => ({
  id: item.assessment_id,
  name: item.name || item.title || "Untitled Assessment",
  description: item.title && item.title !== item.name ? item.title : undefined,
  template_id: "",
  template_name: "",
  status: "active",
  progress: item.progress ?? 0,
  participants: item.participants ?? 0,
  total_responses: item.total_responses ?? 0,
  total_questions: 0,
  due_on: item.due_date || item.due_on || "",
  dueDate: item.due_date || item.due_on || null,
  created_at: item.created_at || "",
  updated_at: item.updated_at || "",
  created_by: { id: "", name: "System", email: "" },
  createdBy: "System",
  category: item.category || "short",
  assessment_type: "self_assessment",
  cronPublishFailed: Boolean(item.cron_publish_failed),
  accounts: (item.target_accounts || []).map(acc => {
    const users = (acc.users || []).map(user => ({
      user_id: user.user_id,
      assignment_id: user.assignment_id,
      name: user.name,
      email: user.email,
      role: user.role || user.relationship_role || "",
      is_responded: user.is_responded,
      submitted_at: user.submitted_at,
      responses: user.responses?.maturity_level
        ? {
            rationale: user.responses.rationale || undefined,
            maturity_level: user.responses.maturity_level,
          }
        : user.maturity_level
          ? { maturity_level: user.maturity_level }
          : undefined,
    }));
    const responded = users.filter(user => user.is_responded).length;
    return {
      id: acc.account_id,
      account_id: acc.account_id,
      account_name: acc.account_name,
      name: acc.account_name,
      totalAssigned: users.length,
      requests_sent: users.length,
      responses: responded,
      responses_received: responded,
      hubPublished: Boolean(acc.hubPublished),
      min_level: acc.min_level || acc.minLevel || null,
      median_level: acc.median_level || acc.medianLevel || null,
      max_level: acc.max_level || acc.maxLevel || null,
      final_level: acc.final_level || acc.eslFinalLevel || null,
      users,
    };
  }),
});

async function loadFullReportData(
  dispatch: any,
  assessment: AssessmentWithAccounts,
  account: AssessmentAccount
): Promise<FullReportData> {
  const getAccountLevel = (
    levelAccount: AssessmentAccount,
    levelType: "min" | "median" | "max" | "final"
  ): string | null => {
    const levelMap: Record<string, string | null | undefined> = {
      min: levelAccount.minLevel || levelAccount.min_level,
      median: levelAccount.medianLevel || levelAccount.median_level,
      max: levelAccount.maxLevel || levelAccount.max_level,
      final: levelAccount.finalLevel || levelAccount.final_level,
    };
    return levelMap[levelType] || null;
  };

  // Normalize users|executives so .every/.filter are not on a union of arrays.
  const rawUsers: Array<ApiAssessmentUser | ExecutiveAssignment> = account.users
    ?.length
    ? [...account.users]
    : [...(account.executives || [])];
  // Assigned report excludes pending fold-in. Unassigned-only report (scoped
  // row) keeps those executives so ESL can still open Surendhar's Self.
  const onlyPendingFoldIn =
    rawUsers.length > 0 &&
    rawUsers.every(user => isPendingSelfFoldExecutive(user));
  const accountUsers = onlyPendingFoldIn
    ? rawUsers
    : rawUsers.filter(user => !isPendingSelfFoldExecutive(user));
  const medianLevel =
    getAccountLevel(account, "median") || getAccountLevelVerboseLabel(2);

  const dynamicResponses = accountUsers.map((user: any, index: number) => {
    const isResponded = Boolean(
      user.is_responded ?? user.status?.toLowerCase() === "completed"
    );
    const ignored =
      !isResponded &&
      (Boolean(user.notes?.skipped) ||
        user.status?.toLowerCase() === "ignored" ||
        isDisconnectHubDisplayIgnored(
          user.is_responded,
          user.notes,
          account.disconnectStatus
        ));
    const sponsorMatch = account.disconnectReviewRow?.sponsorAssessments?.find(
      s =>
        s.executiveId === (user.user_id || user.id) ||
        s.assignmentId === user.assignment_id
    );
    const calculatedLevel = isResponded
      ? user.responses?.maturity_level ||
        sponsorMatch?.levelLabel ||
        (sponsorMatch?.level != null ? `L${sponsorMatch.level}` : undefined) ||
        getAccountLevel(account, "final") ||
        medianLevel ||
        undefined
      : undefined;

    return {
      id: user.user_id || user.id || `response-${index + 1}`,
      executiveName: user.name || "Unknown Executive",
      executiveEmail: user.email || "",
      submittedAt:
        user.submitted_at ||
        user.submittedAt ||
        sponsorMatch?.submittedAt ||
        undefined,
      calculatedLevel,
      ignored,
      responses: isResponded
        ? {
            maturity_level: calculatedLevel,
            rationale: user.responses?.rationale || "",
          }
        : undefined,
    };
  });

  let disconnectResolution = resolveAccountDisconnectResolution(account);
  const disconnectStatus = (account.disconnectStatus ?? "").toLowerCase();
  const shouldFetchResolution =
    !disconnectResolution &&
    (Boolean(account.resolutionId) ||
      disconnectStatus === "resolved" ||
      disconnectStatus === "published");
  if (shouldFetchResolution) {
    try {
      const resolutions = await dispatch(
        fetchDisconnectResolutions({
          assessmentId: assessment.id,
          accountId: getAssessmentAccountId(account),
        })
      ).unwrap();
      const detail =
        resolutions.resolutions?.find(
          (item: { id?: string }) => item.id === account.resolutionId
        ) ?? resolutions.resolutions?.[0];
      if (detail) {
        disconnectResolution = mapApiDisconnectResolution({
          id: detail.id,
          status: detail.status,
          agreedLevel: detail.agreed_level ?? null,
          agreedLevelLabel:
            detail.agreed_level != null
              ? getAccountLevelVerboseLabel(detail.agreed_level)
              : null,
          rationale: detail.resolution_justification ?? null,
          resolvedBy: detail.resolvedByName ?? null,
          resolvedById: detail.resolved_by ?? null,
          resolvedAt: detail.resolved_at ?? null,
        });
      }
    } catch {
      // Best-effort: open report without primary resolution details.
    }
  }

  return {
    assessment,
    account: {
      ...account,
      disconnectResolution,
      published_level:
        account.published_level ||
        (disconnectResolution?.agreedLevel != null
          ? `L${disconnectResolution.agreedLevel}`
          : null),
    },
    responses: dynamicResponses,
  };
}

function FullReportModalFrame({
  isOpen,
  fullReportData,
  organizationLevels,
  onClose,
  onExport,
  showNotification,
  setIndividualResponseModal,
}: {
  isOpen: boolean;
  fullReportData: FullReportData | null;
  organizationLevels: Array<{
    level_code?: string;
    levels?: string;
    name: string;
    description: string;
  }>;
  onClose: () => void;
  onExport: (data: FullReportData | null) => void;
  showNotification: (
    message: string,
    details: string,
    type?: "success" | "error"
  ) => void;
  setIndividualResponseModal: React.Dispatch<React.SetStateAction<any>>;
}) {
  const dispatch = useAppDispatch();
  const loadingFullReport = false;
  const getAccountLevel = (
    account: AssessmentAccount,
    levelType: "min" | "median" | "max" | "final"
  ): string | null => {
    const levelMap: Record<string, string | null | undefined> = {
      min: account.minLevel || account.min_level,
      median: account.medianLevel || account.median_level,
      max: account.maxLevel || account.max_level,
      final: account.finalLevel || account.final_level,
    };
    return levelMap[levelType] || null;
  };
  const shouldShowLevelMetrics = (
    category: string | null | undefined
  ): boolean => {
    if (!category) return false;
    const normalized = category.toLowerCase();
    return normalized === "short" || normalized === "small";
  };
  const getLevelDisplayName = (level: string | null | undefined): string => {
    if (!level) return "Pending";
    const levelStr = level.toString().trim();
    if (!/\d/.test(levelStr)) return levelStr;
    const code = toStoredLevelCode(levelStr);
    const levelData = organizationLevels.find(
      levelRow => levelRow.level_code?.toUpperCase() === code
    );
    return levelData
      ? getAccountLevelVerboseLabel(code, levelData.name)
      : toVerboseLevelLabel(levelStr);
  };
  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      title={`Full Assessment Report: ${fullReportData?.account.name || ""}`}
      size="5xl"
      overlayClassName="bg-black/30 z-[1040]"
      containerClassName="z-[1050]"
    >
      {fullReportData && (
        <div className="space-y-5">
          <XFactorLevelGuide
            showHeader={true}
            compact={false}
            className="mb-2"
            organizationLevels={organizationLevels || []}
          />
          {/* Report Header */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500 block mb-1">
                  Assessment
                </label>
                <p className="mb-0 text-sm font-medium text-gray-900">
                  {fullReportData.assessment.name}
                </p>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500 block mb-1">
                  Account
                </label>
                <div className="flex flex-col items-start gap-1">
                  <p className="mb-0 text-sm font-medium text-gray-900">
                    {fullReportData.account.name}
                  </p>
                  {accountHasDisconnectHistory(fullReportData.account) && (
                    <span
                      className={PUBLISHED_DISCONNECT_TAG_CLASS}
                      title="This account went through a disconnect workflow"
                    >
                      Disconnect
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500 block mb-1">
                  Total Responses
                </label>
                <p className="mb-0 text-sm font-medium text-gray-900">
                  {fullReportData.account.responses ||
                    fullReportData.account.responses_received ||
                    0}
                  /
                  {fullReportData.account.totalAssigned ||
                    fullReportData.account.requests_sent ||
                    0}
                </p>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide text-gray-500 block mb-1">
                  Final Account Level
                </label>
                <div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {getLevelDisplayName(
                      resolveFullReportFinalLevel(fullReportData.account)
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Level Distribution — all hub tabs when level data exists */}
          {(getAccountLevel(fullReportData.account, "min") ||
            getAccountLevel(fullReportData.account, "median") ||
            getAccountLevel(fullReportData.account, "max") ||
            resolveFullReportFinalLevel(fullReportData.account)) && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <h5 className="text-sm font-medium text-gray-800 mb-3">
                Level Distribution
              </h5>
              <div className="flex flex-wrap items-center gap-4">
                {getAccountLevel(fullReportData.account, "min") && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Min:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                      {getLevelDisplayName(
                        getAccountLevel(fullReportData.account, "min")
                      )}
                    </span>
                  </div>
                )}
                {getAccountLevel(fullReportData.account, "median") && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Median:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                      {getLevelDisplayName(
                        getAccountLevel(fullReportData.account, "median")
                      )}
                    </span>
                  </div>
                )}
                {getAccountLevel(fullReportData.account, "max") && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Max:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      {getLevelDisplayName(
                        getAccountLevel(fullReportData.account, "max")
                      )}
                    </span>
                  </div>
                )}
                {resolveFullReportFinalLevel(fullReportData.account) && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Final:</span>
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      {getLevelDisplayName(
                        resolveFullReportFinalLevel(fullReportData.account)
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Disconnect resolved and/or Overriding details */}
          {(() => {
            const primaryResolution = resolveAccountDisconnectResolution(
              fullReportData.account
            );
            const eslOverride = fullReportData.account.override;
            const accountId = getAssessmentAccountId(fullReportData.account);
            const overrideEntry = getAccountOverrideRationaleEntry(
              fullReportData.assessment,
              accountId
            );
            const storedOverrideRationale =
              overrideEntry?.rationale?.trim() || null;
            const accountUpdateLog = getLevelUpdateLog(
              fullReportData.assessment.notes as any
            ).filter(
              entry =>
                String(entry.account_id || "") === String(accountId || "")
            );
            const hasAcceptLog = accountUpdateLog.some(
              entry => String(entry.type || "").toLowerCase() === "accept"
            );
            const hasOverrideLog = accountUpdateLog.some(
              entry => String(entry.type || "").toLowerCase() === "override"
            );
            const hasPrimary =
              primaryResolution &&
              (primaryResolution.agreedLevel != null ||
                Boolean(primaryResolution.rationale?.trim()) ||
                Boolean(primaryResolution.resolvedBy) ||
                Boolean(primaryResolution.resolvedAt) ||
                hasAcceptLog);
            const hasEsl =
              isManualEslOverride(
                fullReportData.account,
                primaryResolution,
                storedOverrideRationale
              ) ||
              (hasOverrideLog && Boolean(eslOverride?.override_level));
            // Hide Disconnect resolved only for ESL-close-only (no PS / Accept).
            // PS resolve → Accept → later Override must show both blocks.
            const showDisconnectResolved =
              Boolean(hasPrimary && primaryResolution) &&
              !isEslCloseOnlyWithoutPsOrAccept(primaryResolution, {
                hasAcceptLog,
              });
            const disconnectRationale =
              primaryResolution?.rationale?.trim() &&
              !isEslClosedByOverrideResolution(primaryResolution)
                ? primaryResolution.rationale.trim()
                : null;
            if (!showDisconnectResolved && !hasEsl) return null;

            return (
              <div className="mb-6 space-y-4">
                {showDisconnectResolved && primaryResolution && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">
                      Disconnect resolved
                    </h4>
                    <div className="rounded-md border border-orange-200 bg-orange-50/60 px-4 py-3">
                      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
                        {(primaryResolution.agreedLevelLabel ||
                          primaryResolution.agreedLevel != null) && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">
                              Agreed Level:
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-900">
                              {getLevelDisplayName(
                                primaryResolution.agreedLevel != null
                                  ? `L${primaryResolution.agreedLevel}`
                                  : primaryResolution.agreedLevelLabel
                              )}
                            </span>
                          </div>
                        )}
                        {primaryResolution.resolvedBy && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">
                              Resolved By:
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {primaryResolution.resolvedBy}
                            </span>
                          </div>
                        )}
                        {primaryResolution.resolvedAt && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">
                              Resolved At:
                            </span>
                            <span className="text-sm text-gray-900">
                              {formatAssessmentDateTimeUtc(
                                primaryResolution.resolvedAt
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                      {disconnectRationale && (
                        <div className="mt-3">
                          <div className="text-sm text-gray-600 mb-1">
                            Rationale
                          </div>
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">
                            {disconnectRationale}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {hasEsl && eslOverride?.override_level && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3">
                      Overridden
                    </h4>
                    <div className="rounded-md border border-purple-200 bg-purple-50/60 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">
                            Overridden Level:
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {getLevelDisplayName(eslOverride.override_level)}
                          </span>
                        </div>
                        {eslOverride.overridden_by && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">
                              Overridden By:
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {eslOverride.overridden_by}
                            </span>
                          </div>
                        )}
                        {eslOverride.overridden_at && (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">
                              Overridden At:
                            </span>
                            <span className="text-sm text-gray-900">
                              {formatAssessmentDateTimeUtc(
                                eslOverride.overridden_at
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                      {storedOverrideRationale && (
                        <div className="mt-3">
                          <div className="text-sm text-gray-600 mb-1">
                            Rationale
                          </div>
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">
                            {storedOverrideRationale}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Individual Responses Table */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h5 className="text-sm font-semibold text-gray-800 mb-3">
              Individual Responses
            </h5>
            {loadingFullReport ? (
              <div className="text-center py-4">
                <p className="text-muted">Loading responses...</p>
              </div>
            ) : (
              <div className="overflow-hidden">
                <Table
                  columns={[
                    {
                      key: "executiveName",
                      title: "Executive",
                      render: value => (
                        <div className="text-sm font-medium text-gray-900">
                          {value}
                        </div>
                      ),
                    },
                    {
                      key: "executiveEmail",
                      title: "Email",

                      render: value => (
                        <div className="text-sm text-gray-500">{value}</div>
                      ),
                    },
                    ...(shouldShowLevelMetrics(
                      fullReportData.assessment.category
                    )
                      ? [
                          {
                            key: "calculatedLevel",
                            title: "Calculated Level",
                          },
                        ]
                      : []),
                    {
                      key: "submittedAt",
                      title: "Submitted At",
                      render: value => (
                        <div className="text-sm text-gray-500">
                          {formatAssessmentDateTimeUtc(value || "")}
                        </div>
                      ),
                    },
                    { key: "actions", title: "Actions" },
                  ]}
                  data={fullReportData.responses.map(response => ({
                    executiveName: response.executiveName,
                    executiveEmail: response.executiveEmail,
                    calculatedLevel: shouldShowLevelMetrics(
                      fullReportData.assessment.category
                    ) ? (
                      response.ignored ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          Ignored
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-50 text-yellow-800 border border-yellow-200">
                          {getLevelDisplayName(response.calculatedLevel)}
                        </span>
                      )
                    ) : null,
                    submittedAt: response.submittedAt,
                    actions: response.ignored ? (
                      <span className="text-sm text-gray-400">No response</span>
                    ) : (
                      <button
                        onClick={async () => {
                          // Get assessment ID with fallback
                          const assessmentId =
                            fullReportData.assessment.id ||
                            (fullReportData.assessment as any).assessment_id ||
                            null;

                          if (!assessmentId) {
                            showNotification(
                              "Invalid assessment",
                              "Unable to identify the assessment. Please try again.",
                              "error"
                            );
                            return;
                          }

                          const accountId =
                            fullReportData.account.id ||
                            fullReportData.account.account_id ||
                            null;
                          setIndividualResponseModal({
                            isOpen: true,
                            response: response,
                            assessmentId: assessmentId,
                            accountId,
                            questionsAndResponses: null,
                            loading: true,
                          });

                          try {
                            await dispatch(
                              fetchAssessmentQuestionsAndResponses(assessmentId)
                            ).unwrap();

                            // Update loading state after successful fetch
                            setIndividualResponseModal((prev: any) => ({
                              ...prev,
                              loading: false,
                            }));
                          } catch (error: any) {
                            console.error(
                              "Error fetching questions and responses:",
                              error
                            );
                            showNotification(
                              "Failed to load response",
                              error ||
                                "Unable to fetch questions and responses.",
                              "error"
                            );
                            setIndividualResponseModal((prev: any) => ({
                              ...prev,
                              loading: false,
                            }));
                          }
                        }}
                        className="bg-transparent  border-none text-purple-600 cursor-pointer hover:underline transition-all font-medium"
                      >
                        View Response
                      </button>
                    ),
                  }))}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline-primary" onClick={onClose}>
              Close
            </Button>
            <Button variant="primary" onClick={() => onExport(fullReportData)}>
              Export Report
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function IndividualResponseModal({
  modal,
  onClose,
  questionsAndResponses,
  isLoadingQuestionsAndResponses,
}: {
  modal: {
    isOpen: boolean;
    response: {
      id?: string;
      executiveName?: string;
      executiveEmail?: string;
      submittedAt?: string;
    } | null;
    accountId: string | null;
    loading: boolean;
  };
  onClose: () => void;
  questionsAndResponses: any;
  isLoadingQuestionsAndResponses: boolean;
}) {
  return (
    <Modal
      isOpen={modal.isOpen}
      toggle={onClose}
      title={`Individual Response: ${modal.response?.executiveName ?? ""}`}
      size="5xl"
      overlayClassName="bg-black/60 z-[1060]"
      containerClassName="z-[1070]"
    >
      {modal.response &&
        (() => {
          const res = modal.response;
          const matchedApi = getMatchedApiResponse(
            questionsAndResponses,
            res.id ?? null,
            modal.accountId
          );
          const submittedAt = matchedApi?.submitted_at ?? res.submittedAt ?? "";

          return (
            <div className="space-y-5">
              {/* Executive and Submission Details */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-gray-500 block mb-1">
                      Executive
                    </label>
                    <div>
                      <p className="text-sm text-gray-900 mb-0 font-medium">
                        {res.executiveName}
                      </p>
                      <p className="text-xs text-gray-500 mb-0">
                        {res.executiveEmail}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-gray-500 block mb-1">
                      Submitted
                    </label>
                    <div>
                      <p className="text-sm text-gray-900 mb-0 font-medium">
                        {formatAssessmentDateTimeUtc(submittedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Questions and Answers */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm min-h-[300px]">
                <h5 className="text-sm font-semibold text-gray-800 mb-3">
                  Assessment Responses
                </h5>

                {isLoadingQuestionsAndResponses || modal.loading ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">
                      Loading questions and responses...
                    </p>
                  </div>
                ) : !questionsAndResponses ? (
                  <div className="border border-gray-200 rounded-lg p-4 bg-white text-center">
                    <p className="text-gray-500 text-sm m-0">
                      No response data available
                    </p>
                  </div>
                ) : !matchedApi ? (
                  <div className="border border-gray-200 rounded-lg p-4 bg-white text-center">
                    <p className="text-gray-500 text-sm m-0">
                      No response data found for this executive.
                    </p>
                  </div>
                ) : (
                  (() => {
                    const questions =
                      questionsAndResponses.questions?.questions ?? [];
                    const responses = matchedApi.responses ?? {};
                    const sortedQuestions = [...questions].sort(
                      (a: any, b: any) =>
                        (a.order_index ?? 0) - (b.order_index ?? 0)
                    );

                    return (
                      <div className="space-y-4">
                        {sortedQuestions.length === 0 ? (
                          <div className="border border-gray-200 rounded-lg p-4 bg-white text-center">
                            <p className="text-gray-500 text-sm m-0">
                              No questions found
                            </p>
                          </div>
                        ) : (
                          sortedQuestions.map(
                            (question: any, index: number) => {
                              const answer = responses[question.id] ?? "";
                              const displayAnswer = getDisplayAnswerForQuestion(
                                question,
                                answer
                              );
                              return (
                                <div
                                  key={question.id ?? index}
                                  className="border border-gray-200 rounded-lg p-4 bg-white"
                                >
                                  <div className="mb-2">
                                    <label className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-semibold">
                                        {question.order_index ?? index + 1}
                                      </span>
                                      {question.question_text}
                                      {question.required && (
                                        <span className="text-red-600">*</span>
                                      )}
                                    </label>
                                    <p className="text-xs text-gray-500 mt-1 ml-8">
                                      {question.question_type}
                                    </p>
                                  </div>
                                  <div className="ml-8 p-3 bg-gray-50 rounded-md border border-gray-200">
                                    <p className="text-gray-900 text-sm whitespace-pre-wrap m-0">
                                      {displayAnswer}
                                    </p>
                                  </div>
                                </div>
                              );
                            }
                          )
                        )}
                      </div>
                    );
                  })()
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline-primary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          );
        })()}
    </Modal>
  );
}

function exportFullReportCsv(reportData: FullReportData | null) {
  if (!reportData) return;

  const { assessment, account, responses } = reportData;
  const showLevels = ["short", "small"].includes(
    (assessment.category || "").toLowerCase()
  );
  const csvHeaders = ["Executive Name", "Email", "Submitted At"];

  if (showLevels) {
    csvHeaders.push("Calculated Level", "Rationale");
  } else {
    csvHeaders.push(
      "Engagement Quality",
      "Strategic Alignment",
      "Relationship Depth",
      "Growth Potential",
      "Key Challenges",
      "Success Metrics",
      "Next Steps"
    );
  }

  const csvRows = responses.map(response => {
    const row = [
      response.executiveName,
      response.executiveEmail,
      formatAssessmentDateTimeUtc(response.submittedAt || ""),
    ];

    if (showLevels) {
      row.push(
        response.ignored
          ? "Ignored"
          : response.calculatedLevel ||
              response.responses?.maturity_level ||
              "",
        response.ignored ? "" : response.responses?.rationale || ""
      );
    } else {
      row.push(
        response.responses?.engagement_quality || "",
        response.responses?.strategic_alignment || "",
        response.responses?.relationship_depth || "",
        response.responses?.growth_potential || "",
        response.responses?.key_challenges || "",
        response.responses?.success_metrics || "",
        response.responses?.next_steps || ""
      );
    }

    return row;
  });

  const csvContent = [csvHeaders, ...csvRows]
    .map(row =>
      row
        .map(cell => {
          const escapedCell = String(cell).replace(
            /"/g,
            String.fromCharCode(34).repeat(2)
          );
          return `"${escapedCell}"`;
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${assessment.name}_${account.name}_Report_${utcTodayInputValue()}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function StatusAccountFullReport({
  account,
  onClose,
}: {
  account: {
    account_id: string;
    report_assessment_id?: string | null;
  } | null;
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const { organizationLevels } = useAppSelector((state: any) => state.admin);
  const { questionsAndResponses, isLoadingQuestionsAndResponses } =
    useAppSelector(state => state.assessment);
  const [fullReportData, setFullReportData] = useState<FullReportData | null>(
    null
  );
  const [open, setOpen] = useState(false);
  const [individualResponseModal, setIndividualResponseModal] = useState<{
    isOpen: boolean;
    response: {
      id?: string;
      executiveName?: string;
      executiveEmail?: string;
      submittedAt?: string;
    } | null;
    accountId: string | null;
    loading: boolean;
  }>({
    isOpen: false,
    response: null,
    accountId: null,
    loading: false,
  });

  useEffect(() => {
    if (!account?.report_assessment_id) {
      setOpen(false);
      setFullReportData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const response = await dispatch(fetchAssessments({})).unwrap();
      const mapped = (
        (response as { assessments?: any[] }).assessments || []
      ).map(mapApiAssessmentToAssessmentWithAccounts);
      const assessment = mapped.find(
        item => String(item.id) === String(account.report_assessment_id)
      );
      const match = assessment?.accounts?.find(
        item =>
          String(item.id || item.account_id) === String(account.account_id)
      );
      if (!assessment || !match || cancelled) return;
      const data = await loadFullReportData(dispatch, assessment, match);
      if (cancelled) return;
      setFullReportData(data);
      setOpen(true);
    })().catch(() => {
      if (!cancelled) setOpen(false);
    });
    return () => {
      cancelled = true;
    };
  }, [account, dispatch]);

  return (
    <>
      <FullReportModalFrame
        isOpen={open && fullReportData != null}
        fullReportData={fullReportData}
        organizationLevels={organizationLevels || []}
        onClose={() => {
          setOpen(false);
          setFullReportData(null);
          setIndividualResponseModal({
            isOpen: false,
            response: null,
            accountId: null,
            loading: false,
          });
          onClose();
        }}
        onExport={exportFullReportCsv}
        showNotification={() => undefined}
        setIndividualResponseModal={
          setIndividualResponseModal as React.Dispatch<
            React.SetStateAction<any>
          >
        }
      />
      <IndividualResponseModal
        modal={individualResponseModal}
        onClose={() =>
          setIndividualResponseModal({
            isOpen: false,
            response: null,
            accountId: null,
            loading: false,
          })
        }
        questionsAndResponses={questionsAndResponses}
        isLoadingQuestionsAndResponses={isLoadingQuestionsAndResponses}
      />
    </>
  );
}

const AssessmentsContainer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole = loggedInUser?.role || "USER";
  const isCLevel = isRole(userRole, ROLES.C_LEVEL);
  const isSponsorWithExecOpsApprove =
    isRole(userRole, ROLES.EXECUTIVE_SPONSOR) &&
    loggedInUser?.executiveOps === true &&
    loggedInUser?.executiveOpsApprove === true;
  const canOverrideLevel =
    isCLevel ||
    isRole(userRole, ROLES.EXECUTIVE_SPONSOR_LEAD) ||
    isRole(userRole, ROLES.VENDOR_ADMIN) ||
    isSponsorWithExecOpsApprove;
  const allowInProgressReport =
    isRole(userRole, ROLES.EXECUTIVE_SPONSOR_LEAD) ||
    isRole(userRole, ROLES.VENDOR_ADMIN) ||
    loggedInUser?.executiveOps === true;
  const dispatch = useAppDispatch();
  const {
    assessments,
    isLoadingAssessments,
    createAssessmentSuccess,
    questionsAndResponses,
    isLoadingQuestionsAndResponses,
    disconnectReview,
    isLoadingDisconnectReview,
    disconnectReviewError,
    isTriggeringDisconnect,
  } = useAppSelector(state => state.assessment);
  const { organizationLevels } = useAppSelector((state: any) => state.admin);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] =
    useState<AssessmentForReview | null>(null);
  const [templateData, setTemplateData] = useState<TemplateData | null>(null);
  const [isTemplatePreviewMode, setIsTemplatePreviewMode] = useState(false);
  const [assessmentResponses, setAssessmentResponses] = useState<
    Record<string, AssessmentResponseMap>
  >({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [voiceModeEnabled, setVoiceModeEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  // Initialize tab from URL or default to "completed"
  const tabFromUrlRaw = searchParams.get("assessmentTab");
  // Legacy URL assessmentTab=self-assessments → team-assessments
  const tabFromUrl =
    tabFromUrlRaw === "self-assessments" ? "team-assessments" : tabFromUrlRaw;
  const validTabs = [
    "status",
    ...(isCLevel ? [] : (["team-assessments"] as const)),
    "pending",
    "ongoing",
    "disconnects",
    "published",
    "aborted",
    "level-mix",
  ];
  const initialTab =
    tabFromUrl && validTabs.includes(tabFromUrl)
      ? tabFromUrl
      : tabFromUrl === "completed"
        ? "ongoing"
        : "ongoing";
  const [activeAssessmentStatusTab, setActiveAssessmentStatusTab] =
    useState(initialTab);
  const publishedKindFromUrl = searchParams.get("publishedKind");
  const [publishedKindTab, setPublishedKindTab] = useState<PublishedKindTab>(
    publishedKindFromUrl === "formal" ? "formal" : "team"
  );
  const abortedKindFromUrl = searchParams.get("abortedKind");
  const [abortedKindTab, setAbortedKindTab] = useState<AssessmentKindTab>(
    abortedKindFromUrl === "formal" ? "formal" : "team"
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState("all");
  const [disconnectSubTab, setDisconnectSubTab] =
    useState<DisconnectSubTab>("ongoing");
  const disconnectKindFromUrl = searchParams.get("disconnectKind");
  const [disconnectKindTab, setDisconnectKindTab] = useState<AssessmentKindTab>(
    disconnectKindFromUrl === "formal" ? "formal" : "team"
  );
  const mixFromUrl = searchParams.get("mix");
  const [levelMixScopeTab, setLevelMixScopeTab] = useState<LevelMixScope>(() =>
    parseHubLevelMixScope(mixFromUrl)
  );
  const [selectedDisconnectKeys, setSelectedDisconnectKeys] = useState<
    Set<string>
  >(new Set());
  const [isDisconnectTriggerModalOpen, setIsDisconnectTriggerModalOpen] =
    useState(false);
  const [disconnectTriggerAccounts, setDisconnectTriggerAccounts] = useState<
    DisconnectReviewAccount[]
  >([]);
  const [disconnectTriggerDueDate, setDisconnectTriggerDueDate] = useState("");
  const [disconnectTriggerContextNote, setDisconnectTriggerContextNote] =
    useState("");
  const [resolutionReviewModal, setResolutionReviewModal] = useState<{
    isOpen: boolean;
    assessment: AssessmentWithAccounts | null;
    account: AssessmentAccount | null;
  }>({
    isOpen: false,
    assessment: null,
    account: null,
  });
  const [isAcceptingResolution, setIsAcceptingResolution] = useState(false);
  const [forcePublishSuccessInfo, setForcePublishSuccessInfo] =
    useState<AssessmentSubmitSuccessInfo | null>(null);
  const [launchingAssessmentId, setLaunchingAssessmentId] = useState<
    string | null
  >(null);
  const [selectedHubPublishByAssessment, setSelectedHubPublishByAssessment] =
    useState<Record<string, Set<string>>>({});
  const [
    completedAccountViewByAssessment,
    setCompletedAccountViewByAssessment,
  ] = useState<Record<string, "aligned" | "disconnects">>({});
  const [publishingAssessmentId, setPublishingAssessmentId] = useState<
    string | null
  >(null);

  // Single state for all expanded assessments by status tab
  const [expandedAssessments, setExpandedAssessments] = useState<
    Record<string, Set<string>>
  >({
    pending: new Set(),
    ongoing: new Set(),
    completed: new Set(),
    published: new Set(),
    aborted: new Set(),
  });
  const [showFullReportModal, setShowFullReportModal] = useState(false);
  const [fullReportData, setFullReportData] = useState<FullReportData | null>(
    null
  );
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [statisticsAssessment, setStatisticsAssessment] =
    useState<AssessmentWithAccounts | null>(null);
  const [statisticsProgressTab, setStatisticsProgressTab] = useState<
    "completed" | "incomplete"
  >("completed");
  const [consolidatedReportModal, setConsolidatedReportModal] = useState<{
    isOpen: boolean;
    assessment: AssessmentWithAccounts | null;
  }>({
    isOpen: false,
    assessment: null,
  });
  const [overrideModal, setOverrideModal] = useState<OverrideModalState>({
    isOpen: false,
    assessmentId: null,
    accountId: null,
    currentValue: null,
    publishOnSave: false,
  });
  const [overrideValue, setOverrideValue] = useState("");
  const [overrideRationale, setOverrideRationale] = useState("");
  const [individualResponseModal, setIndividualResponseModal] = useState<{
    isOpen: boolean;
    response: FullReportData["responses"][0] | null;
    assessmentId: string | null;
    accountId: string | null;
    questionsAndResponses: {
      questions: Array<{
        id: string;
        question_text: string;
        question_type: string;
        required: boolean;
        order_index: number;
        options?: any;
      }>;
      responses: Record<string, string>;
    } | null;
    loading: boolean;
  }>({
    isOpen: false,
    response: null,
    assessmentId: null,
    accountId: null,
    questionsAndResponses: null,
    loading: false,
  });

  const [extendDueDateModal, setExtendDueDateModal] = useState<{
    isOpen: boolean;
    assessment: AssessmentWithAccounts | null;
    newDueDate: string;
    reason: string;
  }>({
    isOpen: false,
    assessment: null,
    newDueDate: "",
    reason: "",
  });

  const [abortAssessmentModal, setAbortAssessmentModal] = useState<{
    isOpen: boolean;
    assessment: AssessmentWithAccounts | null;
    reason: string;
  }>({
    isOpen: false,
    assessment: null,
    reason: "",
  });
  const [extendedReasonModal, setExtendedReasonModal] = useState<{
    isOpen: boolean;
    assessment: AssessmentWithAccounts | null;
  }>({
    isOpen: false,
    assessment: null,
  });

  const [isExtendingDueDate, setIsExtendingDueDate] = useState(false);
  const [isAbortingAssessment, setIsAbortingAssessment] = useState(false);

  const [notification, setNotification] = useState({
    isOpen: false,
    message: "",
    details: "",
    type: "success" as "success" | "error",
  });
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const win = window as typeof window & {
      webkitSpeechRecognition?: unknown;
      SpeechRecognition?: unknown;
    };

    const recognitionSupported = Boolean(
      win.SpeechRecognition || win.webkitSpeechRecognition
    );
    const synthesisSupported = typeof win.speechSynthesis !== "undefined";

    setSpeechSupported(recognitionSupported || synthesisSupported);
  }, []);

  const startListening = React.useCallback(() => {
    if (!speechSupported) {
      return;
    }
    setIsListening(true);
  }, [speechSupported]);

  const stopListening = React.useCallback(() => {
    setIsListening(false);
  }, []);

  const handleToggleVoiceMode = React.useCallback(
    (next: boolean) => {
      if (!speechSupported && next) {
        setVoiceModeEnabled(false);
        return;
      }

      setVoiceModeEnabled(next);
      if (next) {
        startListening();
      } else {
        stopListening();
      }
    },
    [speechSupported, startListening, stopListening]
  );

  const handleCloseReviewModal = React.useCallback(() => {
    setPreviewModalOpen(false);
    setSelectedAssessment(null);
    setTemplateData(null);
    setIsTemplatePreviewMode(false);
    setCurrentQuestionIndex(0);
    setIsSubmittingReview(false);
    setVoiceModeEnabled(false);
    stopListening();
  }, [stopListening]);

  const handleOpenReviewAssessment = React.useCallback(
    (assessment: AssessmentWithAccounts, account: AssessmentAccount) => {
      const reviewAssessment: AssessmentForReview = {
        id: assessment.id,
        title:
          assessment.name ||
          assessment.template_name ||
          assessment.name ||
          "Assessment Preview",
        status: assessment.status === "completed" ? "completed" : "in-progress",
        account: {
          name:
            account.name || account.account_name || account.account_id || null,
        },
        questions: [],
      };

      setSelectedAssessment(reviewAssessment);
      setIsTemplatePreviewMode(false);
      setCurrentQuestionIndex(0);
      setPreviewModalOpen(true);
      setAssessmentResponses(prev => ({
        ...prev,
        [reviewAssessment.id]: prev[reviewAssessment.id] || {},
      }));
    },
    []
  );

  const handleReviewResponseChange = React.useCallback(
    (questionId: string, value: string | string[]) => {
      setAssessmentResponses(prev => {
        const activeAssessmentId = selectedAssessment?.id || templateData?.id;
        if (!activeAssessmentId) {
          return prev;
        }

        return {
          ...prev,
          [activeAssessmentId]: {
            ...(prev[activeAssessmentId] || {}),
            [questionId]: value,
          },
        };
      });
    },
    [selectedAssessment, templateData]
  );

  const handleSaveDraft = React.useCallback(() => {
    setIsSubmittingReview(true);
    window.setTimeout(() => {
      setIsSubmittingReview(false);
    }, 600);
  }, []);

  const handleSubmitAssessment = React.useCallback(() => {
    setIsSubmittingReview(true);
    window.setTimeout(() => {
      setIsSubmittingReview(false);
      handleCloseReviewModal();
    }, 800);
  }, [handleCloseReviewModal]);

  const showNotification = React.useCallback(
    (
      message: string,
      details: string,
      type: "success" | "error" = "success"
    ) => {
      setNotification({ isOpen: true, message, details, type });
    },
    []
  );

  const closeNotification = React.useCallback(() => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Dynamic handler for updating assessment status
  const handleUpdateAssessmentStatus = React.useCallback(
    async (
      assessment: any,
      status: "active" | "completed" | "published" | "aborted",
      successTitle: string,
      successMessage: string,
      errorAction: string
    ) => {
      if (!assessment?.id) return;

      try {
        await dispatch(
          updateAssessmentStatus({
            id: assessment.id,
            status,
          })
        ).unwrap();

        toast.success(successMessage);

        setActiveAssessmentStatusTab(status === "active" ? "ongoing" : status);
      } catch (error: any) {
        const message =
          error || `Failed to ${errorAction} assessment. Please try again.`;
        toast.error(message);
      }
    },
    [dispatch]
  );

  // Handler for launching assessment (pending -> active)
  const handleLaunchAssessment = React.useCallback(
    async (assessment: any) => {
      if (!assessment?.id) return;

      setLaunchingAssessmentId(assessment.id);
      try {
        await handleUpdateAssessmentStatus(
          assessment,
          "active",
          "Assessment launched",
          "The assessment has been successfully launched.",
          "launch"
        );
      } finally {
        setLaunchingAssessmentId(null);
      }
    },
    [handleUpdateAssessmentStatus]
  );

  // Publishing aligned accounts via disconnect-aware PUT publish
  const getHubPublishSelection = React.useCallback(
    (assessmentId: string) =>
      selectedHubPublishByAssessment[assessmentId] || new Set<string>(),
    [selectedHubPublishByAssessment]
  );

  const handleToggleHubPublishAccount = React.useCallback(
    (assessmentId: string, accountId: string) => {
      setSelectedHubPublishByAssessment(prev => {
        const next = new Set(prev[assessmentId] || []);
        if (next.has(accountId)) next.delete(accountId);
        else next.add(accountId);
        return { ...prev, [assessmentId]: next };
      });
    },
    []
  );

  const handleToggleAllHubPublishAccounts = React.useCallback(
    (assessmentId: string, accountIds: string[], selectAll: boolean) => {
      setSelectedHubPublishByAssessment(prev => {
        const next = new Set(prev[assessmentId] || []);
        if (selectAll) accountIds.forEach(id => next.add(id));
        else accountIds.forEach(id => next.delete(id));
        return { ...prev, [assessmentId]: next };
      });
    },
    []
  );

  const refreshAssessmentsAfterPublish = React.useCallback(
    async (options?: { includeDisconnectReview?: boolean }) => {
      const includeDisconnectReview =
        options?.includeDisconnectReview ??
        activeAssessmentStatusTab !== "published";

      if (includeDisconnectReview) {
        await dispatch(
          fetchDisconnectReview({ assessmentStatus: "all" })
        ).unwrap();
      }

      if (activeAssessmentStatusTab === "team-assessments") {
        await dispatch(
          fetchVendorSelfAssessments({
            status: "open",
            sort_by: "created_at",
            sort_order: "desc",
          })
        ).unwrap();
        return;
      }

      if (
        activeAssessmentStatusTab === "disconnects" ||
        activeAssessmentStatusTab === "level-mix"
      ) {
        return;
      }

      const apiStatus = getApiStatus(activeAssessmentStatusTab);
      await dispatch(
        fetchAssessments({
          sort_by: "created_at",
          sort_order: "desc",
          status: apiStatus as any,
        })
      ).unwrap();
    },
    [dispatch, activeAssessmentStatusTab]
  );

  const getForcePublishAccountIds = React.useCallback(
    (assessmentId: string): string[] => {
      const alignedIds = Array.from(getHubPublishSelection(assessmentId));
      const disconnectIds = Array.from(selectedDisconnectKeys)
        .filter(key => key.startsWith(`${assessmentId}::`))
        .map(key => key.split("::")[1])
        .filter(Boolean);
      return Array.from(new Set([...alignedIds, ...disconnectIds]));
    },
    [getHubPublishSelection, selectedDisconnectKeys]
  );

  const clearForcePublishSelection = React.useCallback(
    (assessmentId: string) => {
      setSelectedHubPublishByAssessment(prev => {
        if (!prev[assessmentId]) return prev;
        const next = { ...prev };
        delete next[assessmentId];
        return next;
      });
      setSelectedDisconnectKeys(prev => {
        const next = new Set(prev);
        Array.from(prev).forEach(key => {
          if (key.startsWith(`${assessmentId}::`)) {
            next.delete(key);
          }
        });
        return next;
      });
    },
    []
  );

  const executeForcePublish = React.useCallback(
    async (assessment: AssessmentWithAccounts, accountIds?: string[]) => {
      if (!assessment?.id) return;

      setPublishingAssessmentId(assessment.id);
      try {
        const payload: {
          assessmentId: string;
          force: boolean;
          accountIds?: string[];
        } = {
          assessmentId: assessment.id,
          force: true,
        };
        if (accountIds?.length) {
          payload.accountIds = accountIds;
        }
        await dispatch(publishAssessmentAccounts(payload)).unwrap();
        const selectedCount = accountIds?.length ?? 0;
        let successAccountId = assessment.id;
        let successAccountName = assessment.name?.trim() || "this assessment";
        if (selectedCount === 1 && accountIds?.[0]) {
          successAccountId = accountIds[0];
          const match = (assessment.accounts ?? []).find(
            acc =>
              acc.id === accountIds[0] ||
              (acc as { account_id?: string }).account_id === accountIds[0]
          );
          const named =
            match?.name?.trim() ||
            (match as { account_name?: string })?.account_name?.trim();
          if (named) successAccountName = named;
        }
        setForcePublishSuccessInfo({
          kind: "assessment_force_publish",
          accountId: successAccountId,
          accountName: successAccountName,
          assessmentTitle: assessment.name,
          publishedAccountCount: selectedCount > 0 ? selectedCount : undefined,
          dateLabel: formatAssessmentSubmitSavedOnDate(),
        });
        clearForcePublishSelection(assessment.id);
        await refreshAssessmentsAfterPublish({ includeDisconnectReview: true });
      } catch (error: unknown) {
        toast.error(
          typeof error === "string"
            ? error
            : "Unable to force-publish this assessment."
        );
      } finally {
        setPublishingAssessmentId(null);
      }
    },
    [clearForcePublishSelection, dispatch, refreshAssessmentsAfterPublish]
  );

  const [editingAssessment, setEditingAssessment] =
    useState<AssessmentWithAccounts | null>(null);
  const skipAssessmentsFetchRef = useRef(false);

  const [deleteAssessmentModal, setDeleteAssessmentModal] = useState<{
    isOpen: boolean;
    assessment: AssessmentWithAccounts | null;
    inputValue: string;
  }>({
    isOpen: false,
    assessment: null,
    inputValue: "",
  });

  const [forcePublishConfirmModal, setForcePublishConfirmModal] = useState<{
    isOpen: boolean;
    assessment: AssessmentWithAccounts | null;
    accountIds: string[];
  }>({
    isOpen: false,
    assessment: null,
    accountIds: [],
  });

  const handleOpenForcePublishConfirm = (
    assessment: AssessmentWithAccounts
  ) => {
    if (!assessment?.id) return;
    setForcePublishConfirmModal({
      isOpen: true,
      assessment,
      accountIds: getForcePublishAccountIds(assessment.id),
    });
  };

  const handleCloseForcePublishConfirm = () => {
    setForcePublishConfirmModal({
      isOpen: false,
      assessment: null,
      accountIds: [],
    });
  };

  const handleConfirmForcePublish = async () => {
    const assessment = forcePublishConfirmModal.assessment;
    if (!assessment?.id) return;
    const accountIds = forcePublishConfirmModal.accountIds;
    handleCloseForcePublishConfirm();
    await executeForcePublish(
      assessment,
      accountIds.length > 0 ? accountIds : undefined
    );
  };

  const assessmentsWithDetails = React.useMemo<AssessmentWithAccounts[]>(() => {
    if (!Array.isArray(assessments)) {
      return [];
    }
    // Map API assessments to AssessmentWithAccounts format
    return assessments.map((assessment: any) => {
      if (assessment.assessment_id || assessment.id) {
        if (assessment.target_accounts || assessment.accounts) {
          return mapApiAssessmentToAssessmentWithAccounts({
            ...assessment,
            assessment_id: assessment.assessment_id || assessment.id,
          } as ApiAssessmentResponse);
        }
      }
      // If it already has id and matches AssessmentWithAccounts structure, extract template_id if needed
      if (assessment.id && assessment.accounts !== undefined) {
        const assessmentWithAccounts = assessment as AssessmentWithAccounts;
        // Extract template_id from questions_data.metadata.template_id if not already present
        if (!assessmentWithAccounts.template_id) {
          const templateId =
            (assessment as any).questions_data?.metadata?.template_id ||
            (assessment as any).data?.metadata?.template_id ||
            (assessment as any).template_id ||
            "";
          assessmentWithAccounts.template_id = templateId;
        }
        return assessmentWithAccounts;
      }
      // Fallback: try to map it anyway if it has target_accounts
      if (assessment.target_accounts) {
        return mapApiAssessmentToAssessmentWithAccounts(
          assessment as ApiAssessmentResponse
        );
      }
      // Otherwise, return as is (shouldn't happen with new API)
      return assessment as AssessmentWithAccounts;
    });
  }, [assessments]);

  const assessmentsWithDisconnectEnrichment = React.useMemo<
    AssessmentWithAccounts[]
  >(() => {
    if (!disconnectReview?.accounts?.length) return assessmentsWithDetails;

    const reviewByKey = new Map(
      disconnectReview.accounts.map(row => [
        getDisconnectSelectionKey(row.assessmentId, row.accountId),
        row,
      ])
    );

    return assessmentsWithDetails.map(assessment => ({
      ...assessment,
      accounts: (assessment.accounts || []).map(account => {
        const reviewRow = reviewByKey.get(
          getDisconnectSelectionKey(
            assessment.id,
            getAssessmentAccountId(account)
          )
        );
        if (!reviewRow) return account;
        if (!shouldMergeDisconnectReviewRow(account)) return account;
        return mergeAssessmentAccountFromDisconnectReview(account, reviewRow);
      }),
    }));
  }, [assessmentsWithDetails, disconnectReview]);

  // Utility functions for assessment display
  const getLevelDisplayName = (level: string | null | undefined): string => {
    if (!level) return "Pending";

    const levelStr = level.toString().trim();

    if (!/\d/.test(levelStr)) return levelStr;

    const code = toStoredLevelCode(levelStr);
    const levelData = organizationLevels.find(
      (l: { level_code: string; name: string; description: string }) =>
        l.level_code?.toUpperCase() === code
    );

    return levelData
      ? getAccountLevelVerboseLabel(code, levelData.name)
      : toVerboseLevelLabel(levelStr);
  };

  const shouldShowLevelMetrics = (
    category: string | null | undefined
  ): boolean => {
    if (!category) return false;
    const normalized = category.toLowerCase();
    return normalized === "short" || normalized === "small";
  };

  // Helper to get account level values (handles both camelCase and snake_case)
  const getAccountLevel = (
    account: AssessmentAccount,
    levelType: "min" | "median" | "max" | "final"
  ): string | null => {
    const levelMap: Record<string, string | null | undefined> = {
      min: account.minLevel || account.min_level,
      median: account.medianLevel || account.median_level,
      max: account.maxLevel || account.max_level,
      final: account.finalLevel || account.final_level,
    };
    return levelMap[levelType] || null;
  };

  // Helper to get account response counts (assigned only — pending fold-in excluded)
  const getAccountResponseCounts = (account: AssessmentAccount) => {
    const executives = account.executives?.length
      ? account.executives
      : account.users?.map(u =>
          mapApiUserToExecutive(u, account.disconnectStatus)
        ) || [];
    const countable = executives.filter(
      exec =>
        !isSkippedExecutive(exec) &&
        !isPendingSelfFoldExecutive(exec) &&
        !(isHeldExecutive(exec) && !exec.is_responded)
    );
    const useExecCounts = executives.length > 0;
    const totalAssigned = useExecCounts
      ? countable.length
      : account.totalAssigned || account.requests_sent || 0;
    const responsesCount = useExecCounts
      ? countable.filter(exec => exec.is_responded).length
      : account.responses || account.responses_received || 0;
    const responseRate =
      totalAssigned > 0
        ? Math.round((responsesCount / totalAssigned) * 100)
        : 0;

    return { totalAssigned, responsesCount, responseRate };
  };

  const getStatusDisplayName = (status: string): string => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "pending":
        return "Pending";
      case "ongoing":
        return "Ongoing";
      case "aborted":
        return "Aborted";
      case "active":
        return "Live";
      case "completed":
        return "Completed";
      case "published":
        return "Published";
      case "draft":
        return "Draft";
      case "upcoming":
        return "Upcoming";
      default:
        return status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown";
    }
  };

  const isShortCategory = (category: string | null | undefined): boolean => {
    const normalized = category?.toLowerCase();
    return normalized === "short" || normalized === "small";
  };

  const formatDueDate = (date?: string | null): string => {
    if (!date) return "Not set";
    const formatted = formatAssessmentDateTimeUtc(date, true);
    return formatted || "Not set";
  };

  const isOverdue = (dueDate?: string | null): boolean =>
    isUtcDateOverdue(dueDate);

  // Handle open edit assessment modal - now opens wizard
  const handleOpenEditAssessmentModal = (
    assessment: AssessmentWithAccounts
  ) => {
    setEditingAssessment(assessment);
    setIsWizardOpen(true);
  };

  // Handle delete assessment modal
  const handleOpenDeleteAssessmentModal = (
    assessment: AssessmentWithAccounts
  ) => {
    setDeleteAssessmentModal({
      isOpen: true,
      assessment,
      inputValue: assessment.name || "",
    });
  };

  const handleCloseDeleteAssessmentModal = () => {
    setDeleteAssessmentModal({
      isOpen: false,
      assessment: null,
      inputValue: "",
    });
  };

  const handleDeleteAssessment = async () => {
    if (!deleteAssessmentModal.assessment) return;

    try {
      await dispatch(
        deleteAssessment(deleteAssessmentModal.assessment.id)
      ).unwrap();

      showNotification(
        "Assessment deleted",
        "The pending assessment has been removed."
      );

      handleCloseDeleteAssessmentModal();

      // Refetch assessments for the current tab
      const apiStatus = getApiStatus(activeAssessmentStatusTab);
      dispatch(
        fetchAssessments({
          sort_by: "created_at",
          sort_order: "desc",
          status: apiStatus as any,
        })
      );
    } catch (error: any) {
      const message =
        typeof error === "string"
          ? error
          : error?.message || "Unable to delete the assessment.";
      showNotification("Delete failed", message, "error");
    }
  };

  // Handle open extend due date modal
  const handleOpenExtendDueDateModal = (assessment: AssessmentWithAccounts) => {
    const currentDueDate =
      assessment.dueDate ||
      assessment.due_on ||
      assessment.due_date_info?.current_due_date ||
      "";
    let formattedDate = "";
    if (currentDueDate) {
      formattedDate = toUtcDateInputValue(currentDueDate);
    }
    setExtendDueDateModal({
      isOpen: true,
      assessment,
      newDueDate: formattedDate,
      reason: "",
    });
  };

  // Handle close extend due date modal
  const handleCloseExtendDueDateModal = () => {
    setExtendDueDateModal({
      isOpen: false,
      assessment: null,
      newDueDate: "",
      reason: "",
    });
  };

  // Handle extend due date
  const handleExtendDueDate = async () => {
    if (
      !extendDueDateModal.assessment ||
      !extendDueDateModal.newDueDate ||
      !extendDueDateModal.reason.trim()
    ) {
      showNotification(
        "Missing required fields",
        "Please provide a new due date and reason for extension.",
        "error"
      );
      return;
    }

    setIsExtendingDueDate(true);
    try {
      await dispatch(
        updateAssessment({
          id: extendDueDateModal.assessment.id,
          data: {
            due_on: extendDueDateModal.newDueDate,
            reason: extendDueDateModal.reason.trim(),
          },
        })
      ).unwrap();

      showNotification(
        "Due date extended",
        "The assessment due date has been successfully extended."
      );

      handleCloseExtendDueDateModal();

      // Refetch assessments for the current tab
      const apiStatus = getApiStatus(activeAssessmentStatusTab);
      dispatch(
        fetchAssessments({
          sort_by: "created_at",
          sort_order: "desc",
          status: apiStatus as any,
        })
      );
    } catch (error: any) {
      const message =
        typeof error === "string"
          ? error
          : error?.message || "Unable to extend the due date.";
      showNotification("Extend failed", message, "error");
    } finally {
      setIsExtendingDueDate(false);
    }
  };

  // Handle open abort assessment modal
  const handleOpenAbortAssessmentModal = (
    assessment: AssessmentWithAccounts
  ) => {
    setAbortAssessmentModal({
      isOpen: true,
      assessment,
      reason: "",
    });
  };

  // Handle close abort assessment modal
  const handleCloseAbortAssessmentModal = () => {
    setAbortAssessmentModal({
      isOpen: false,
      assessment: null,
      reason: "",
    });
  };

  const handleOpenExtendedReasonModal = (
    assessment: AssessmentWithAccounts
  ) => {
    setExtendedReasonModal({
      isOpen: true,
      assessment,
    });
  };

  const handleCloseExtendedReasonModal = () => {
    setExtendedReasonModal({
      isOpen: false,
      assessment: null,
    });
  };

  // Handle abort assessment
  const handleAbortAssessment = async () => {
    if (
      !abortAssessmentModal.assessment ||
      !abortAssessmentModal.reason.trim()
    ) {
      showNotification(
        "Missing required field",
        "Please provide a reason for aborting the assessment.",
        "error"
      );
      return;
    }

    setIsAbortingAssessment(true);
    try {
      await dispatch(
        updateAssessmentStatus({
          id: abortAssessmentModal.assessment.id,
          status: "aborted",
          reason: abortAssessmentModal.reason.trim(),
        })
      ).unwrap();

      showNotification(
        "Assessment aborted",
        "The assessment has been successfully aborted."
      );

      handleCloseAbortAssessmentModal();

      // Refetch assessments for the current tab
      const apiStatus = getApiStatus("aborted");
      dispatch(
        fetchAssessments({
          sort_by: "created_at",
          sort_order: "desc",
          status: apiStatus as any,
        })
      );

      setActiveAssessmentStatusTab("aborted");
      setAbortedKindTab("formal");
      const abortParams = new URLSearchParams(searchParams);
      abortParams.set("assessmentTab", "aborted");
      abortParams.set("abortedKind", "formal");
      setSearchParams(abortParams);
    } catch (error: any) {
      const message =
        typeof error === "string"
          ? error
          : error?.message || "Unable to abort the assessment.";
      showNotification("Abort failed", message, "error");
    } finally {
      setIsAbortingAssessment(false);
    }
  };

  // Fetch organization levels on mount
  useEffect(() => {
    if (organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, organizationLevels.length]);

  useEffect(() => {
    if (createAssessmentSuccess) {
      dispatch(clearCreateAssessmentSuccess());
    }
  }, [createAssessmentSuccess, dispatch]);

  // Map status tab to API status - directly use API status values
  const getApiStatus = (statusTab: string): string => {
    switch (statusTab) {
      case "pending":
        return "draft"; // Backend will return draft assessments
      case "ongoing":
        return "active";
      case "published":
        return "published"; // Backend should handle published status
      case "aborted":
        return "aborted";
      default:
        return "all-status";
    }
  };

  // Fetch assessments when assessments tab is active (not team-assessments hub)
  useEffect(() => {
    if (activeAssessmentStatusTab === "status") return;
    if (activeAssessmentStatusTab === "team-assessments") return;
    if (activeAssessmentStatusTab === "level-mix") return;

    // Keep in sync with assessments list (e.g. gap closes after re-assessment).
    dispatch(fetchDisconnectReview({ assessmentStatus: "all" }));

    if (activeAssessmentStatusTab === "disconnects") return;
    if (skipAssessmentsFetchRef.current) {
      skipAssessmentsFetchRef.current = false;
      return;
    }
    const apiStatus = getApiStatus(activeAssessmentStatusTab);
    dispatch(
      fetchAssessments({
        sort_by: "created_at",
        sort_order: "desc",
        status: apiStatus as any, // Backend may accept additional status values
      })
    );
  }, [dispatch, activeAssessmentStatusTab]);

  // Filter assessments based on search query and type filter
  const filteredAssessments = React.useMemo(() => {
    let filtered = [...assessmentsWithDisconnectEnrichment];

    // Apply type filter (short/long assessment)
    if (selectedTypeFilter !== "all") {
      filtered = filtered.filter(assessment => {
        const category = assessment.category?.toLowerCase() || "";
        const normalizedFilter = selectedTypeFilter.toLowerCase();

        // Only check for exact "short" or "long" values
        return category === normalizedFilter;
      });
    }

    if (activeAssessmentStatusTab === "published") {
      filtered = filtered.filter(assessment =>
        matchesAssessmentKindTab(
          {
            assessment_type: assessment.assessment_type,
            notes: assessment.notes,
          },
          publishedKindTab,
          "published"
        )
      );
    }

    if (activeAssessmentStatusTab === "aborted") {
      filtered = filtered.filter(assessment =>
        matchesAssessmentKindTab(
          {
            assessment_type: assessment.assessment_type,
            notes: assessment.notes,
          },
          abortedKindTab,
          "aborted"
        )
      );
    }

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(assessment => {
        // Search in assessment name
        const nameMatch = assessment.name?.toLowerCase().includes(query);

        // Search in assessment description
        const descriptionMatch = assessment.description
          ?.toLowerCase()
          .includes(query);

        // Search in account names
        const accountNamesMatch = assessment.accounts?.some(account =>
          (account.name || account.account_name || "")
            .toLowerCase()
            .includes(query)
        );

        // Search in account codes
        const accountCodesMatch = assessment.accounts?.some(account =>
          (account.account_code || "").toLowerCase().includes(query)
        );

        // Search in created by name
        const createdByMatch = (
          assessment.created_by_user?.name ||
          assessment.createdBy ||
          ""
        )
          .toLowerCase()
          .includes(query);

        return (
          nameMatch ||
          descriptionMatch ||
          accountNamesMatch ||
          accountCodesMatch ||
          createdByMatch
        );
      });
    }

    return filtered;
  }, [
    assessmentsWithDisconnectEnrichment,
    searchQuery,
    selectedTypeFilter,
    activeAssessmentStatusTab,
    publishedKindTab,
    abortedKindTab,
  ]);

  const publishedOverrideEligibleKeys = React.useMemo(() => {
    if (activeAssessmentStatusTab !== "published") return undefined;
    const kindScoped = assessmentsWithDisconnectEnrichment.filter(assessment =>
      matchesAssessmentKindTab(
        {
          assessment_type: assessment.assessment_type,
          notes: assessment.notes,
        },
        publishedKindTab,
        "published"
      )
    );
    return buildPublishedOverrideEligibleKeys(kindScoped);
  }, [
    activeAssessmentStatusTab,
    assessmentsWithDisconnectEnrichment,
    publishedKindTab,
  ]);

  const disconnectAssessmentsList = React.useMemo(() => {
    const rows = disconnectReview?.accounts ?? [];
    if (!rows.length) return [];

    const query = searchQuery.trim().toLowerCase();
    const grouped = new Map<string, DisconnectReviewAccount[]>();

    rows.forEach(row => {
      if (!matchesDisconnectSubTab(row, disconnectSubTab)) return;
      if (
        !matchesDisconnectKindTab(
          row.assessmentType,
          disconnectKindTab,
          row.assessmentOrigin,
          row.assessmentTitle
        )
      ) {
        return;
      }
      if (
        selectedTypeFilter !== "all" &&
        row.deltaTier !== selectedTypeFilter
      ) {
        return;
      }
      if (
        query &&
        !row.assessmentTitle.toLowerCase().includes(query) &&
        !row.accountName.toLowerCase().includes(query) &&
        !(row.assessmentDescription || "").toLowerCase().includes(query)
      ) {
        return;
      }

      const existing = grouped.get(row.assessmentId) ?? [];
      existing.push(row);
      grouped.set(row.assessmentId, existing);
    });

    return Array.from(grouped.entries())
      .map(([assessmentId, accountRows]) => {
        const accounts = accountRows.map(row => ({
          ...mapDisconnectReviewAccountToAssessmentAccount(row),
          hubPublished: row.hubPublished ?? false,
        }));
        const first = accountRows[0];
        const hubPublishedStatus =
          resolveDisconnectCardHubPublishedStatus(accountRows);
        const publishedAt = accountRows
          .map(resolveDisconnectPublishedAt)
          .filter((value): value is string => Boolean(value))
          .sort()
          .at(-1);
        const disconnectDueDate = accountRows
          .map(row => row.resolution?.due_date)
          .filter((value): value is string => Boolean(value))
          .sort()[0];
        const participantCount = accountRows.reduce(
          (sum, row) => sum + (row.sponsorAssessments?.length ?? 0),
          0
        );

        return {
          id: assessmentId,
          name: first.assessmentTitle,
          assessment_type: first.assessmentType ?? undefined,
          description: first.assessmentDescription?.trim() || undefined,
          template_id: "",
          template_name: "",
          status: "completed",
          progress: 0,
          participants: participantCount,
          total_responses: accounts.reduce(
            (sum, account) => sum + (account.responses ?? 0),
            0
          ),
          total_questions: 0,
          due_on: first.assessmentDueDate ?? undefined,
          dueDate: first.assessmentDueDate ?? undefined,
          created_at: first.assessmentCreatedAt ?? undefined,
          completed_at: first.assessmentCompletedAt ?? undefined,
          published_at: publishedAt ?? null,
          updated_at: first.resolution?.resolved_at ?? "",
          created_by: {
            id: "",
            name: first.createdByName?.trim() || "System",
            email: "",
          },
          accounts,
          category: "short",
          createdBy: first.createdByName?.trim() || "System",
          hubPublishedStatus,
          disconnectDueDate,
        } as AssessmentWithAccounts & {
          hubPublishedStatus?: string;
          disconnectDueDate?: string;
        };
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [
    disconnectReview,
    disconnectSubTab,
    disconnectKindTab,
    searchQuery,
    selectedTypeFilter,
  ]);

  const {
    items: hubOrgAccounts,
    loading: hubOrgLoading,
    error: hubOrgError,
  } = useAppSelector(state => state.customerWorkspace.orgPortfolioAccounts);

  useEffect(() => {
    if (activeAssessmentStatusTab !== "status") return;
    dispatch(fetchPortfolioAccounts("org"));
  }, [dispatch, activeAssessmentStatusTab]);

  const assessmentStatusTabs = [
    {
      id: "status",
      label: "Status",
      icon: <ListChecks size={18} />,
    },
    {
      id: "team-assessments",
      label: "Team Assessments",
      icon: <ClipboardList size={18} />,
      isHide: isCLevel,
    },
    {
      id: "pending",
      label: "Draft",
      icon: <FileText size={18} />,
    },
    {
      id: "ongoing",
      label: "Ongoing",
      icon: <Clock size={18} />,
    },
    {
      id: "aborted",
      label: "Aborted",
      icon: <Ban size={18} />,
    },
    {
      id: "disconnects",
      label: "Disconnects",
      icon: <AlertTriangle size={18} />,
    },
    {
      id: "published",
      label: "Published",
      icon: <Archive size={18} />,
    },
    {
      id: "level-mix",
      label: "Levels Mix",
      icon: <BarChart3 size={18} />,
    },
  ];

  const publishedKindTabs = [
    {
      id: "team",
      label: "Team assessments",
      icon: <User size={18} />,
    },
    {
      id: "formal",
      label: "Formal assessments",
      icon: <FileText size={18} />,
    },
  ];

  const abortedKindTabs = publishedKindTabs;

  const disconnectKindTabs = [
    {
      id: "team",
      label: "Team Disconnect",
      icon: <User size={18} />,
    },
    {
      id: "formal",
      label: "Formal Disconnect",
      icon: <FileText size={18} />,
    },
  ];

  // Toggle assessment expansion for any status tab
  const toggleAssessmentExpansion = React.useCallback(
    (assessmentId: string, statusTab: string) => {
      setExpandedAssessments(prev => {
        const currentSet = prev[statusTab];
        const isCurrentlyExpanded = currentSet?.has(assessmentId) || false;

        // Create a completely new Set to ensure React detects the change
        const newSet = new Set<string>(currentSet || []);

        if (isCurrentlyExpanded) {
          newSet.delete(assessmentId);
        } else {
          newSet.add(assessmentId);
        }

        // Return new object with new Set to trigger re-render
        return {
          ...prev,
          [statusTab]: newSet,
        };
      });
    },
    []
  );

  // Handle view full report
  const handleViewFullReport = async (
    assessment: AssessmentWithAccounts,
    account: AssessmentAccount
  ) => {
    const data = await loadFullReportData(dispatch, assessment, account);
    setFullReportData(data);
    setShowFullReportModal(true);
  };

  const openPortfolioFullReport = async (account: {
    account_id: string;
    report_assessment_id?: string | null;
  }) => {
    if (!account.report_assessment_id) return;
    let assessment = assessmentsWithDisconnectEnrichment.find(
      item => String(item.id) === String(account.report_assessment_id)
    );
    if (!assessment) {
      const response = await dispatch(fetchAssessments({})).unwrap();
      const mapped = (
        (response as { assessments?: any[] }).assessments || []
      ).map(mapApiAssessmentToAssessmentWithAccounts);
      assessment = mapped.find(
        item => String(item.id) === String(account.report_assessment_id)
      );
    }
    const match = assessment?.accounts?.find(
      item => String(item.id || item.account_id) === String(account.account_id)
    );
    if (assessment && match) {
      await handleViewFullReport(assessment, match);
    }
  };

  // Handle open override modal
  const handleOpenOverrideModal = (
    assessment: AssessmentWithAccounts,
    account: AssessmentAccount
  ) => {
    const currentLevel =
      account.published_level ||
      account.override?.override_level ||
      getAccountLevel(account, "final") ||
      getAccountLevel(account, "median") ||
      "";

    const levelCode = /\d/.test(currentLevel)
      ? toStoredLevelCode(currentLevel)
      : currentLevel;
    setOverrideModal({
      isOpen: true,
      assessmentId: assessment.id,
      accountId: account.id || account.account_id || null,
      currentValue: currentLevel,
      publishOnSave:
        activeAssessmentStatusTab === "ongoing" ||
        activeAssessmentStatusTab === "disconnects",
    });
    setOverrideValue(levelCode);
  };

  // Handle save override
  const handleSaveOverride = async () => {
    if (
      !overrideValue ||
      !overrideRationale.trim() ||
      !overrideModal.assessmentId ||
      !overrideModal.accountId
    )
      return;

    try {
      const levelCode = toStoredLevelCode(overrideValue);

      await dispatch(
        overrideAccountLevel({
          assessmentId: overrideModal.assessmentId,
          accountId: overrideModal.accountId,
          overrideLevel: levelCode,
          rationale: overrideRationale.trim(),
          publishAccount: Boolean(overrideModal.publishOnSave),
        })
      ).unwrap();

      const overrideAssessment = assessmentsWithDisconnectEnrichment.find(
        a =>
          a.id === overrideModal.assessmentId ||
          (a as { assessment_id?: string }).assessment_id ===
            overrideModal.assessmentId
      );
      const overrideAccount = overrideAssessment?.accounts?.find(
        acc => getAssessmentAccountId(acc) === overrideModal.accountId
      );
      const overrideAccountName =
        overrideAccount?.name?.trim() ||
        overrideAccount?.account_name?.trim() ||
        "this account";
      const overrideLevelLabel = getLevelDisplayName(levelCode);

      if (overrideModal.publishOnSave) {
        await refreshAssessmentsAfterPublish({ includeDisconnectReview: true });
        setForcePublishSuccessInfo({
          kind: "level_override_publish",
          accountId: overrideModal.accountId,
          accountName: overrideAccountName,
          levelLabel: overrideLevelLabel,
          dateLabel: formatAssessmentSubmitSavedOnDate(),
          assessmentTitle: overrideAssessment?.name ?? null,
        });
      } else {
        const apiStatus = getApiStatus(activeAssessmentStatusTab);
        dispatch(
          fetchAssessments({
            sort_by: "created_at",
            sort_order: "desc",
            status: apiStatus as any,
          })
        );
        setForcePublishSuccessInfo({
          kind: "published_level_override",
          accountId: overrideModal.accountId,
          accountName: overrideAccountName,
          levelLabel: overrideLevelLabel,
          dateLabel: formatAssessmentSubmitSavedOnDate(),
          assessmentTitle: overrideAssessment?.name ?? null,
        });
      }

      setOverrideModal({
        isOpen: false,
        assessmentId: null,
        accountId: null,
        currentValue: null,
        publishOnSave: false,
      });
      setOverrideValue("");
      setOverrideRationale("");
    } catch (error: any) {
      console.error("Error saving override:", error);
      toast.error(error || "Unable to update the final level.");
    }
  };

  // Handle export report
  const handleExportReport = (reportData: FullReportData | null) => {
    exportFullReportCsv(reportData);
  };

  // Handle assessment status tab change
  const handleAssessmentStatusTabChange = (tabId: string) => {
    setActiveAssessmentStatusTab(tabId);
    if (tabId === "disconnects") {
      setSelectedTypeFilter("all");
      setDisconnectSubTab("ongoing");
      setSelectedDisconnectKeys(new Set());
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set("assessmentTab", tabId);
    if (tabId === "published") {
      newParams.set("publishedKind", publishedKindTab);
    } else {
      newParams.delete("publishedKind");
    }
    if (tabId === "aborted") {
      newParams.set("abortedKind", abortedKindTab);
    } else {
      newParams.delete("abortedKind");
    }
    if (tabId === "disconnects") {
      newParams.set("disconnectKind", disconnectKindTab);
    } else {
      newParams.delete("disconnectKind");
    }
    if (tabId === "level-mix") {
      newParams.set("mix", levelMixScopeTab);
    } else {
      newParams.delete("mix");
    }
    setSearchParams(newParams);
  };

  const handleLevelMixScopeChange = (scope: LevelMixScope) => {
    setLevelMixScopeTab(scope);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("assessmentTab", "level-mix");
    newParams.set("mix", scope);
    setSearchParams(newParams);
  };

  const handlePublishedKindTabChange = (tabId: string) => {
    const nextKind = tabId === "formal" ? "formal" : "team";
    setPublishedKindTab(nextKind);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("assessmentTab", "published");
    newParams.set("publishedKind", nextKind);
    setSearchParams(newParams);
  };

  const handleAbortedKindTabChange = (tabId: string) => {
    const nextKind = tabId === "formal" ? "formal" : "team";
    setAbortedKindTab(nextKind);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("assessmentTab", "aborted");
    newParams.set("abortedKind", nextKind);
    setSearchParams(newParams);
  };

  const handleDisconnectKindTabChange = (nextKind: AssessmentKindTab) => {
    setDisconnectKindTab(nextKind);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("assessmentTab", "disconnects");
    newParams.set("disconnectKind", nextKind);
    setSearchParams(newParams);
  };

  const handleToggleDisconnectAccount = React.useCallback(
    (assessmentId: string, accountId: string) => {
      const key = getDisconnectSelectionKey(assessmentId, accountId);
      setSelectedDisconnectKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    []
  );

  const handleToggleAllDisconnectAccounts = React.useCallback(
    (assessmentId: string, accountIds: string[], selectAll: boolean) => {
      setSelectedDisconnectKeys(prev => {
        const next = new Set(prev);
        accountIds.forEach(accountId => {
          const key = getDisconnectSelectionKey(assessmentId, accountId);
          if (selectAll) next.add(key);
          else next.delete(key);
        });
        return next;
      });
    },
    []
  );

  const handleClearDisconnectSelection = React.useCallback(() => {
    setSelectedDisconnectKeys(new Set());
  }, []);

  const getSelectedDisconnectRows =
    React.useCallback((): DisconnectReviewAccount[] => {
      return Array.from(selectedDisconnectKeys)
        .map(key => {
          const reviewRow = disconnectReview?.accounts?.find(
            account =>
              getDisconnectSelectionKey(
                account.assessmentId,
                account.accountId
              ) === key
          );
          if (reviewRow) return reviewRow;

          const [assessmentId, accountId] = key.split("::");
          if (!assessmentId || !accountId) return null;

          const assessment = assessmentsWithDisconnectEnrichment.find(
            item => item.id === assessmentId
          );
          const account = assessment?.accounts?.find(
            item => getAssessmentAccountId(item) === accountId
          );
          if (account?.disconnectReviewRow) return account.disconnectReviewRow;
          if (
            !assessment ||
            !account ||
            !canSelectCompletedDisconnectAccount(account)
          ) {
            return null;
          }

          return buildDisconnectReviewFromAssessmentAccount(
            assessment,
            account
          );
        })
        .filter((row): row is DisconnectReviewAccount => !!row);
    }, [
      assessmentsWithDisconnectEnrichment,
      disconnectReview?.accounts,
      selectedDisconnectKeys,
    ]);

  const handleOpenDisconnectTriggerModal = React.useCallback(() => {
    if (selectedDisconnectKeys.size === 0) return;

    const rows = getSelectedDisconnectRows();
    if (!rows.length) {
      toast.error("Selected accounts are no longer available.");
      return;
    }

    setDisconnectTriggerAccounts(
      rows.map(
        account =>
          JSON.parse(JSON.stringify(account)) as DisconnectReviewAccount
      )
    );
    setDisconnectTriggerDueDate(
      buildDefaultDisconnectDueDate(disconnectReview?.defaultDueDateDays)
    );
    setDisconnectTriggerContextNote(
      buildDisconnectContextNote(rows.map(row => row.accountName))
    );
    setIsDisconnectTriggerModalOpen(true);
  }, [
    disconnectReview?.defaultDueDateDays,
    getSelectedDisconnectRows,
    selectedDisconnectKeys.size,
  ]);

  const handleCloseDisconnectTriggerModal = React.useCallback(() => {
    setIsDisconnectTriggerModalOpen(false);
  }, []);

  const handleConfirmDisconnectTrigger = React.useCallback(async () => {
    if (!disconnectTriggerAccounts.length || !disconnectTriggerDueDate) return;

    try {
      const result = await dispatch(
        triggerDisconnectWorkflow({
          dueDate: disconnectTriggerDueDate,
          contextNote: disconnectTriggerContextNote.trim(),
          accounts: disconnectTriggerAccounts.map(
            buildTriggerDisconnectAccountPayload
          ),
        })
      ).unwrap();

      toast.success(result.message || "Disconnect workflow triggered.");
      setIsDisconnectTriggerModalOpen(false);
      setSelectedDisconnectKeys(new Set());
      if (activeAssessmentStatusTab === "completed") {
        dispatch(
          fetchAssessments({
            sort_by: "created_at",
            sort_order: "desc",
            status: "completed",
          })
        );
      } else if (activeAssessmentStatusTab === "ongoing") {
        dispatch(
          fetchAssessments({
            sort_by: "created_at",
            sort_order: "desc",
            status: "active",
          })
        );
      } else {
        setDisconnectSubTab("ongoing");
      }
      dispatch(fetchDisconnectReview({ assessmentStatus: "all" }));
    } catch (error: unknown) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to trigger disconnect workflow."
      );
    }
  }, [
    activeAssessmentStatusTab,
    disconnectSubTab,
    dispatch,
    disconnectTriggerAccounts,
    disconnectTriggerContextNote,
    disconnectTriggerDueDate,
  ]);

  const handleOpenResolutionReview = React.useCallback(
    async (assessment: AssessmentWithAccounts, account: AssessmentAccount) => {
      const accountId = getAssessmentAccountId(account);
      const selectionKey = getDisconnectSelectionKey(assessment.id, accountId);
      let reviewRow: DisconnectReviewAccount | null =
        account.disconnectReviewRow ?? null;

      try {
        const review = await dispatch(
          fetchDisconnectReview({ assessmentStatus: "all" })
        ).unwrap();
        reviewRow =
          review.accounts?.find(
            row =>
              getDisconnectSelectionKey(row.assessmentId, row.accountId) ===
              selectionKey
          ) ?? reviewRow;
      } catch (error: unknown) {
        toast.error(
          typeof error === "string"
            ? error
            : "Failed to load resolution details."
        );
        return;
      }

      const agreedLevelMissing = readReviewRowAgreedLevel(reviewRow) === null;
      const resolvedByMissing =
        !reviewRow?.resolvedByName && !reviewRow?.resolution?.resolvedByName;

      if (
        (agreedLevelMissing || resolvedByMissing) &&
        (account.resolutionId || reviewRow?.resolutionId)
      ) {
        try {
          const resolutions = await dispatch(
            fetchDisconnectResolutions({
              assessmentId: assessment.id,
              accountId,
              status: "resolved",
            })
          ).unwrap();
          const resolutionId = account.resolutionId || reviewRow?.resolutionId;
          const detail =
            resolutions.resolutions?.find(item => item.id === resolutionId) ??
            resolutions.resolutions?.[0];
          if (detail) {
            reviewRow = reviewRow
              ? mergeResolutionDetailIntoReviewRow(reviewRow, detail)
              : buildReviewRowFromResolutionDetail(assessment, account, detail);
          }
        } catch {
          // Best-effort: still open with disconnect-review row if present.
        }
      }

      if (!reviewRow) {
        toast.error("Resolution details not found for this account.");
        return;
      }

      setResolutionReviewModal({
        isOpen: true,
        assessment,
        account: { ...account, disconnectReviewRow: reviewRow },
      });
    },
    [dispatch]
  );

  const handleCloseResolutionReview = React.useCallback(() => {
    setResolutionReviewModal({
      isOpen: false,
      assessment: null,
      account: null,
    });
  }, []);

  const handleAcceptResolution = React.useCallback(async () => {
    const reviewRow = resolutionReviewModal.account?.disconnectReviewRow;
    const assessment = resolutionReviewModal.assessment;

    if (!reviewRow?.resolutionId) return;

    setIsAcceptingResolution(true);
    try {
      await dispatch(
        publishPostResolution({
          resolutionId: reviewRow.resolutionId,
        })
      ).unwrap();

      const agreedLevel = readResolutionAgreedLevel(reviewRow);
      const levelLabel =
        agreedLevel != null ? getLevelDisplayName(String(agreedLevel)) : null;

      handleCloseResolutionReview();
      setForcePublishSuccessInfo({
        kind: "disconnect_force_publish",
        accountId: reviewRow.accountId,
        accountName: reviewRow.accountName || "the account",
        levelLabel,
        dateLabel: formatAssessmentSubmitSavedOnDate(),
        isSelfDisconnect: assessment
          ? isSelfAssessmentType(assessment.assessment_type)
          : isSelfAssessmentType(reviewRow.assessmentType),
      });
      await refreshAssessmentsAfterPublish({ includeDisconnectReview: true });
    } catch (error: unknown) {
      toast.error(
        typeof error === "string" ? error : "Failed to publish resolution"
      );
    } finally {
      setIsAcceptingResolution(false);
    }
  }, [
    dispatch,
    getLevelDisplayName,
    handleCloseResolutionReview,
    refreshAssessmentsAfterPublish,
    resolutionReviewModal,
  ]);

  // Helper function to get expanded state based on status tab
  const getExpandedState = React.useCallback(
    (assessmentId: string, statusTab: string): boolean => {
      const statusSet = expandedAssessments[statusTab];
      return statusSet ? statusSet.has(assessmentId) : false;
    },
    [expandedAssessments]
  );

  const closeIndividualResponseModal = React.useCallback(() => {
    setIndividualResponseModal({ ...EMPTY_INDIVIDUAL_RESPONSE_MODAL });
  }, []);

  const handleOpenStatisticsModal = React.useCallback(
    (assessment: AssessmentWithAccounts) => {
      setStatisticsAssessment(assessment);
      setStatisticsProgressTab("completed");
      setShowStatisticsModal(true);
    },
    []
  );

  const wizardEditingAssessment = useMemo(() => {
    if (!editingAssessment) return null;

    return {
      id: editingAssessment.id,
      name: editingAssessment.name || "",
      status: editingAssessment.status || undefined,
      description: editingAssessment.description || undefined,
      dueDate: editingAssessment.dueDate || undefined,
      due_on: editingAssessment.due_on || undefined,
      template_id: editingAssessment.template_id || undefined,
      target_accounts: editingAssessment.target_accounts || undefined,
      accounts: editingAssessment.accounts || undefined,
      category: editingAssessment.category || undefined,
      target_questionnaires:
        editingAssessment.target_questionnaires || undefined,
      questions_data: editingAssessment.questions_data || undefined,
    };
  }, [editingAssessment]);

  const handleOpenConsolidatedReportModal = React.useCallback(
    (assessment: AssessmentWithAccounts) => {
      setConsolidatedReportModal({
        isOpen: true,
        assessment,
      });
    },
    []
  );

  // Render assessments list using reusable component
  const renderAssessmentsList = (
    assessmentsList: AssessmentWithAccounts[],
    statusTab: "pending" | "ongoing" | "completed" | "published" | "aborted",
    options?: {
      hideToolbar?: boolean;
      publishedOverrideEligibleKeys?: Set<string>;
    }
  ) => {
    return (
      <div className="w-full">
        {!options?.hideToolbar && (
          <AssessmentToolbar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            typeFilterValue={selectedTypeFilter}
            onTypeFilterChange={setSelectedTypeFilter}
          />
        )}

        {isLoadingAssessments ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading assessments...</p>
          </div>
        ) : assessmentsList.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] py-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-base font-medium text-gray-600">
              {searchQuery.trim() || selectedTypeFilter !== "all"
                ? `No ${statusTab} assessments match your search or filter criteria.`
                : statusTab === "published"
                  ? publishedKindTab === "team"
                    ? "No published team assessments found."
                    : "No published formal assessments found."
                  : statusTab === "aborted"
                    ? abortedKindTab === "team"
                      ? "No aborted team assessments found."
                      : "No aborted formal assessments found."
                    : `No ${statusTab} assessments found.`}
            </p>
            {(searchQuery.trim() || selectedTypeFilter !== "all") && (
              <p className="text-sm text-gray-500 mt-2">
                Try adjusting your search or filter to see more results.
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {assessmentsList.map((assessment, index) => {
              const assessmentId =
                assessment.id ||
                (assessment as any).assessment_id ||
                `assessment-${index}`;
              const resolvedCompletedView =
                completedAccountViewByAssessment[assessmentId] ||
                getDefaultCompletedAccountView(assessment);

              return (
                <AssessmentCardComponent
                  key={assessmentId}
                  assessment={assessment}
                  isExpanded={getExpandedState(assessmentId, statusTab)}
                  onToggleExpand={() =>
                    toggleAssessmentExpansion(assessmentId, statusTab)
                  }
                  onViewStatistics={handleOpenStatisticsModal}
                  onViewReport={
                    statusTab === "published"
                      ? handleOpenConsolidatedReportModal
                      : undefined
                  }
                  onEdit={
                    statusTab === "pending" || statusTab === "ongoing"
                      ? handleOpenEditAssessmentModal
                      : undefined
                  }
                  onDelete={
                    statusTab === "pending"
                      ? handleOpenDeleteAssessmentModal
                      : undefined
                  }
                  onLaunch={
                    statusTab === "pending" ? handleLaunchAssessment : undefined
                  }
                  isLaunching={
                    statusTab === "pending" &&
                    launchingAssessmentId === assessmentId
                  }
                  hubPublishSelectionEnabled={
                    (statusTab === "completed" || statusTab === "ongoing") &&
                    resolvedCompletedView === "aligned" &&
                    hasPublishableAlignedAccounts(assessment)
                  }
                  selectedHubPublishAccountIds={getHubPublishSelection(
                    assessmentId
                  )}
                  onToggleHubPublishAccount={handleToggleHubPublishAccount}
                  onToggleAllHubPublishAccounts={
                    handleToggleAllHubPublishAccounts
                  }
                  completedDisconnectSelectionEnabled={
                    (statusTab === "completed" || statusTab === "ongoing") &&
                    resolvedCompletedView === "disconnects" &&
                    hasSelectableCompletedDisconnectAccounts(assessment)
                  }
                  selectedDisconnectAccountKeys={selectedDisconnectKeys}
                  onToggleDisconnectAccount={handleToggleDisconnectAccount}
                  onToggleAllDisconnectAccounts={
                    handleToggleAllDisconnectAccounts
                  }
                  completedAccountView={resolvedCompletedView}
                  onCompletedAccountViewChange={view => {
                    setCompletedAccountViewByAssessment(prev => ({
                      ...prev,
                      [assessmentId]: view,
                    }));
                  }}
                  onPublishAssessment={
                    statusTab === "ongoing"
                      ? handleOpenForcePublishConfirm
                      : undefined
                  }
                  isPublishing={
                    statusTab === "ongoing" &&
                    publishingAssessmentId === assessmentId
                  }
                  onViewFullReport={handleViewFullReport}
                  onOpenReviewAssessment={handleOpenReviewAssessment}
                  onOpenOverrideModal={
                    canOverrideLevel &&
                    (statusTab === "published" || statusTab === "ongoing")
                      ? handleOpenOverrideModal
                      : undefined
                  }
                  onExtendDueDate={
                    statusTab === "ongoing"
                      ? handleOpenExtendDueDateModal
                      : undefined
                  }
                  onOpenExtendedReason={
                    statusTab === "ongoing"
                      ? handleOpenExtendedReasonModal
                      : undefined
                  }
                  onAbortAssessment={
                    statusTab === "ongoing" &&
                    isRole(userRole, ROLES.EXECUTIVE_SPONSOR_LEAD)
                      ? handleOpenAbortAssessmentModal
                      : undefined
                  }
                  publishedOverrideEligibleKeys={
                    statusTab === "published"
                      ? options?.publishedOverrideEligibleKeys
                      : undefined
                  }
                  statusTab={statusTab}
                  userRole={userRole}
                  getLevelDisplayName={getLevelDisplayName}
                  getStatusDisplayName={getStatusDisplayName}
                  isShortCategory={isShortCategory}
                  shouldShowLevelMetrics={shouldShowLevelMetrics}
                  formatDueDate={formatDueDate}
                  getAccountLevel={getAccountLevel}
                  getAccountResponseCounts={getAccountResponseCounts}
                  isOverdue={isOverdue}
                  disconnectAcceptEnabled={
                    (statusTab === "completed" || statusTab === "ongoing") &&
                    resolvedCompletedView === "disconnects"
                  }
                  onOpenResolutionReview={handleOpenResolutionReview}
                />
              );
            })}
          </div>
        )}

        {(statusTab === "completed" || statusTab === "ongoing") &&
          selectedDisconnectKeys.size > 0 && (
            <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-wrap items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-lg">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-900">
                <AlertTriangle className="h-4 w-4 shrink-0 text-gray-900" />
                {selectedDisconnectKeys.size} account
                {selectedDisconnectKeys.size === 1 ? "" : "s"} selected
              </span>
              <Button
                variant="primary"
                onClick={handleOpenDisconnectTriggerModal}
              >
                <span className="inline-flex items-center gap-2">
                  Trigger Disconnect Workflow
                  <ArrowRight size={16} />
                </span>
              </Button>
              <button
                type="button"
                onClick={handleClearDisconnectSelection}
                className="border-0 bg-transparent p-0 text-sm font-medium text-gray-900 hover:text-gray-600"
              >
                Clear
              </button>
            </div>
          )}
      </div>
    );
  };

  const renderDisconnectsList = () => {
    const isLoading = isLoadingDisconnectReview;
    const disconnectCounts =
      disconnectReview?.counts?.[disconnectSubTab] ?? null;

    return (
      <div className="w-full">
        <div className="mb-4 bg-white rounded-lg border border-gray-200 px-4 pt-2 flex items-end justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1 overflow-x-auto">
            <TabsComponent
              tabs={disconnectKindTabs}
              activeTab={disconnectKindTab}
              onTabChange={handleDisconnectKindTabChange}
              variant="tab-underline"
            />
          </div>
          <AssessmentToolbar
            className="pb-2"
            searchPlaceholder="Search assessments or accounts..."
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            typeFilterValue={selectedTypeFilter}
            onTypeFilterChange={setSelectedTypeFilter}
            typeFilterOptions={DISCONNECT_TYPE_OPTIONS}
            trailingContent={
              <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-1">
                {(
                  [
                    {
                      id: "ongoing" as const,
                      label: "Ongoing",
                      icon: <ClipboardList size={14} />,
                    },
                    {
                      id: "logs" as const,
                      label: "Logs",
                      icon: <Archive size={14} />,
                    },
                  ] as const
                ).map(tab => {
                  const isActive = disconnectSubTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => {
                        setDisconnectSubTab(tab.id);
                        setSelectedDisconnectKeys(new Set());
                      }}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-all ${
                        isActive
                          ? "border border-[#974ae4] bg-white text-[#7a36c4] shadow-sm"
                          : "border border-transparent bg-transparent text-gray-600 hover:bg-white hover:text-gray-800"
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            }
          />
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
          <DisconnectKpiCard
            value={disconnectCounts?.strong_disconnect ?? 0}
            label="Strong disconnect"
            valueClassName="text-red-600"
            loading={isLoading}
          />
          <DisconnectKpiCard
            value={disconnectCounts?.soft_disconnect ?? 0}
            label="Soft disconnect"
            valueClassName="text-orange-500"
            loading={isLoading}
          />
          <DisconnectKpiCard
            value={disconnectCounts?.not_triggered ?? 0}
            label="Not triggered"
            valueClassName="text-emerald-600"
            loading={isLoading}
          />
        </div>

        {disconnectReviewError ? (
          <p className="mb-4 text-sm text-red-600">{disconnectReviewError}</p>
        ) : null}

        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading disconnects...</p>
          </div>
        ) : disconnectReviewError ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] py-12">
            <AlertTriangle className="w-8 h-8 text-red-500 mb-3" />
            <p className="text-base font-medium text-gray-900">
              Unable to load disconnect review
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {disconnectReviewError}
            </p>
          </div>
        ) : disconnectAssessmentsList.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] py-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-base font-medium text-gray-600">
              {searchQuery.trim() || selectedTypeFilter !== "all"
                ? "No disconnects match your search or filter criteria."
                : "No disconnects found for this view."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {disconnectAssessmentsList.map((assessment, index) => {
              const assessmentId =
                assessment.id ||
                (assessment as any).assessment_id ||
                `disconnect-assessment-${index}`;

              return (
                <AssessmentCardComponent
                  key={assessmentId}
                  assessment={assessment}
                  isExpanded={getExpandedState(assessmentId, "disconnects")}
                  onToggleExpand={() =>
                    toggleAssessmentExpansion(assessmentId, "disconnects")
                  }
                  onViewStatistics={handleOpenStatisticsModal}
                  onViewReport={handleOpenConsolidatedReportModal}
                  onViewFullReport={handleViewFullReport}
                  onOpenOverrideModal={
                    canOverrideLevel && disconnectSubTab === "ongoing"
                      ? handleOpenOverrideModal
                      : undefined
                  }
                  disconnectSelectionEnabled={false}
                  selectedDisconnectAccountKeys={selectedDisconnectKeys}
                  onToggleDisconnectAccount={handleToggleDisconnectAccount}
                  onToggleAllDisconnectAccounts={
                    handleToggleAllDisconnectAccounts
                  }
                  disconnectAcceptEnabled={disconnectSubTab === "ongoing"}
                  onOpenResolutionReview={handleOpenResolutionReview}
                  statusTab="disconnects"
                  disconnectSubTab={disconnectSubTab}
                  disconnectAssessmentKind={disconnectKindTab}
                  userRole={userRole}
                  getLevelDisplayName={getLevelDisplayName}
                  getStatusDisplayName={getStatusDisplayName}
                  isShortCategory={isShortCategory}
                  shouldShowLevelMetrics={shouldShowLevelMetrics}
                  formatDueDate={formatDueDate}
                  getAccountLevel={getAccountLevel}
                  getAccountResponseCounts={getAccountResponseCounts}
                  isOverdue={isOverdue}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="assessments-main-container">
      <div className="assessments-wrapper p-6">
        <div className="assessments-container">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h1 className="text-xl font-bold text-gray-900">
                  Assessment Hub
                </h1>
                <AssessmentCalendarStatusBadge showPersonalDates={false} />
              </div>
              <p className="text-sm text-gray-600 font-normal">
                Manage and track assessments across your portfolio
              </p>
            </div>
            <div className="ml-4">
              <Button
                variant="primary"
                onClick={() => setIsWizardOpen(true)}
                icon={<FileText size={16} />}
              >
                Create Assessment
              </Button>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="mb-6">
            <TabsComponent
              tabs={assessmentStatusTabs}
              activeTab={activeAssessmentStatusTab}
              onTabChange={handleAssessmentStatusTabChange}
              variant="tab-underline"
              className="border-b"
            />
          </div>
          {activeAssessmentStatusTab === "status" ? (
            hubOrgLoading ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white px-16 py-16 text-center text-base text-gray-500">
                Loading org status…
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <PortfolioAccountsStatusPanel
                  accounts={hubOrgAccounts}
                  error={hubOrgError}
                  mode="org"
                  allowInProgressReport={allowInProgressReport}
                  onViewFullReport={openPortfolioFullReport}
                />
              </div>
            )
          ) : activeAssessmentStatusTab === "team-assessments" ? (
            <SelfAssessmentsHubTab
              renderCard={(item, isExpanded, onToggleExpand) => {
                const teamCard = toTeamAssessmentCard(item);
                const teamForcePublishReady = Boolean(item.cron_publish_failed);
                return (
                  <AssessmentCardComponent
                    assessment={teamCard}
                    isExpanded={isExpanded}
                    onToggleExpand={onToggleExpand}
                    onViewStatistics={handleOpenStatisticsModal}
                    onViewFullReport={handleViewFullReport}
                    onPublishAssessment={
                      teamForcePublishReady
                        ? handleOpenForcePublishConfirm
                        : undefined
                    }
                    forcePublishTitle={
                      teamForcePublishReady
                        ? "The publish cron failed. Force Publish the accounts that are still unpublished."
                        : undefined
                    }
                    isPublishing={publishingAssessmentId === item.assessment_id}
                    hubPublishSelectionEnabled={teamForcePublishReady}
                    selectedHubPublishAccountIds={getHubPublishSelection(
                      item.assessment_id
                    )}
                    onToggleHubPublishAccount={handleToggleHubPublishAccount}
                    onToggleAllHubPublishAccounts={
                      handleToggleAllHubPublishAccounts
                    }
                    statusTab="ongoing"
                    userRole={userRole}
                    getLevelDisplayName={getLevelDisplayName}
                    getStatusDisplayName={getStatusDisplayName}
                    isShortCategory={isShortCategory}
                    shouldShowLevelMetrics={shouldShowLevelMetrics}
                    formatDueDate={formatDueDate}
                    getAccountLevel={getAccountLevel}
                    getAccountResponseCounts={getAccountResponseCounts}
                    isOverdue={() => false}
                    hideCurrentUnassigned
                  />
                );
              }}
            />
          ) : activeAssessmentStatusTab === "pending" ? (
            renderAssessmentsList(filteredAssessments, "pending")
          ) : activeAssessmentStatusTab === "completed" ? (
            renderAssessmentsList(filteredAssessments, "completed")
          ) : activeAssessmentStatusTab === "disconnects" ? (
            renderDisconnectsList()
          ) : activeAssessmentStatusTab === "ongoing" ? (
            renderAssessmentsList(filteredAssessments, "ongoing")
          ) : activeAssessmentStatusTab === "published" ? (
            <>
              <div className="mb-4 bg-white rounded-lg border border-gray-200 px-4 pt-2 flex items-end justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1 overflow-x-auto">
                  <TabsComponent
                    tabs={publishedKindTabs}
                    activeTab={publishedKindTab}
                    onTabChange={handlePublishedKindTabChange}
                    variant="tab-underline"
                  />
                </div>
                <AssessmentToolbar
                  className="pb-2"
                  searchValue={searchQuery}
                  onSearchChange={setSearchQuery}
                  typeFilterValue={selectedTypeFilter}
                  onTypeFilterChange={setSelectedTypeFilter}
                />
              </div>
              {renderAssessmentsList(filteredAssessments, "published", {
                hideToolbar: true,
                publishedOverrideEligibleKeys,
              })}
            </>
          ) : activeAssessmentStatusTab === "aborted" ? (
            <>
              <div className="mb-4 bg-white rounded-lg border border-gray-200 px-4 pt-2 flex items-end justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1 overflow-x-auto">
                  <TabsComponent
                    tabs={abortedKindTabs}
                    activeTab={abortedKindTab}
                    onTabChange={handleAbortedKindTabChange}
                    variant="tab-underline"
                  />
                </div>
                <AssessmentToolbar
                  className="pb-2"
                  searchValue={searchQuery}
                  onSearchChange={setSearchQuery}
                  typeFilterValue={selectedTypeFilter}
                  onTypeFilterChange={setSelectedTypeFilter}
                />
              </div>
              {renderAssessmentsList(filteredAssessments, "aborted", {
                hideToolbar: true,
              })}
            </>
          ) : activeAssessmentStatusTab === "level-mix" ? (
            (() => {
              const loggedInUser = getFromLocalStorage(
                STORAGE_KEYS.LOGGED_IN_USER
              );
              const orgId =
                loggedInUser?.tenantDomain || loggedInUser?.orgId || null;
              return (
                <LevelMixContainer
                  orgId={orgId}
                  showPortfolioFilter={false}
                  allowedScopes={HUB_LEVEL_MIX_SCOPES}
                  defaultScope="formal"
                  initialMixScope={levelMixScopeTab}
                  onMixScopeChange={handleLevelMixScopeChange}
                />
              );
            })()
          ) : null}
        </div>
      </div>

      <Modal
        isOpen={forcePublishConfirmModal.isOpen}
        toggle={handleCloseForcePublishConfirm}
        title={
          forcePublishConfirmModal.accountIds.length > 0
            ? `Force Publish (${forcePublishConfirmModal.accountIds.length})`
            : "Force Publish"
        }
        size="xl"
      >
        <div>
          {forcePublishConfirmModal.accountIds.length > 0 ? (
            <p className="text-base text-gray-900 mb-4">
              Force publish{" "}
              <span className="font-semibold">
                {forcePublishConfirmModal.accountIds.length}
              </span>{" "}
              selected account
              {forcePublishConfirmModal.accountIds.length === 1
                ? ""
                : "s"} from{" "}
              <span className="font-semibold">
                {forcePublishConfirmModal.assessment?.name?.trim() ||
                  "this assessment"}
              </span>{" "}
              now? Selected aligned accounts will be published; selected
              disconnect accounts will start the disconnect flow. Unselected
              accounts stay on Ongoing and auto-publish on due+1.
            </p>
          ) : (
            <p className="text-base text-gray-900 mb-4">
              Publish{" "}
              <span className="font-semibold">
                {forcePublishConfirmModal.assessment?.name?.trim() ||
                  "this assessment"}
              </span>{" "}
              now without waiting for the due date? Aligned accounts will be
              published, disconnect accounts will start the disconnect flow, and
              accounts with no responses will keep their previous level or be
              set to Unknown.
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline-primary"
              onClick={handleCloseForcePublishConfirm}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmForcePublish}>
              {forcePublishConfirmModal.accountIds.length > 0
                ? `Force Publish (${forcePublishConfirmModal.accountIds.length})`
                : "Force Publish"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteAssessmentModal.isOpen}
        toggle={handleCloseDeleteAssessmentModal}
        title="Delete Assessment"
        size="xl"
      >
        <div>
          <p className="text-base text-gray-900 mb-4">
            Are you sure you want to delete this assessment?
          </p>
          <CommonInput
            label="Assessment Name"
            name="delete_assessment_name"
            value={deleteAssessmentModal.inputValue}
            onChange={() => undefined}
            readOnly
            disabled
          />
          <p className="text-red-600 text-sm mt-3 mb-6">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline-primary"
              onClick={handleCloseDeleteAssessmentModal}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteAssessment}>
              Delete Assessment
            </Button>
          </div>
        </div>
      </Modal>

      <ReviewAssessmentModal
        isOpen={previewModalOpen}
        templateData={templateData}
        onClose={handleCloseReviewModal}
        responses={assessmentResponses}
        onChange={handleReviewResponseChange}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmitAssessment}
        isSubmitting={isSubmittingReview}
        voiceMode={{
          enabled: voiceModeEnabled && speechSupported,
          supported: speechSupported,
          isListening,
          onToggle: handleToggleVoiceMode,
          showInstructions: voiceModeEnabled && speechSupported,
        }}
        currentQuestionIndex={currentQuestionIndex}
        onQuestionIndexChange={setCurrentQuestionIndex}
        isPreviewMode={isTemplatePreviewMode}
      />

      <ConsolidatedReportModal
        isOpen={consolidatedReportModal.isOpen}
        assessment={consolidatedReportModal.assessment as any}
        organizationLevels={organizationLevels}
        onClose={() =>
          setConsolidatedReportModal({
            isOpen: false,
            assessment: null,
          })
        }
        onViewAccount={(assessment, account) =>
          handleViewFullReport(
            assessment as AssessmentWithAccounts,
            account as AssessmentAccount
          )
        }
      />

      <FullReportModalFrame
        isOpen={showFullReportModal}
        fullReportData={fullReportData}
        organizationLevels={organizationLevels || []}
        onClose={() => {
          setShowFullReportModal(false);
          setFullReportData(null);
        }}
        onExport={handleExportReport}
        showNotification={showNotification}
        setIndividualResponseModal={setIndividualResponseModal}
      />

      {/* Override Final Account Level Modal */}
      <Modal
        isOpen={overrideModal.isOpen}
        toggle={() => {
          setOverrideModal({
            isOpen: false,
            assessmentId: null,
            accountId: null,
            currentValue: null,
            publishOnSave: false,
          });
          setOverrideValue("");
          setOverrideRationale("");
        }}
        title={
          overrideModal.publishOnSave
            ? "Override Final Account Level & Publish"
            : "Override Final Account Level"
        }
        size="3xl"
      >
        <div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Final Account Level:{" "}
              {getLevelDisplayName(overrideModal.currentValue)}
            </label>
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Final Account Level
            </label>
            <Select
              name="overrideLevel"
              value={overrideValue}
              onChange={e => setOverrideValue(e.target.value)}
              options={organizationLevels.map(
                (level: {
                  level_code: string;
                  name: string;
                  description: string;
                }) => ({
                  label: getAccountLevelVerboseLabel(
                    level.level_code,
                    level.name
                  ),
                  value: level.level_code,
                })
              )}
            />
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rationale <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
              rows={3}
              value={overrideRationale}
              onChange={e => setOverrideRationale(e.target.value)}
              placeholder="Explain why you are overriding the final Account Level"
            />
          </div>
          <div className="bg-purple-50 border border-purple-600 rounded-md p-3 mb-4">
            <p className="mb-0 text-sm text-black">
              <strong>Note:</strong>{" "}
              {overrideModal.publishOnSave ? (
                "Saving publishes this account to the hub with the overridden level and removes it from Disconnect Ongoing if applicable."
              ) : (
                <>
                  Individual sponsor responses can be accessed using &quot;View
                  Response&quot; to help you make this decision.
                </>
              )}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline-primary"
              onClick={() => {
                setOverrideModal({
                  isOpen: false,
                  assessmentId: null,
                  accountId: null,
                  currentValue: null,
                  publishOnSave: false,
                });
                setOverrideValue("");
                setOverrideRationale("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveOverride}
              disabled={!overrideValue || !overrideRationale.trim()}
            >
              {overrideModal.publishOnSave
                ? "Save Override & Publish"
                : "Save Override"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Extend Due Date Modal */}
      <Modal
        isOpen={extendDueDateModal.isOpen}
        toggle={handleCloseExtendDueDateModal}
        title="Extend Overdue Assessment"
        size="3xl"
      >
        {extendDueDateModal.assessment && (
          <div>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-6">
              <p className="text-base text-gray-900 mb-1">
                {extendDueDateModal.assessment.name}
              </p>
              <p className="text-sm text-gray-600 mb-0">
                Current Due Date:{" "}
                {formatDueDate(
                  extendDueDateModal.assessment.dueDate ||
                    extendDueDateModal.assessment.due_on ||
                    extendDueDateModal.assessment.due_date_info
                      ?.current_due_date
                )}
              </p>
            </div>
            <div className="mb-6">
              <CommonInput
                label="New Due Date"
                name="newDueDate"
                type="date"
                value={extendDueDateModal.newDueDate}
                onChange={e =>
                  setExtendDueDateModal(prev => ({
                    ...prev,
                    newDueDate: e.target.value,
                  }))
                }
                required
                min={utcTodayInputValue()}
                max={formalLatestDueInputValue(extendDueDateModal.assessment)}
                lang="en-GB"
              />
              {formalLatestDueInputValue(extendDueDateModal.assessment) ? (
                <p className="mt-1 text-xs text-gray-500">
                  A Formal can run at most {FORMAL_MAX_DUE_DAYS} days from
                  launch: latest{" "}
                  {formatDueDate(
                    formalLatestDueInputValue(extendDueDateModal.assessment)
                  )}
                  . Moving the due date into the next calendar period can make
                  some inputs stale.
                </p>
              ) : null}
            </div>
            <div className="mb-6">
              <CommonInput
                label="Reason for extension"
                name="reason"
                type="textarea"
                value={extendDueDateModal.reason}
                onChange={e =>
                  setExtendDueDateModal(prev => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
                placeholder="Please explain why you're extending this overdue assessment..."
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline-primary"
                onClick={handleCloseExtendDueDateModal}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleExtendDueDate}
                loading={isExtendingDueDate}
              >
                Extend Due Date
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={extendedReasonModal.isOpen}
        toggle={handleCloseExtendedReasonModal}
        title="Extended Reason"
        size="xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 m-0">
            <span className="font-semibold text-gray-900">Assessment:</span>{" "}
            {extendedReasonModal.assessment?.name || "Assessment"}
          </p>

          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm font-medium text-gray-700 mb-1">Reason</p>
            <p className="text-sm text-gray-900 m-0 whitespace-pre-wrap leading-relaxed">
              {extendedReasonModal.assessment?.due_date_info?.reason ||
                "No reason available."}
            </p>
          </div>

          <div className="flex justify-end">
            <Button variant="primary" onClick={handleCloseExtendedReasonModal}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Abort Assessment Modal */}
      <Modal
        isOpen={abortAssessmentModal.isOpen}
        toggle={handleCloseAbortAssessmentModal}
        title="Abort Assessment"
        size="3xl"
      >
        {abortAssessmentModal.assessment &&
          (() => {
            const assessment = abortAssessmentModal.assessment;
            const title = assessment.name?.trim() || "Untitled assessment";
            const rawDesc = assessment.description?.trim();
            const showDescription =
              !!rawDesc && rawDesc.toLowerCase() !== title.toLowerCase();

            return (
              <div className="space-y-6">
                <div className="flex gap-3 rounded-xl border border-red-100 bg-red-50/60 px-4 py-3.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-700">
                    <Ban size={18} strokeWidth={2} aria-hidden />
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-sm font-semibold text-gray-900 m-0">
                      Stop this assessment permanently
                    </p>
                    <p className="text-sm text-gray-600 mt-1 mb-0 leading-relaxed">
                      Participants will no longer be able to submit responses.
                      This is recorded for your organization.
                    </p>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white px-4 py-4 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                    Assessment
                  </p>
                  <p className="text-base font-semibold text-gray-900 m-0 leading-snug">
                    {title}
                  </p>
                  {showDescription && (
                    <p className="text-sm text-gray-600 mt-3 mb-0 leading-relaxed border-t border-gray-100 pt-3">
                      {rawDesc}
                    </p>
                  )}
                </div>

                <div>
                  <CommonInput
                    label="Reason for aborting"
                    name="reason"
                    type="textarea"
                    value={abortAssessmentModal.reason}
                    onChange={e =>
                      setAbortAssessmentModal(prev => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    placeholder="Briefly explain why this assessment is being aborted…"
                    required
                  />
                </div>

                <div
                  className="flex gap-2.5 rounded-lg border border-amber-200/80 bg-amber-50 px-3.5 py-3"
                  role="status"
                >
                  <AlertTriangle
                    className="h-5 w-5 shrink-0 text-amber-600 mt-0.5"
                    aria-hidden
                  />
                  <p className="text-sm text-amber-950 m-0 leading-relaxed">
                    <span className="font-medium">This cannot be undone.</span>{" "}
                    Aborted assessments cannot be reactivated from this screen.
                  </p>
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-gray-100 pt-6 sm:flex-row sm:justify-end sm:gap-3">
                  <Button
                    variant="outline-primary"
                    onClick={handleCloseAbortAssessmentModal}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleAbortAssessment}
                    loading={isAbortingAssessment}
                    className="w-full sm:w-auto"
                  >
                    Abort Assessment
                  </Button>
                </div>
              </div>
            );
          })()}
      </Modal>

      <IndividualResponseModal
        modal={individualResponseModal}
        onClose={closeIndividualResponseModal}
        questionsAndResponses={questionsAndResponses}
        isLoadingQuestionsAndResponses={isLoadingQuestionsAndResponses}
      />

      <SuccessNotification
        isOpen={notification.isOpen}
        onClose={closeNotification}
        message={notification.message}
        details={notification.details}
        type={notification.type}
      />

      <Modal
        isOpen={showStatisticsModal}
        toggle={() => {
          setShowStatisticsModal(false);
          setStatisticsAssessment(null);
          setStatisticsProgressTab("completed");
        }}
        title={statisticsAssessment?.name || "Assessment Statistics"}
        subtitle="Program Statistics"
        size="4xl"
      >
        {statisticsAssessment &&
          (() => {
            const accounts = statisticsAssessment.accounts || [];
            const progressParticipants = buildProgressParticipants(accounts);

            const participantsTotal = progressParticipants.length;
            const participantsCompleted = progressParticipants.filter(
              p => p.hasResponded
            ).length;
            const participantsPercent1 =
              participantsTotal > 0
                ? Number(
                    ((participantsCompleted / participantsTotal) * 100).toFixed(
                      1
                    )
                  )
                : 0;

            const responsesTotal = accounts.reduce((sum, account) => {
              const { totalAssigned } = getAccountCompletionCounts(account);
              return sum + totalAssigned;
            }, 0);
            const responsesCompleted = accounts.reduce((sum, account) => {
              const { responsesCount } = getAccountCompletionCounts(account);
              return sum + responsesCount;
            }, 0);
            const responsesPercent =
              responsesTotal > 0
                ? Number(
                    ((responsesCompleted / responsesTotal) * 100).toFixed(1)
                  )
                : 0;

            const accountsTotal = accounts.length;
            const accountsCompleted = accounts.filter(account =>
              accountHasAllExecutivesResponded(account)
            ).length;
            const accountsPercent1 =
              accountsTotal > 0
                ? Number(((accountsCompleted / accountsTotal) * 100).toFixed(1))
                : 0;

            const completedRows = progressParticipants
              .filter(p => p.hasResponded)
              .map(({ id, name, accountNames }) => ({
                id,
                name,
                accountNames,
              }));
            const incompleteRows = progressParticipants
              .filter(p => !p.hasResponded)
              .map(({ id, name, accountNames }) => ({
                id,
                name,
                accountNames,
              }));
            const progressRows =
              statisticsProgressTab === "completed"
                ? completedRows
                : incompleteRows;

            return (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-gray-200 p-4 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-700">
                        Participants
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        Response Rate
                      </span>
                    </div>
                    <div className="mt-2 text-3xl leading-none font-semibold text-green-600 tabular-nums">
                      {participantsPercent1}%
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      {participantsCompleted} of {participantsTotal} unique
                      participants responded
                    </div>
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500"
                        style={{ width: `${participantsPercent1}%` }}
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-700">
                        Responses
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        Completion Rate
                      </span>
                    </div>
                    <div className="mt-2 text-3xl leading-none font-semibold text-blue-600 tabular-nums">
                      {responsesPercent}%
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      {responsesCompleted} of {responsesTotal} responses
                      completed
                    </div>
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{ width: `${responsesPercent}%` }}
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-700">
                        Accounts
                      </div>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                        Completion
                      </span>
                    </div>
                    <div className="mt-2 text-3xl leading-none font-semibold text-purple-600 tabular-nums">
                      {accountsPercent1}%
                    </div>
                    <div className="mt-2 text-[12px] text-gray-500 whitespace-nowrap">
                      {accountsCompleted} of {accountsTotal} accounts with
                      completed responses
                    </div>
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500"
                        style={{ width: `${accountsPercent1}%` }}
                      />
                    </div>
                  </div>
                </div>

                {Array.isArray(accounts) && accounts.length > 0 && (
                  <div className="rounded-lg border border-gray-200 overflow-hidden bg-white">
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-gray-800">
                        Account Progress
                      </div>
                      <div className="flex rounded-md overflow-hidden border border-gray-200 text-[11px] font-semibold">
                        <button
                          type="button"
                          onClick={() => setStatisticsProgressTab("completed")}
                          className={`px-3 py-1 transition-colors ${statisticsProgressTab === "completed" ? "bg-green-600 text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}
                        >
                          Completed ({completedRows.length})
                        </button>
                        <button
                          type="button"
                          onClick={() => setStatisticsProgressTab("incomplete")}
                          className={`px-3 py-1 border-l border-gray-200 transition-colors ${statisticsProgressTab === "incomplete" ? "bg-red-500 text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}
                        >
                          Incomplete ({incompleteRows.length})
                        </button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto">
                      {progressRows.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                          {statisticsProgressTab === "completed"
                            ? "No participants have responded yet."
                            : "All participants have responded."}
                        </div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 w-1/2">
                                Participant
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 w-1/2">
                                Account
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {progressRows.map(participant => (
                              <tr
                                key={participant.id}
                                className="hover:bg-gray-50"
                              >
                                <td className="px-4 py-2.5 w-1/2">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`w-2 h-2 rounded-full flex-shrink-0 ${statisticsProgressTab === "completed" ? "bg-green-500" : "bg-red-400"}`}
                                    />
                                    <span className="text-gray-800 font-medium">
                                      {participant.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 w-1/2">
                                  <div className="flex flex-wrap gap-1">
                                    {participant.accountNames.map((name, i) => (
                                      <span
                                        key={i}
                                        className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                          statisticsProgressTab === "completed"
                                            ? "bg-green-50 text-green-700 border border-green-200"
                                            : "bg-red-50 text-red-600 border border-red-200"
                                        }`}
                                      >
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-1">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowStatisticsModal(false);
                      setStatisticsAssessment(null);
                      setStatisticsProgressTab("completed");
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            );
          })()}
      </Modal>

      {/* Assessment Creation Wizard */}
      {isWizardOpen && (
        <AssessmentCreationWizard
          onClose={() => {
            setIsWizardOpen(false);
            setEditingAssessment(null);
          }}
          editingAssessment={wizardEditingAssessment}
          onComplete={data => {
            // Don't close modal on save as draft - user closes manually
            if (data.status !== "draft") {
              setIsWizardOpen(false);
              setEditingAssessment(null);
            } else {
              // New draft created: switch to "edit" so next Save as Draft updates the same assessment
              if (!editingAssessment) {
                setEditingAssessment({
                  id: data.id,
                  name: data.title ?? data.name ?? "Untitled",
                  description: data.description,
                  due_on: data.due_date,
                  template_id: data.template_id,
                } as AssessmentWithAccounts);
              }
            }
            // Refresh assessments list
            const apiStatus =
              data.navigateToTab === "pending"
                ? "draft"
                : data.navigateToTab === "ongoing"
                  ? "active"
                  : data.navigateToTab === "completed"
                    ? "completed"
                    : data.navigateToTab === "published"
                      ? "published"
                      : "all-status";

            dispatch(
              fetchAssessments({
                sort_by: "created_at",
                sort_order: "desc",
                status: apiStatus as any,
              })
            );

            // Navigate to appropriate tab
            if (data.navigateToTab) {
              setActiveAssessmentStatusTab(data.navigateToTab);
            }

            // Show success notification only when not editing (wizard shows its own toast when editing)
            if (!editingAssessment) {
              showNotification(
                data.status === "draft"
                  ? "Assessment saved as draft successfully!"
                  : "Assessment created and launched successfully!",
                data.status === "draft"
                  ? "The assessment has been saved and is ready to be launched later."
                  : `Assessment has been assigned to ${data.assignmentsCreated || 0} account(s).`,
                "success"
              );
            }
          }}
        />
      )}

      <DisconnectTriggerModal
        isOpen={isDisconnectTriggerModalOpen}
        selectedAccounts={disconnectTriggerAccounts}
        triggerDueDate={disconnectTriggerDueDate}
        onDueDateChange={setDisconnectTriggerDueDate}
        triggerContextNote={disconnectTriggerContextNote}
        onContextNoteChange={setDisconnectTriggerContextNote}
        defaultDueDateDays={disconnectReview?.defaultDueDateDays ?? 7}
        triggerLoading={isTriggeringDisconnect}
        onClose={handleCloseDisconnectTriggerModal}
        onSubmit={handleConfirmDisconnectTrigger}
      />

      <ResolutionReviewModal
        isOpen={resolutionReviewModal.isOpen}
        reviewRow={resolutionReviewModal.account?.disconnectReviewRow ?? null}
        assessmentTitle={resolutionReviewModal.assessment?.name}
        isTeamAssessment={isSelfAssessmentType(
          resolutionReviewModal.assessment?.assessment_type
        )}
        isSubmitting={isAcceptingResolution}
        onClose={handleCloseResolutionReview}
        onAccept={handleAcceptResolution}
      />

      <AssessmentSubmitSuccessModal
        info={forcePublishSuccessInfo}
        showBackToStatus={false}
        onClose={() => setForcePublishSuccessInfo(null)}
        onViewMix={() => setForcePublishSuccessInfo(null)}
      />
    </div>
  );
};

export default AssessmentsContainer;
